import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import 'leaflet/dist/leaflet.css'              // REQUIRED — without it tiles mis-position / render black
import 'leaflet-draw/dist/leaflet.draw.css'    // draw toolbar styling
import { farmMapApi, parcelsApi, soilApi, irrigationApi, irrigationInfraApi, structuresApi, prefsApi, growableRefApi, treeDetectApi, blocksApi, plantZonesApi, plantsApi, tenantApi, locationsApi, droneMapsApi, type FarmFeature, type DetectedTree, type Block, type PlantZone, type Plant, type Location as FarmPlot, type SoilPoint, type IrrigationPipe, type IrrigationValve, type IrrigationManifold, type IrrigationSource, type Structure } from '../lib/api'
import { handlePlantLimit } from '../lib/upsell'
import { PhotoManager } from '../components/shared/PhotoManager'
import { PlantModal } from '../components/shared/PlantModal'
import { TagEditor, useTags } from '../components/shared/TagEditor'
import { varietyColor } from '../lib/varietyColor'
import { polygonAcres, geojsonPolygonAcres, formatWeight, type MeasurementSystem } from '../lib/units'

// ── Types ─────────────────────────────────────────────────────────────────

type LayerKey = 'plants' | 'plantZones' | 'irrigationZones' | 'irrigationInfra' | 'structures'
// Blocks-first drawing: a polygon becomes a Block (its own backing plant_zone),
// a species Sub-zone inside the selected block, an Irrigation zone, or a Structure
// (barn/well house/packing house — a resizable, color-coded building polygon, §174).
type DrawType = 'block' | 'sub_zone' | 'irrigation_zone' | 'boundary' | 'structure'
// Snappy infra point placement (§172/§173): drop a valve / manifold / water source.
type PlaceMode = 'valve' | 'manifold' | 'well_pump' | 'city_water' | null
// Structure types offered in the editor dropdown (mirrors the backend structure_type set;
// backend column is plain TEXT with no CHECK, so any of these is accepted).
const STRUCTURE_TYPES = ['barn', 'pole_barn', 'well_house', 'equipment_shed', 'machine_shed', 'hay_barn', 'packing_house', 'stable', 'horse_stall', 'run_in_shed', 'greenhouse', 'shed', 'feed_store', 'silo', 'coop', 'pen', 'garage', 'office', 'residence', 'water_tank', 'cistern', 'water_trough', 'other'] as const

// Preset structures — pick one → a pre-sized box drops at the map centre, already typed +
// coloured, then you drag its corners to resize (both ways) and ✥ to move. Grouped by
// category (Buildings / Animal / Water / Storage). w/h are the default footprint in METRES
// (w = E–W, h = N–S). Colour is per category so the layer reads at a glance.
type StructurePreset = { key: string; label: string; type: string; category: string; color: string; w: number; h: number }
const STRUCTURE_PRESETS: StructurePreset[] = [
  // Buildings (amber-brown; greenhouse green)
  { key: 'barn', label: 'Barn', type: 'barn', category: 'Buildings', color: '#B45309', w: 18, h: 12 },
  { key: 'pole_barn', label: 'Pole barn', type: 'pole_barn', category: 'Buildings', color: '#B45309', w: 15, h: 9 },
  { key: 'packing_house', label: 'Packing house', type: 'packing_house', category: 'Buildings', color: '#B45309', w: 12, h: 9 },
  { key: 'greenhouse', label: 'Greenhouse', type: 'greenhouse', category: 'Buildings', color: '#15803D', w: 9, h: 5 },
  { key: 'garage', label: 'Garage / shop', type: 'garage', category: 'Buildings', color: '#B45309', w: 7, h: 6 },
  { key: 'office', label: 'Office', type: 'office', category: 'Buildings', color: '#B45309', w: 6, h: 5 },
  { key: 'residence', label: 'Residence', type: 'residence', category: 'Buildings', color: '#B45309', w: 12, h: 10 },
  // Animal (yellow-brown)
  { key: 'stable', label: 'Stable', type: 'stable', category: 'Animal', color: '#A16207', w: 12, h: 8 },
  { key: 'horse_stall', label: 'Horse stall', type: 'horse_stall', category: 'Animal', color: '#A16207', w: 4, h: 4 },
  { key: 'run_in_shed', label: 'Run-in / loafing shed', type: 'run_in_shed', category: 'Animal', color: '#A16207', w: 7, h: 4 },
  { key: 'coop', label: 'Chicken coop', type: 'coop', category: 'Animal', color: '#A16207', w: 4, h: 3 },
  { key: 'pen', label: 'Pen / corral', type: 'pen', category: 'Animal', color: '#A16207', w: 12, h: 12 },
  // Water (blue)
  { key: 'well_house', label: 'Well house', type: 'well_house', category: 'Water', color: '#2563EB', w: 3, h: 3 },
  { key: 'water_trough', label: 'Water trough', type: 'water_trough', category: 'Water', color: '#2563EB', w: 3, h: 1 },
  { key: 'water_tank', label: 'Water tank', type: 'water_tank', category: 'Water', color: '#2563EB', w: 4, h: 4 },
  { key: 'cistern', label: 'Cistern', type: 'cistern', category: 'Water', color: '#2563EB', w: 5, h: 5 },
  // Storage (slate)
  { key: 'equipment_shed', label: 'Equipment shed', type: 'equipment_shed', category: 'Storage', color: '#6B7280', w: 12, h: 7 },
  { key: 'machine_shed', label: 'Machine shed', type: 'machine_shed', category: 'Storage', color: '#6B7280', w: 12, h: 8 },
  { key: 'hay_barn', label: 'Hay barn', type: 'hay_barn', category: 'Storage', color: '#6B7280', w: 12, h: 9 },
  { key: 'feed_store', label: 'Feed store', type: 'feed_store', category: 'Storage', color: '#6B7280', w: 5, h: 4 },
  { key: 'shed', label: 'Shed', type: 'shed', category: 'Storage', color: '#6B7280', w: 4, h: 3 },
  { key: 'silo', label: 'Silo', type: 'silo', category: 'Storage', color: '#6B7280', w: 4, h: 4 },
]
const STRUCTURE_CATEGORIES = ['Buildings', 'Animal', 'Water', 'Storage'] as const

// A closed rectangle ring (GeoJSON Polygon) of w×h metres centred on lat/lng — the dropped
// footprint for a preset structure. Resizable afterwards via leaflet-draw vertex editing.
function rectPolygonAround(lat: number, lng: number, wMeters: number, hMeters: number) {
  const dLat = hMeters / 2 / 111320
  const dLng = wMeters / 2 / (111320 * Math.cos((lat * Math.PI) / 180))
  const ring = [
    [lng - dLng, lat - dLat], [lng + dLng, lat - dLat],
    [lng + dLng, lat + dLat], [lng - dLng, lat + dLat],
    [lng - dLng, lat - dLat],
  ]
  return { type: 'Polygon' as const, coordinates: [ring] }
}
// Pipe colour presets — '' = auto (colour by mainline/lateral role). The named swatches
// follow irrigation/utility convention: off-white = PVC, lavender = reclaimed/non-potable
// (the "purple pipe" standard for recycled water), blue = potable supply. The wheel does the rest.
const PIPE_COLOR_PRESETS: { value: string; label: string; swatch: string }[] = [
  { value: '', label: 'Auto (by role)', swatch: 'linear-gradient(135deg,#38BDF8 50%,#2DD4BF 50%)' },
  { value: '#F2F0E6', label: 'White PVC', swatch: '#F2F0E6' },
  { value: '#A06CD5', label: 'Purple (reclaimed / non-potable)', swatch: '#A06CD5' },
  { value: '#38BDF8', label: 'Blue (potable)', swatch: '#38BDF8' },
]
const AUTO_SWATCH = 'linear-gradient(135deg,#F5A623 50%,#22C55E 50%)'
// Zone/block recolour palette. '' = Auto (role-based: block amber / sub-zone purple / zone green).
const ZONE_COLOR_PRESETS: { value: string; label: string; swatch: string }[] = [
  { value: '', label: 'Auto (by type)', swatch: AUTO_SWATCH },
  { value: '#F5A623', label: 'Amber', swatch: '#F5A623' },
  { value: '#22C55E', label: 'Green', swatch: '#22C55E' },
  { value: '#A855F7', label: 'Purple', swatch: '#A855F7' },
  { value: '#00BFA5', label: 'Teal', swatch: '#00BFA5' },
  { value: '#38BDF8', label: 'Blue', swatch: '#38BDF8' },
  { value: '#EF4444', label: 'Red', swatch: '#EF4444' },
]

// Reusable colour picker: preset swatches + a full colour-wheel (transparent
// <input type=color> over a conic-gradient). The selected swatch gets a ring.
function ColorSwatches({ value, presets, onChange, wheelDefault = '#888888' }: {
  value: string; presets: { value: string; label: string; swatch: string }[]
  onChange: (c: string) => void; wheelDefault?: string
}) {
  const isCustom = !!value && !presets.some(p => p.value === value)
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {presets.map(p => (
        <button key={p.label} type="button" onClick={() => onChange(p.value)} title={p.label} aria-label={p.label}
          style={{ width: 20, height: 20, borderRadius: '50%', background: p.swatch, cursor: 'pointer', padding: 0,
            border: value === p.value ? '2px solid var(--color-text)' : '1px solid var(--color-border)',
            boxShadow: value === p.value ? '0 0 0 1px var(--color-bg-card)' : 'none' }} />
      ))}
      <label title="Custom colour" style={{ width: 20, height: 20, borderRadius: '50%', overflow: 'hidden', cursor: 'pointer', display: 'inline-flex', position: 'relative',
        border: isCustom ? '2px solid var(--color-text)' : '1px solid var(--color-border)' }}>
        <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'conic-gradient(red,#ff0,#0f0,#0ff,#00f,#f0f,red)', pointerEvents: 'none' }} />
        <input type="color" value={value || wheelDefault} onChange={e => onChange(e.target.value)}
          style={{ width: '200%', height: '200%', margin: '-50%', border: 'none', padding: 0, background: 'none', cursor: 'pointer', opacity: 0 }} />
      </label>
    </div>
  )
}
const STRUCTURE_COLOR_PRESETS: { value: string; label: string; swatch: string }[] = [
  { value: '#B45309', label: 'Brown', swatch: '#B45309' },
  { value: '#9CA3AF', label: 'Gray', swatch: '#9CA3AF' },
  { value: '#F5A623', label: 'Amber', swatch: '#F5A623' },
  { value: '#22C55E', label: 'Green', swatch: '#22C55E' },
  { value: '#38BDF8', label: 'Blue', swatch: '#38BDF8' },
  { value: '#EF4444', label: 'Red', swatch: '#EF4444' },
]

// Click-to-edit panel for a placed map element. Remounts per target (keyed by id) so its
// local form re-seeds. Saves via the matching update API + invalidates the right query.
function InfraEditor({ target, qc, zones, sizes, manifolds, valves, onClose, shapeEditing, onEditShape, onSaveShape, onCancelShape }: {
  target: EditTarget
  qc: ReturnType<typeof useQueryClient>
  zones: { id: string; name: string; controller_zone_name?: string }[]
  sizes: { system: string; label: string; diameter_in: number }[]
  manifolds: IrrigationManifold[]
  valves: IrrigationValve[]
  onClose: () => void
  // Pipe geometry editing (drag vertices / add bends) — driven by the parent (needs map access).
  shapeEditing?: boolean
  onEditShape?: () => void
  onSaveShape?: () => void
  onCancelShape?: () => void
}) {
  const d = target.data as any
  const [name, setName] = useState<string>(d.name || '')
  const [color, setColor] = useState<string>(d.color_hex || '')
  const [role, setRole] = useState<'mainline' | 'lateral'>(d.pipe_role || 'mainline')
  const [size, setSize] = useState<string>(d.size_label || '')
  const [material, setMaterial] = useState<string>(d.material || 'pvc')
  const [psi, setPsi] = useState<string>(d.output_psi != null ? String(d.output_psi) : '')
  const [gpm, setGpm] = useState<string>(d.output_gpm != null ? String(d.output_gpm) : '')
  const [kind, setKind] = useState<string>(d.kind || 'well_pump')
  const [stype, setStype] = useState<string>(d.structure_type || 'barn')
  const [zoneId, setZoneId] = useState<string>(d.zone_id || '')
  const [manifoldId, setManifoldId] = useState<string>(d.manifold_id || '')
  const [outletCount, setOutletCount] = useState<string>(d.outlet_count != null ? String(d.outlet_count) : '')
  const [stationNum, setStationNum] = useState<string>(d.station_number != null ? String(d.station_number) : '')
  const [normallyOpen, setNormallyOpen] = useState<boolean>(!!d.normally_open)
  const [flowControl, setFlowControl] = useState<boolean>(!!d.flow_control)
  const [busy, setBusy] = useState(false)

  const titles: Record<EditTarget['kind'], string> = { pipe: 'Pipe', valve: 'Valve', manifold: 'Manifold', source: 'Water source', structure: 'Structure', irrigation_zone: 'Irrigation zone' }
  const invalidateKey = target.kind === 'structure' ? ['structures'] : target.kind === 'irrigation_zone' ? ['irrigation-zones'] : ['irrigation-infra']
  const canDelete = true
  const finish = () => { qc.invalidateQueries({ queryKey: invalidateKey }); onClose() }

  async function save() {
    setBusy(true)
    try {
      const id = d.id
      if (target.kind === 'pipe') {
        const sys = sizes.find(s => s.label === size)?.system
        await irrigationInfraApi.updatePipe(id, { name, pipe_role: role, size_label: size || undefined, size_system: sys, material, color_hex: color || (role === 'lateral' ? '#2DD4BF' : '#38BDF8') } as Partial<IrrigationPipe>)
      } else if (target.kind === 'valve') {
        await irrigationInfraApi.updateValve(id, { name, output_psi: psi ? Number(psi) : null, normally_open: normallyOpen, flow_control: flowControl, zone_id: zoneId || null, manifold_id: manifoldId || null } as Partial<IrrigationValve>)
      } else if (target.kind === 'manifold') {
        await irrigationInfraApi.updateManifold(id, { name, outlet_count: outletCount ? Number(outletCount) : 0 } as Partial<IrrigationManifold>)
      } else if (target.kind === 'source') {
        await irrigationInfraApi.updateSource(id, { name, kind, output_gpm: gpm ? Number(gpm) : null, output_psi: psi ? Number(psi) : null } as Partial<IrrigationSource>)
      } else if (target.kind === 'structure') {
        await structuresApi.update(id, { name, structure_type: stype, color_hex: color || '#9CA3AF' })
      } else if (target.kind === 'irrigation_zone') {
        await irrigationApi.updateZone(id, { name, color_hex: color, station_number: stationNum ? Number(stationNum) : null })
      }
      finish()
    } catch { setBusy(false) }
  }
  async function del() {
    setBusy(true)
    try {
      const id = d.id
      if (target.kind === 'pipe') await irrigationInfraApi.deletePipe(id)
      else if (target.kind === 'valve') await irrigationInfraApi.deleteValve(id)
      else if (target.kind === 'manifold') await irrigationInfraApi.deleteManifold(id)
      else if (target.kind === 'source') await irrigationInfraApi.deleteSource(id)
      else if (target.kind === 'structure') await structuresApi.remove(id)
      else if (target.kind === 'irrigation_zone') await irrigationApi.deleteZone(id)
      finish()
    } catch { setBusy(false) }
  }

  const inputStyle = { flex: 1, padding: '6px 8px' } as const
  return (
    <div className="absolute bottom-6 right-6 z-[1001] rounded-xl p-3" style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', width: 264, boxShadow: '0 8px 28px rgba(0,0,0,0.4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#38BDF8' }}>✎ {titles[target.kind]}</span>
        <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', fontSize: 14 }}>✕</button>
      </div>
      <input className="input text-xs" value={name} onChange={e => setName(e.target.value)} placeholder="Name (optional)" style={{ width: '100%', padding: '6px 8px', marginBottom: 6 }} />

      {target.kind === 'pipe' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <select className="input text-xs" value={role} onChange={e => setRole(e.target.value as 'mainline' | 'lateral')} style={inputStyle}>
              <option value="mainline">Mainline</option>
              <option value="lateral">Lateral</option>
            </select>
            <select className="input text-xs" value={size} onChange={e => setSize(e.target.value)} style={inputStyle}>
              <option value="">size…</option>
              {sizes.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            <select className="input text-xs" value={material} onChange={e => setMaterial(e.target.value)} style={inputStyle}>
              <option value="pvc">PVC</option><option value="poly">Poly</option><option value="drip">Drip</option><option value="other">Other</option>
            </select>
            <ColorSwatches value={color} presets={PIPE_COLOR_PRESETS} wheelDefault="#A06CD5" onChange={setColor} />
          </div>
          {shapeEditing ? (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8, lineHeight: 1.4 }}>
              Drag a dot to move/re-angle; drag a midpoint to add a bend (90° shift); tap a dot to delete it; drag the green ✥ to move the whole pipe. Save shape when done.
            </div>
          ) : (
            <button onClick={onEditShape} className="text-xs w-full px-2 py-1.5 rounded-lg" style={{ marginBottom: 8, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid #38BDF8', cursor: 'pointer' }}>
              ✎ Edit shape on map (move · re-angle · add bends)
            </button>
          )}
        </>
      )}
      {target.kind === 'valve' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input className="input text-xs" type="number" value={psi} onChange={e => setPsi(e.target.value)} placeholder="Outlet PSI" style={inputStyle} />
            <select className="input text-xs" value={zoneId} onChange={e => setZoneId(e.target.value)} style={inputStyle} title="Zone this valve serves">
              <option value="">— zone —</option>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name || z.controller_zone_name || 'Zone'}</option>)}
            </select>
          </div>
          {manifolds.length > 0 && (
            <select className="input text-xs" value={manifoldId} onChange={e => setManifoldId(e.target.value)} style={{ width: '100%', padding: '6px 8px', marginBottom: 6 }} title="Which manifold this valve sits on">
              <option value="">— no manifold —</option>
              {manifolds.map(m => <option key={m.id} value={m.id}>{m.name || 'Manifold'}</option>)}
            </select>
          )}
          <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 11, color: 'var(--color-text-muted)' }}>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={normallyOpen} onChange={e => setNormallyOpen(e.target.checked)} /> Normally open</label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={flowControl} onChange={e => setFlowControl(e.target.checked)} /> Flow control</label>
          </div>
        </>
      )}
      {target.kind === 'manifold' && (() => {
        const placed = valves.filter(v => v.manifold_id === d.id).length
        const n = outletCount ? Number(outletCount) : 0
        return (
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-muted)' }}>
              Valve ports
              <input className="input text-xs" type="number" min={0} value={outletCount} onChange={e => setOutletCount(e.target.value)} placeholder="e.g. 6" style={{ width: 70, padding: '6px 8px' }} />
            </label>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 6 }}>
              <b style={{ color: 'var(--color-text)' }}>{placed}</b>{n > 0 ? ` of ${n}` : ''} valve{placed === 1 ? '' : 's'} placed{n > 0 && placed < n ? ` · ${n - placed} open` : ''}
            </div>
          </div>
        )
      })()}
      {target.kind === 'source' && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <select className="input text-xs" value={kind} onChange={e => setKind(e.target.value)} style={inputStyle}>
            {['well_pump', 'city_water', 'booster_pump', 'tank', 'pond', 'other'].map(k => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
          </select>
          <input className="input text-xs" type="number" value={gpm} onChange={e => setGpm(e.target.value)} placeholder="GPM" style={{ width: 64, padding: '6px 8px' }} />
          <input className="input text-xs" type="number" value={psi} onChange={e => setPsi(e.target.value)} placeholder="PSI" style={{ width: 64, padding: '6px 8px' }} />
        </div>
      )}
      {target.kind === 'structure' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            <select className="input text-xs" value={stype} onChange={e => setStype(e.target.value)} style={inputStyle}>
              {STRUCTURE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
            <ColorSwatches value={color} presets={STRUCTURE_COLOR_PRESETS} wheelDefault="#B45309" onChange={setColor} />
          </div>
          {onEditShape && (shapeEditing ? (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8, lineHeight: 1.4 }}>
              Drag a corner to resize (both ways); drag the green ✥ to move the whole building. Save shape when done.
            </div>
          ) : (
            <button onClick={onEditShape} className="text-xs w-full px-2 py-1.5 rounded-lg" style={{ marginBottom: 8, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid #B45309', cursor: 'pointer' }}>
              ✎ Resize / move on map
            </button>
          ))}
        </>
      )}
      {target.kind === 'irrigation_zone' && (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Colour</span>
            <ColorSwatches value={color} presets={ZONE_COLOR_PRESETS} wheelDefault="#00BFA5" onChange={setColor} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4 }}>
            Controller zone #
            <input className="input text-xs" type="number" min={1} value={stationNum} onChange={e => setStationNum(e.target.value)} placeholder="e.g. 1" style={{ width: 70, padding: '6px 8px' }} title="Which station this zone runs as on the irrigation controller" />
          </label>
          <p style={{ fontSize: 10, color: 'var(--color-text-faint)', margin: '0 0 8px', lineHeight: 1.4 }}>
            {d.bound ? `Linked to controller${d.controller_zone_name ? ` · ${d.controller_zone_name}` : ''}.` : 'Name it now; when you connect a controller, importing it links this zone by its number.'}
          </p>
        </>
      )}

      {shapeEditing ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onSaveShape} className="text-xs flex-1 px-2 py-1.5 rounded-lg font-semibold" style={{ background: '#2D6A2F', color: '#fff', border: '1px solid #2D6A2F', cursor: 'pointer' }}>✓ Save shape</button>
          <button onClick={onCancelShape} className="text-xs px-2 py-1.5 rounded-lg" style={{ background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', cursor: 'pointer' }}>✕ Cancel</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={save} disabled={busy} className="text-xs flex-1 px-2 py-1.5 rounded-lg font-semibold" style={{ background: '#2D6A2F', color: '#fff', border: '1px solid #2D6A2F', opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' }}>Save</button>
          {canDelete && <button onClick={del} disabled={busy} className="text-xs px-2 py-1.5 rounded-lg" style={{ background: 'var(--color-bg)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.4)', cursor: busy ? 'wait' : 'pointer' }} title="Delete this element">🗑</button>}
        </div>
      )}
    </div>
  )
}

interface PlantEntry {
  id: string
  name: string
  scientificName: string
  category: string
  color: string
  featureType: 'tree' | 'palm' | 'shrub'
}

interface IrrigationZone {
  id: string
  name: string
  designation_status: 'undesignated' | 'designated' | 'inactive'
  geometry?: any
  controller_brand?: string
  controller_zone_name?: string
  color_hex?: string
  // Zone-as-hub binding (§174): which controller station this zone runs as.
  controller_id?: string | null
  station_number?: number | null
  bound?: boolean
}

// Click-to-edit target for the map element editor (pipes/valves/manifolds/sources/
// structures/irrigation zones) — recolour, rename, retype, reassign, or delete after drawing.
type EditTarget =
  | { kind: 'pipe'; data: IrrigationPipe }
  | { kind: 'valve'; data: IrrigationValve }
  | { kind: 'manifold'; data: IrrigationManifold }
  | { kind: 'source'; data: IrrigationSource }
  | { kind: 'structure'; data: Structure }
  | { kind: 'irrigation_zone'; data: IrrigationZone }

// ── Layer colors ──────────────────────────────────────────────────────────

const LAYER_COLORS: Record<LayerKey, string> = {
  plants:          '#F5A623',
  plantZones:      '#22c55e',
  irrigationZones: '#00BFA5',
  irrigationInfra: '#38BDF8',
  structures:      '#9CA3AF',
}

// ── Plant catalog ─────────────────────────────────────────────────────────

const PLANT_CATALOG: PlantEntry[] = [
  // Mangoes
  { id: 'mango-keitt',     name: 'Keitt Mango',          scientificName: 'Mangifera indica',          category: 'Mango',   color: '#F59E0B', featureType: 'tree' },
  { id: 'mango-kent',      name: 'Kent Mango',           scientificName: 'Mangifera indica',          category: 'Mango',   color: '#F59E0B', featureType: 'tree' },
  { id: 'mango-haden',     name: 'Haden Mango',          scientificName: 'Mangifera indica',          category: 'Mango',   color: '#D97706', featureType: 'tree' },
  { id: 'mango-atkins',    name: 'Tommy Atkins Mango',   scientificName: 'Mangifera indica',          category: 'Mango',   color: '#F59E0B', featureType: 'tree' },
  { id: 'mango-nam-doc',   name: 'Nam Doc Mai Mango',    scientificName: 'Mangifera indica',          category: 'Mango',   color: '#EAB308', featureType: 'tree' },
  { id: 'mango-carrie',    name: 'Carrie Mango',         scientificName: 'Mangifera indica',          category: 'Mango',   color: '#F59E0B', featureType: 'tree' },
  { id: 'mango-valencia',  name: 'Valencia Pride Mango', scientificName: 'Mangifera indica',          category: 'Mango',   color: '#D97706', featureType: 'tree' },
  { id: 'mango-lemon-zest',name: 'Lemon Zest Mango',    scientificName: 'Mangifera indica',          category: 'Mango',   color: '#EAB308', featureType: 'tree' },
  { id: 'mango-coconut-cream',name:'Coconut Cream Mango',scientificName: 'Mangifera indica',          category: 'Mango',   color: '#F59E0B', featureType: 'tree' },
  // Avocado
  { id: 'avocado-lula',    name: 'Lula Avocado',         scientificName: 'Persea americana',          category: 'Avocado', color: '#84cc16', featureType: 'tree' },
  { id: 'avocado-simmonds',name: 'Simmonds Avocado',     scientificName: 'Persea americana',          category: 'Avocado', color: '#65A30D', featureType: 'tree' },
  { id: 'avocado-choquette',name:'Choquette Avocado',    scientificName: 'Persea americana',          category: 'Avocado', color: '#84cc16', featureType: 'tree' },
  { id: 'avocado-hall',    name: 'Hall Avocado',         scientificName: 'Persea americana',          category: 'Avocado', color: '#4D7C0F', featureType: 'tree' },
  { id: 'avocado-monroe',  name: 'Monroe Avocado',       scientificName: 'Persea americana',          category: 'Avocado', color: '#65A30D', featureType: 'tree' },
  // Citrus
  { id: 'citrus-navel',    name: 'Navel Orange',         scientificName: 'Citrus sinensis',           category: 'Citrus',  color: '#F97316', featureType: 'tree' },
  { id: 'citrus-meyer',    name: 'Meyer Lemon',          scientificName: 'Citrus × meyeri',           category: 'Citrus',  color: '#EAB308', featureType: 'tree' },
  { id: 'citrus-key-lime', name: 'Key Lime',             scientificName: 'Citrus aurantiifolia',      category: 'Citrus',  color: '#A3E635', featureType: 'tree' },
  { id: 'citrus-grapefruit',name:'Ruby Red Grapefruit',  scientificName: 'Citrus × paradisi',         category: 'Citrus',  color: '#FB923C', featureType: 'tree' },
  { id: 'citrus-tangerine',name: 'Dancy Tangerine',      scientificName: 'Citrus reticulata',         category: 'Citrus',  color: '#F97316', featureType: 'tree' },
  // Lychee / Longan / Rambutan
  { id: 'lychee-brewster', name: 'Brewster Lychee',      scientificName: 'Litchi chinensis',          category: 'Tropical',color: '#FB7185', featureType: 'tree' },
  { id: 'lychee-mauritius',name: 'Mauritius Lychee',     scientificName: 'Litchi chinensis',          category: 'Tropical',color: '#E11D48', featureType: 'tree' },
  { id: 'longan-kohala',   name: 'Kohala Longan',        scientificName: 'Dimocarpus longan',         category: 'Tropical',color: '#D97706', featureType: 'tree' },
  { id: 'rambutan',        name: 'Rambutan',             scientificName: 'Nephelium lappaceum',       category: 'Tropical',color: '#DC2626', featureType: 'tree' },
  // Jackfruit / Breadfruit
  { id: 'jackfruit',       name: 'Jackfruit',            scientificName: 'Artocarpus heterophyllus',  category: 'Tropical',color: '#65A30D', featureType: 'tree' },
  { id: 'breadfruit',      name: 'Breadfruit',           scientificName: 'Artocarpus altilis',        category: 'Tropical',color: '#4D7C0F', featureType: 'tree' },
  // Star Fruit / Carambola
  { id: 'carambola',       name: 'Carambola (Star Fruit)',scientificName: 'Averrhoa carambola',        category: 'Tropical',color: '#CA8A04', featureType: 'tree' },
  // Sapodilla / Sapote
  { id: 'sapodilla',       name: 'Sapodilla',            scientificName: 'Manilkara zapota',          category: 'Tropical',color: '#92400E', featureType: 'tree' },
  { id: 'white-sapote',    name: 'White Sapote',         scientificName: 'Casimiroa edulis',          category: 'Tropical',color: '#D97706', featureType: 'tree' },
  { id: 'mamey-sapote',    name: 'Mamey Sapote',         scientificName: 'Pouteria sapota',           category: 'Tropical',color: '#B45309', featureType: 'tree' },
  // Palms
  { id: 'palm-coconut',    name: 'Coconut Palm',         scientificName: 'Cocos nucifera',            category: 'Palm',    color: '#92400E', featureType: 'palm' },
  { id: 'palm-date-medjool',name:'Medjool Date Palm',    scientificName: 'Phoenix dactylifera',       category: 'Palm',    color: '#78350F', featureType: 'palm' },
  { id: 'palm-sabal',      name: 'Sabal Palm',           scientificName: 'Sabal palmetto',            category: 'Palm',    color: '#A16207', featureType: 'palm' },
  { id: 'palm-queen',      name: 'Queen Palm',           scientificName: 'Syagrus romanzoffiana',     category: 'Palm',    color: '#A16207', featureType: 'palm' },
  // Shrubs / Edibles
  { id: 'fig-brown-turkey',name: 'Brown Turkey Fig',     scientificName: 'Ficus carica',              category: 'Shrub',   color: '#7C3AED', featureType: 'shrub' },
  { id: 'mulberry',        name: 'Mulberry',             scientificName: 'Morus alba',                category: 'Shrub',   color: '#6D28D9', featureType: 'shrub' },
  { id: 'guava-cattley',   name: 'Cattley Guava',        scientificName: 'Psidium cattleyanum',       category: 'Tropical',color: '#BE185D', featureType: 'shrub' },
  { id: 'guava-white',     name: 'White Guava',          scientificName: 'Psidium guajava',           category: 'Tropical',color: '#16A34A', featureType: 'shrub' },
  { id: 'banana-cavendish',name: 'Cavendish Banana',     scientificName: 'Musa acuminata',            category: 'Tropical',color: '#CA8A04', featureType: 'shrub' },
  { id: 'banana-plantain', name: 'Plantain',             scientificName: 'Musa × paradisiaca',        category: 'Tropical',color: '#D97706', featureType: 'shrub' },
  { id: 'papaya',          name: 'Papaya',               scientificName: 'Carica papaya',             category: 'Tropical',color: '#F97316', featureType: 'shrub' },
  { id: 'passion-fruit',   name: 'Passion Fruit',        scientificName: 'Passiflora edulis',         category: 'Tropical',color: '#A21CAF', featureType: 'shrub' },
  { id: 'rosemary',        name: 'Rosemary',             scientificName: 'Salvia rosmarinus',         category: 'Herb',    color: '#064E3B', featureType: 'shrub' },
  { id: 'moringa',         name: 'Moringa',              scientificName: 'Moringa oleifera',          category: 'Tropical',color: '#15803D', featureType: 'shrub' },
]

// Color the global-catalog entries by category so newly-seeded types (bananas,
// strawberries, orchids, Garcinia/achacha, …) render distinctly on the map.
const CATEGORY_COLORS: Record<string, string> = {
  mango: '#F59E0B', avocado: '#84cc16', citrus: '#F97316', palm: '#CA8A04',
  banana: '#FACC15', berry: '#E11D48', strawberry: '#E11D48', orchid: '#A21CAF',
  garcinia: '#7C3AED', tropical: '#F97316', herb: '#15803D',
}
const titleCase = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s
const colorForCategory = (cat: string) => CATEGORY_COLORS[cat?.toLowerCase()] ?? '#22c55e'

// mergeCatalog: curated PLANT_CATALOG (hand-tuned colors) + global growable_reference,
// deduped by lowercased name so curated entries win and all global types still appear.
function mergeCatalog(refs?: { id: string; common_name: string; scientific_name: string; category: string }[]): PlantEntry[] {
  if (!refs || refs.length === 0) return PLANT_CATALOG
  const seen = new Set(PLANT_CATALOG.map(p => p.name.toLowerCase()))
  const extra: PlantEntry[] = []
  for (const r of refs) {
    const name = (r.common_name || '').trim()
    if (!name || seen.has(name.toLowerCase())) continue
    seen.add(name.toLowerCase())
    extra.push({
      id: r.id, name, scientificName: r.scientific_name || '',
      category: titleCase(r.category || 'Other'), color: colorForCategory(r.category),
      featureType: 'tree',
    })
  }
  return [...PLANT_CATALOG, ...extra]
}

// ── Farm location persistence ─────────────────────────────────────────────

const FARM_LOC_KEY = 'grafty-farm-location'

interface FarmLocation { lat: number; lng: number; label: string }

function loadFarmLocation(): FarmLocation | null {
  try {
    const raw = localStorage.getItem(FARM_LOC_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function saveFarmLocation(loc: FarmLocation) {
  localStorage.setItem(FARM_LOC_KEY, JSON.stringify(loc))
}

// Default center (South Florida) used only when no farm location is set
const DEFAULT_CENTER: [number, number] = [26.45, -81.8]

// Leaflet renders STRING tooltip/popup content as innerHTML — so any user-controlled
// text (block / zone / plant names) must be HTML-escaped to prevent stored XSS.
function escTip(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string))
}

// Whole-shape drag for an in-edit polyline/polygon. leaflet-draw 1.0.4 has no path
// drag, so we add a draggable centroid "✥ move" handle: dragging it translates every
// vertex by the delta. leaflet-draw's own vertex handles are disabled during the drag
// and re-enabled on drop so they snap to the new geometry (they don't auto-follow
// setLatLngs). Works for polylines (flat LatLng[]) and polygons (nested rings) via the
// recursive shift. Returns a disposer that removes the handle. (Per-vertex delete is
// already built into leaflet-draw — clicking a vertex removes it, down to the min.)
function enableShapeDrag(map: any, poly: any): () => void {
  const L: any = (window as any).L
  if (!L || !poly?.getBounds) return () => {}
  let center: any
  try { center = poly.getBounds().getCenter() } catch { return () => {} }
  if (!center) return () => {}
  const handle = L.marker(center, {
    draggable: true, zIndexOffset: 2000, keyboard: false,
    icon: L.divIcon({
      className: 'grafty-move-handle',
      html: '<div style="width:26px;height:26px;border-radius:50%;background:#2D6A2F;color:#fff;display:flex;align-items:center;justify-content:center;font-size:15px;box-shadow:0 1px 5px rgba(0,0,0,.5);cursor:move">✥</div>',
      iconSize: [26, 26], iconAnchor: [13, 13],
    }),
  })
  handle.addTo(map)
  let prev = handle.getLatLng()
  const shift = (lls: any, dLat: number, dLng: number): any =>
    Array.isArray(lls) ? lls.map((p: any) => shift(p, dLat, dLng)) : L.latLng(lls.lat + dLat, lls.lng + dLng)
  handle.on('dragstart', () => { prev = handle.getLatLng(); try { poly.editing?.disable() } catch { /* ignore */ } })
  handle.on('drag', () => {
    const cur = handle.getLatLng()
    poly.setLatLngs(shift(poly.getLatLngs(), cur.lat - prev.lat, cur.lng - prev.lng))
    prev = cur
  })
  handle.on('dragend', () => { try { poly.editing?.enable() } catch { /* ignore */ } })
  return () => { try { map.removeLayer(handle) } catch { /* ignore */ } }
}

// Saved map view (center + zoom) — the exact view the farmer wants the map to
// open at, so it doesn't default too zoomed-out. Set via the "Save this view"
// button; restored on load. Per-device (localStorage), like the farm location.
const FARM_VIEW_KEY = 'grafty-farm-view'
interface SavedView { lat: number; lng: number; zoom: number }
// Saved views are keyed per plot (farm_location id) so each property remembers its own
// center+zoom — switching plots restores that plot's view instead of resetting. Legacy
// flat {lat,lng,zoom} (the old single global view) is honored as a fallback for any plot.
function loadSavedView(plotId?: string | null): SavedView | null {
  try {
    const raw = localStorage.getItem(FARM_VIEW_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.lat === 'number') return parsed as SavedView // legacy global view
    const key = plotId || '_default'
    return (parsed?.[key] ?? parsed?._default ?? null) as SavedView | null
  } catch { return null }
}
function saveView(plotId: string | null, v: SavedView) {
  try {
    const raw = localStorage.getItem(FARM_VIEW_KEY)
    let map: Record<string, SavedView> = {}
    if (raw) { const p = JSON.parse(raw); map = (p && typeof p.lat === 'number') ? {} : (p ?? {}) }
    map[plotId || '_default'] = v
    localStorage.setItem(FARM_VIEW_KEY, JSON.stringify(map))
  } catch { /* ignore */ }
}

// Persisted per-layer visibility — default all ON; a deselected layer is remembered
// (per-device, localStorage). "Reset (show all)" restores every layer.
const LAYERS_KEY = 'grafty-map-layers'
const BLOCK_LABELS_KEY = 'grafty-block-labels' // show/hide the always-on block name·acreage labels
const ALL_LAYERS_ON: Record<LayerKey, boolean> = { plants: true, plantZones: true, irrigationZones: true, irrigationInfra: true, structures: true }
function loadLayers(): Record<LayerKey, boolean> {
  try { const r = localStorage.getItem(LAYERS_KEY); return r ? { ...ALL_LAYERS_ON, ...JSON.parse(r) } : { ...ALL_LAYERS_ON } } catch { return { ...ALL_LAYERS_ON } }
}

const LAYER_LABELS: Record<LayerKey, string> = {
  plants:          'Plants & Trees',
  plantZones:      'Plant Zones',
  irrigationZones: 'Irrigation Zones',
  irrigationInfra: 'Irrigation Pipes & Valves',
  structures:      'Structures & Beds',
}

// ── Helper components ─────────────────────────────────────────────────────

function SpeciesCard({ speciesId, speciesName, catalog = PLANT_CATALOG }: { speciesId?: string; speciesName: string; catalog?: PlantEntry[] }) {
  const sp = speciesId ? catalog.find(p => p.id === speciesId) : undefined
  return (
    <div className="rounded-lg p-3 mb-3" style={{ background: 'var(--color-bg-card-2)' }}>
      <p className="text-xs font-semibold" style={{ color: 'var(--color-text)' }}>{speciesName}</p>
      {sp && (
        <p className="text-xs italic mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
          {sp.scientificName}
        </p>
      )}
      {sp && (
        <span
          className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full"
          style={{ background: sp.color + '25', color: sp.color }}
        >
          {sp.category}
        </span>
      )}
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────

export function FarmMapPage() {
  const mapRef = useRef<HTMLDivElement>(null)
  const leafletMap = useRef<any>(null)
  const layerRefs = useRef<Record<LayerKey, any>>({
    plants: null, plantZones: null, irrigationZones: null, irrigationInfra: null, structures: null,
  })
  // Dedicated group for structures-table polygons (§174) — kept separate from the
  // farm_features 'structures' group (which the features effect clearLayers()es), but
  // toggled together with the "Structures & Beds" layer key.
  const structuresExtraRef = useRef<any>(null)

  // These refs mirror state so the map click handler always reads fresh values
  const placingRef = useRef<PlantEntry | null>(null)
  const drawTypeRef = useRef<DrawType | null>(null)
  const drawerRef = useRef<any>(null) // active leaflet-draw polygon drawer (Finish/Undo/Cancel)
  const drawHelpersRef = useRef<{ markFirst: () => void; onMove: (e: any) => void } | null>(null) // first-vertex + snap-close handlers
  const selectedBlockRef = useRef<string | null>(null)
  // ── Snappy irrigation-pipe drawing (§172/§174) ──
  // Custom click-to-add polyline (NOT leaflet-draw) so vertices snap to existing
  // valves / manifolds / sources / pipe-ends and to the pipe's own bends. Refs hold
  // the live draw so the map handlers (bound once per draw) always read fresh state.
  const pipeDrawRef = useRef<{ role: 'mainline' | 'lateral'; points: [number, number][]; sizeLabel: string; sizeSystem: string; color: string; material: string } | null>(null)
  const pipeLayerRef = useRef<any>(null) // FeatureGroup: in-progress line + vertex dots + preview + snap halo
  const pipeHandlersRef = useRef<{ click: (e: any) => void; move: (e: any) => void; dbl: (e: any) => void; ctx: (e: any) => void; key: (e: KeyboardEvent) => void } | null>(null)
  const placeHandlersRef = useRef<{ click: (e: any) => void } | null>(null)
  const placeModeRef = useRef<PlaceMode>(null)
  // Mirrors so the bound-once draw:created handler + map handlers read fresh values.
  const infraRef = useRef<{ pipes: IrrigationPipe[]; valves: IrrigationValve[]; manifolds: IrrigationManifold[]; sources: IrrigationSource[] }>({ pipes: [], valves: [], manifolds: [], sources: [] })
  const structureTypeRef = useRef<string>('barn')
  const structureColorRef = useRef<string>('#B45309')

  const [layers, setLayers] = useState<Record<LayerKey, boolean>>(loadLayers)
  // Block name·acreage labels are always-on tooltips that can overlay tree pins;
  // this toggle hides them (persisted) without hiding the block polygons themselves.
  const [showBlockLabels, setShowBlockLabels] = useState<boolean>(() => {
    try { return localStorage.getItem(BLOCK_LABELS_KEY) !== '0' } catch { return true }
  })
  // True once the Leaflet map + its layer groups exist. Render effects depend on it
  // so they re-run after the (async) map init — otherwise, on a client-side return
  // with cached data, the effects fire before the map exists, early-return, and the
  // zones/blocks/features never appear until a hard refresh. (The cache "loses" them.)
  const [mapReady, setMapReady] = useState(false)
  // Right tool panel: hideable so the map gets the full screen (essential on phones).
  // Default open on desktop, closed on a narrow screen; choice persisted.
  const [panelOpen, setPanelOpen] = useState<boolean>(() => {
    try { const v = localStorage.getItem('grafty-map-panel'); if (v !== null) return v === '1' } catch { /* ignore */ }
    return !(typeof window !== 'undefined' && window.matchMedia?.('(max-width: 820px)').matches)
  })
  const [isNarrow, setIsNarrow] = useState<boolean>(() => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 820px)').matches)
  const togglePanel = () => setPanelOpen(o => { const n = !o; try { localStorage.setItem('grafty-map-panel', n ? '1' : '0') } catch { /* ignore */ } return n })
  const [activeTab, setActiveTab] = useState<'features' | 'catalog' | 'irrigation'>('features')
  const [selectedFeature, setSelectedFeature] = useState<FarmFeature | null>(null)
  const [placingSpecies, setPlacingSpecies] = useState<PlantEntry | null>(null)
  const [drawingType, setDrawingType] = useState<DrawType | null>(null)
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null)
  const [selectedZone, setSelectedZone] = useState<PlantZone | null>(null)
  const [renameVal, setRenameVal] = useState('')
  // Full plant record opened from a map pin (shared editor with the Plants page).
  const [editingPlant, setEditingPlant] = useState<Plant | null>(null)
  const [loadingPlant, setLoadingPlant] = useState(false)
  const [editingShape, setEditingShape] = useState(false)
  const [findingParcel, setFindingParcel] = useState(false)
  const [parcelMsg, setParcelMsg] = useState('')
  const [showLots, setShowLots] = useState(false)
  const [lotMsg, setLotMsg] = useState('')
  // Whether this location has a free parcel source (null = not yet checked) — drives
  // "Find my parcel boundary" (covered) vs "Draw my parcel boundary" (not covered).
  const [parcelCoverage, setParcelCoverage] = useState<{ covered: boolean; kind: string; county?: string; source?: string } | null>(null)
  const coverageKeyRef = useRef('')
  // Public reference overlays: FEMA flood zones (tiles) + NRCS soil (tap-to-inspect).
  const [overlays, setOverlays] = useState<{ flood: boolean; soil: boolean; drone: boolean }>({ flood: false, soil: false, drone: false })
  const floodLayerRef = useRef<any>(null)
  const soilClickRef = useRef<((e: any) => void) | null>(null)
  const droneOverlaysRef = useRef<Record<string, any>>({})
  const droneUrlRef = useRef<Record<string, string>>({})
  // Active drone-map overlays (§228) — fetched only when the "Drone imagery" overlay is on.
  const droneMapsQuery = useQuery({ queryKey: ['drone-maps'], queryFn: () => droneMapsApi.list().then(r => r.data.maps), enabled: overlays.drone })
  // Multi-select: pick plant pins on the map, then bulk-assign to a block / zone.
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Bulk naming controls (marquee selection): base name + start # + block prefix.
  const [bulkBaseName, setBulkBaseName] = useState('')
  const [bulkStartNum, setBulkStartNum] = useState(1)
  const [bulkPrependBlock, setBulkPrependBlock] = useState(false)
  const [editingBoundary, setEditingBoundary] = useState(false)
  const [maskOn, setMaskOn] = useState(true)
  const [catalogSearch, setCatalogSearch] = useState('')
  const [catalogCategory, setCatalogCategory] = useState('All')
  const [showLocationPanel, setShowLocationPanel] = useState(false)
  const [locationSearch, setLocationSearch] = useState('')
  const [locationResults, setLocationResults] = useState<{ display_name: string; lat: string; lon: string }[]>([])
  const [locationSearching, setLocationSearching] = useState(false)
  const [farmLocation, setFarmLocation] = useState<FarmLocation | null>(loadFarmLocation)
  // The farm's address from Settings → Farm Profile. The map location (and the
  // parcel lookup) DEFAULT to this address, but a pinned/searched location
  // overrides it (see the effect below + findMyParcel). One farm = one plot for
  // now (1:1 with the settings address); multi-plot is a future paid feature.
  const { data: tenantProfile } = useQuery({ queryKey: ['tenant-profile'], queryFn: () => tenantApi.get().then(r => r.data) })
  // Which in-UX detection pickers to show (superadmin-gated, §179). Both off → no
  // picker UI and detection behaves exactly as before.
  const { data: detectOptions } = useQuery({ queryKey: ['detect-options'], queryFn: () => treeDetectApi.options().then(r => r.data), staleTime: 5 * 60_000 })
  // Farm plots (multi-plot). The active plot drives the map centre + parcel lookup.
  // Additional plots beyond the first are a paid feature (Grower/Estate).
  const { data: plotsData } = useQuery({ queryKey: ['locations'], queryFn: () => locationsApi.list().then(r => r.data.locations) })
  const plots = plotsData ?? []
  const isPaidPlan = !!tenantProfile?.plan && tenantProfile.plan !== 'seedling' && tenantProfile.plan !== 'free'
  const canAddPlot = plots.length === 0 || isPaidPlan
  const [activePlotId, setActivePlotId] = useState<string | null>(null)
  // Set right before a plot switch sets farmLocation, so the recenter effect restores
  // that plot's saved view (if any) instead of resetting to the plot centre @ zoom 17.
  const restoringPlotRef = useRef(false)
  const [plotMsg, setPlotMsg] = useState('')
  const plotInitRef = useRef(false)

  // AI tree detection (satellite → tree GPS points within the current view)
  const [detected, setDetected] = useState<DetectedTree[] | null>(null)
  const [detecting, setDetecting] = useState(false)
  // "Pickiness": only show/place detections at/above this confidence. Higher = fewer,
  // more-certain trees. Default is moderately picky to cut false positives up front.
  const [pickiness, setPickiness] = useState(0.55)
  const [detectWarning, setDetectWarning] = useState<string | null>(null)
  const [detectEngine, setDetectEngine] = useState<string | null>(null) // which detector ran: 'gemini' | 'deepforest'
  const [detectModelUsed, setDetectModelUsed] = useState<string | null>(null) // which Gemini model ran (when non-default)
  // In-UX picker selections (superadmin-gated via detectOptions). '' = use the default.
  const [detectEngineChoice, setDetectEngineChoice] = useState('')
  const [detectModelChoice, setDetectModelChoice] = useState('')
  const [detectSpecies, setDetectSpecies] = useState('')
  const [placing, setPlacing] = useState(false)
  const detectLayerRef = useRef<any>(null)
  // "Show lot lines" — neighboring parcel boundaries overlaid for the current view.
  const lotLayerRef = useRef<any>(null)
  const showLotsRef = useRef(false)
  const loadLotLinesRef = useRef<() => void>(() => {})
  const lotDebounce = useRef<any>(null)
  const [viewSaved, setViewSaved] = useState(false)
  const [backfilling, setBackfilling] = useState(false)
  // Transient confirmation toast (e.g. "Added 4 plants inside to this block").
  const [absorbMsg, setAbsorbMsg] = useState('')
  const flashAbsorb = (m: string) => { setAbsorbMsg(m); setTimeout(() => setAbsorbMsg(''), 2800) }
  // Irrigation infra + structure draw controls (§172/§174).
  const [pipeDraw, setPipeDraw] = useState<{ role: 'mainline' | 'lateral'; count: number } | null>(null)
  const [pipeRole, setPipeRole] = useState<'mainline' | 'lateral'>('mainline')
  const [pipeSize, setPipeSize] = useState('') // nominal size_label, defaults to the tenant measurement system
  const [pipeMaterial, setPipeMaterial] = useState('pvc')
  // '' = auto (colour by role); otherwise a real hex. Presets follow trade convention:
  // off-white = PVC, lavender/purple = reclaimed/non-potable water; the wheel picks anything.
  const [pipeColor, setPipeColor] = useState('')
  const [placeMode, setPlaceMode] = useState<PlaceMode>(null)
  // Click-to-edit a placed map element (recolour/rename/retype/delete after drawing).
  const [editInfra, setEditInfra] = useState<EditTarget | null>(null)
  const [structureType] = useState<string>('barn')
  const [structureColor] = useState<string>('#B45309')
  const [showInfraTools, setShowInfraTools] = useState(false)
  const [infraMsg, setInfraMsg] = useState('')
  const flashInfra = (m: string) => { setInfraMsg(m); setTimeout(() => setInfraMsg(''), 2800) }

  // Ray-cast point-in-polygon for a GeoJSON [lng,lat] outer ring.
  function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1]
      const hit = ((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)
      if (hit) inside = !inside
    }
    return inside
  }
  // The outer ring of a GeoJSON Polygon OR MultiPolygon. Blocks adopted from a
  // parcel lot ("use as boundary" / "+ block from lot") are MultiPolygon, so a
  // Polygon-only check silently dropped them (no detect button, no point-in-
  // polygon block assignment) — the "sometimes it works, sometimes not".
  function outerRing(geometry: unknown): number[][] | undefined {
    const g = geometry as { type?: string; coordinates?: number[][][] | number[][][][] } | undefined
    if (!g) return undefined
    if (g.type === 'Polygon') return (g.coordinates as number[][][])?.[0]
    if (g.type === 'MultiPolygon') return (g.coordinates as number[][][][])?.[0]?.[0]
    return undefined
  }
  // The block whose backing polygon contains this point — but only if EXACTLY one
  // does (auto-snap is unambiguous; overlapping blocks → leave it for manual assign).
  function blockIdAt(lat: number, lng: number): string | undefined {
    let found: string | undefined
    for (const b of blocksRef.current) {
      if (!b.zone_id) continue
      const z = plantZonesRef.current.find(pz => pz.id === b.zone_id)
      const ring = outerRing(z?.geometry)
      if (ring && ring.length >= 3 && pointInRing(lng, lat, ring)) {
        if (found) return undefined // inside 2+ blocks → ambiguous, don't auto-assign
        found = b.id
      }
    }
    return found
  }
  // The irrigation zone containing this point — only if EXACTLY one does (same rule).
  function irrigationZoneIdAt(lat: number, lng: number): string | undefined {
    let found: string | undefined
    for (const z of irrigationZonesRef.current) {
      const ring = outerRing(z.geometry)
      if (ring && ring.length >= 3 && pointInRing(lng, lat, ring)) {
        if (found) return undefined
        found = z.id
      }
    }
    return found
  }
  // The plant zone containing this point — only if EXACTLY one does, and EXCLUDING
  // any zone that backs a block (those are captured as block_id instead, so we don't
  // double-assign the block's own polygon). Standalone zones + sub-zones qualify.
  function plantZoneAt(lat: number, lng: number): string | undefined {
    const blockZoneIds = new Set(blocksRef.current.map(b => b.zone_id).filter(Boolean) as string[])
    let found: string | undefined
    for (const z of plantZonesRef.current) {
      if (blockZoneIds.has(z.id)) continue // this zone IS a block → handled by blockIdAt
      const ring = outerRing(z.geometry)
      if (ring && ring.length >= 3 && pointInRing(lng, lat, ring)) {
        if (found) return undefined
        found = z.id
      }
    }
    return found
  }
  // After a block is drawn, pull every plant pin that falls inside its polygon and
  // isn't already in a block into the new block (the user's "auto-add plants in the
  // area that aren't part of either" rule). Returns how many were absorbed.
  async function absorbPlantsIntoBlock(ring: number[][], blockId: string): Promise<number> {
    const plantById = new Map(plantsRef.current.map(p => [p.id, p]))
    const ids: string[] = []
    for (const f of featuresRef.current) {
      const g = f.geometry as { type?: string; coordinates?: number[] } | undefined
      if (g?.type !== 'Point' || !Array.isArray(g.coordinates)) continue
      const pid = (f.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined
      if (!pid) continue
      if (!pointInRing(g.coordinates[0], g.coordinates[1], ring)) continue
      if (plantById.get(pid)?.block_id) continue // already in a block — leave it
      ids.push(pid)
    }
    if (ids.length === 0) return 0
    try { await plantsApi.assign(ids, { block_id: blockId }) } catch { return 0 }
    qc.invalidateQueries({ queryKey: ['plants'] })
    qc.invalidateQueries({ queryKey: ['blocks'] })
    return ids.length
  }
  // Place a tree: create the map pin (geometry) AND a real Plant linked to it via
  // location_feature_id, assign block_id if dropped inside a block, and store the
  // plant_id back on the pin so it can be opened/edited. This is why placed trees
  // now show up in Plants.
  async function placeTreeAt(plant: PlantEntry, lat: number, lng: number) {
    try {
      const feat = await farmMapApi.createFeature({
        feature_type: plant.featureType,
        name: plant.name,
        geometry: { type: 'Point', coordinates: [lng, lat] } as unknown as FarmFeature['geometry'],
        properties: { species_id: plant.id, species_name: plant.name, scientific_name: plant.scientificName },
        location_id: activePlotId ?? undefined, // tag the pin to the active plot
      })
      const featureId = feat.data.id
      const blockId = blockIdAt(lat, lng)
      const irrigationZoneId = irrigationZoneIdAt(lat, lng) // auto-snap to the overlapping zone
      const zoneId = plantZoneAt(lat, lng) // auto-snap to an overlapping plant zone / sub-zone
      let plantId: string | undefined
      try {
        const p = await plantsApi.create({ label: plant.name, status: 'active', location_feature_id: featureId, block_id: blockId, irrigation_zone_id: irrigationZoneId, zone_id: zoneId } as Partial<Plant>)
        plantId = p.data.id
      } catch (e) { handlePlantLimit(e) /* plant-limit 402 → upsell; pin exists even if plant create fails */ }
      if (plantId) {
        try {
          await farmMapApi.updateFeature(featureId, {
            properties: { species_id: plant.id, species_name: plant.name, scientific_name: plant.scientificName, plant_id: plantId },
          } as Partial<FarmFeature>)
        } catch { /* ignore */ }
      }
      qc.invalidateQueries({ queryKey: ['map-features'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      qc.invalidateQueries({ queryKey: ['blocks'] })
    } catch { /* ignore */ }
  }

  // One-time: turn existing tree pins (no plant_id) into real Plants linked to
  // their pin, so legacy placements show up in Plants. Opt-in (button), no auto-run.
  async function backfillTreesToPlants() {
    setBackfilling(true)
    try {
      // One server-side set-based request (no per-pin loop → no rate-limit 429s).
      await farmMapApi.backfillTrees()
      qc.invalidateQueries({ queryKey: ['map-features'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      qc.invalidateQueries({ queryKey: ['blocks'] })
    } catch (e) { handlePlantLimit(e) /* plant-limit 402 → upsell; else surfaced by the unchanged count + button state */ } finally { setBackfilling(false) }
  }

  // Pick a draw mode AND immediately activate the polygon tool, so "+ Block"
  // starts drawing right away (previously the user had to also find the
  // leaflet-draw toolbar button).
  // Vertex dots scale with zoom: small when zoomed out, larger when zoomed in for
  // precise placement. Sets the --vtx-size CSS var the editing-icon rules read.
  function updateVtxSize() {
    const map = leafletMap.current
    if (!map) return
    const px = Math.max(7, Math.min(13, Math.round(map.getZoom() - 8))) // z16→8 … z20→12
    try { map.getContainer().style.setProperty('--vtx-size', `${px}px`) } catch { /* ignore */ }
  }
  // Remove the first-vertex + snap-close listeners attached during a draw.
  function detachDrawHelpers() {
    const map = leafletMap.current
    const h = drawHelpersRef.current
    if (map && h) { map.off('draw:drawvertex', h.markFirst); map.off('mousemove', h.onMove) }
    drawHelpersRef.current = null
  }
  function cancelDraw() {
    try { drawerRef.current?.disable() } catch { /* drawer already gone */ }
    drawerRef.current = null
    detachDrawHelpers()
    setDrawingType(null)
  }
  function startDraw(type: DrawType) {
    if (drawingType === type) { cancelDraw(); return } // toggle off
    cancelPipeDraw(); cancelPlace() // only one drawing mode at a time
    if (type !== 'structure') setShowStructurePalette(false)
    setDrawingType(type)
    const map = leafletMap.current
    if (!map) return
    updateVtxSize()
    // Use the global L wired up during map init — the real, mutable default export
    // with leaflet-draw attached. `import('leaflet')` resolves the FROZEN ESM
    // namespace, which has no `.Draw`, so `new L.Draw.Polygon` threw (silently
    // caught) and the crosshair never appeared. window.L always has Draw by the
    // time the map exists (init imports leaflet-draw before creating the map).
    const L: any = (window as any).L
    if (!L?.Draw?.Polygon) return
    const color = type === 'block' ? '#F5A623' : type === 'sub_zone' ? '#A855F7' : type === 'boundary' ? '#38bdf8' : type === 'structure' ? structureColor : LAYER_COLORS.irrigationZones
    try {
      try { drawerRef.current?.disable() } catch { /* none active */ }
      const drawer = new L.Draw.Polygon(map, { shapeOptions: { color, fillOpacity: 0.15, weight: 2 } })
      drawer.enable()
      drawerRef.current = drawer // keep a handle so Finish/Undo work without tapping the first vertex
      // Mark the FIRST vertex green ("start" dot) so it's obvious where the shape closes;
      // works on touch too (fires after each placed vertex). And on mouse-move, when the
      // cursor nears that first vertex (≥3 points placed), enlarge+pulse it → a snappy,
      // obvious "tap here to close" (the bigger icon is also a bigger tap target).
      const SNAP_PX = 22
      const markFirst = () => {
        try { drawer._markers?.[0]?._icon?.classList.add('grafty-vtx-first') } catch { /* ignore */ }
      }
      const onMove = (e: any) => {
        try {
          const m = drawer._markers
          if (!m || m.length < 3 || !m[0]?._icon) return
          const p0 = map.latLngToContainerPoint(m[0].getLatLng())
          m[0]._icon.classList.toggle('grafty-vtx-snap', p0.distanceTo(e.containerPoint) <= SNAP_PX)
        } catch { /* ignore */ }
      }
      map.on('draw:drawvertex', markFirst)
      map.on('mousemove', onMove)
      drawHelpersRef.current = { markFirst, onMove }
    } catch { /* leaflet-draw not ready */ }
  }
  // Close the polygon from the points placed so far — avoids having to tap the (small,
  // overlapping on a phone) FIRST vertex. completeShape() fires draw:created on success.
  function finishDraw() {
    const d = drawerRef.current
    if (!d || (d._markers?.length ?? 0) < 3) return // a polygon needs ≥3 points
    try { d.completeShape() } catch { /* leaflet-draw will fire CREATED on success */ }
  }
  function undoLastVertex() {
    try { drawerRef.current?.deleteLastVertex() } catch { /* nothing to undo */ }
  }

  // ── Snappy irrigation-pipe drawing ───────────────────────────────────────
  // Snap a clicked/hovered latlng to the nearest existing infra point — a valve,
  // manifold, water source, or any pipe vertex/endpoint — and (while drawing) to
  // the current pipe's own bends, when within ~16px on screen. Returns the snapped
  // latlng + whether a snap happened (for the halo). This is what makes connecting
  // a lateral to its valve, or branching one pipe off another, "click and it locks".
  const SNAP_PX = 22
  function infraSnapTargets(includeOwn: boolean): [number, number][] {
    const out: [number, number][] = []
    const inf = infraRef.current
    for (const p of inf.pipes) { const cs = (p.geometry as any)?.coordinates; if (Array.isArray(cs)) for (const c of cs) if (Array.isArray(c) && c.length >= 2) out.push([c[1], c[0]]) }
    for (const v of inf.valves) { const c = (v.geometry as any)?.coordinates; if (Array.isArray(c) && c.length >= 2) out.push([c[1], c[0]]) }
    for (const m of inf.manifolds) { const c = (m.geometry as any)?.coordinates; if (Array.isArray(c) && c.length >= 2) out.push([c[1], c[0]]) }
    for (const s of inf.sources) { const c = (s.geometry as any)?.coordinates; if (Array.isArray(c) && c.length >= 2) out.push([c[1], c[0]]) }
    if (includeOwn && pipeDrawRef.current) for (const pt of pipeDrawRef.current.points) out.push(pt)
    return out
  }
  function snapLatLng(latlng: any, includeOwn: boolean): { pt: [number, number]; snapped: boolean } {
    const map = leafletMap.current
    if (!map) return { pt: [latlng.lat, latlng.lng], snapped: false }
    const cp = map.latLngToContainerPoint(latlng)
    let best: [number, number] | null = null
    let bestD = SNAP_PX
    for (const cand of infraSnapTargets(includeOwn)) {
      const d = cp.distanceTo(map.latLngToContainerPoint(cand))
      if (d <= bestD) { bestD = d; best = cand }
    }
    return best ? { pt: best, snapped: true } : { pt: [latlng.lat, latlng.lng], snapped: false }
  }
  // Redraw the in-progress pipe (line + numbered vertex dots), optionally with a
  // live preview segment to the (snapped) cursor and a snap halo.
  function redrawPipe(preview?: [number, number], snapped?: boolean) {
    const L: any = (window as any).L
    const lg = pipeLayerRef.current
    const st = pipeDrawRef.current
    if (!L || !lg || !st) return
    lg.clearLayers()
    const lateral = st.role === 'lateral'
    const color = st.color || (lateral ? '#2DD4BF' : '#38BDF8')
    const all = preview ? [...st.points, preview] : st.points
    if (all.length >= 2) lg.addLayer(L.polyline(all, { color, weight: lateral ? 3 : 4, opacity: 0.9, dashArray: lateral ? '8 6' : undefined }))
    st.points.forEach((p, i) => {
      lg.addLayer(L.circleMarker(p, { radius: i === 0 ? 6 : 5, color: '#fff', fillColor: i === 0 ? '#2D6A2F' : '#F5A623', fillOpacity: 1, weight: 2 }))
    })
    if (preview && snapped) lg.addLayer(L.circleMarker(preview, { radius: 10, color: '#2D6A2F', weight: 2, fill: false, opacity: 0.9 }))
  }
  function startPipeDraw() {
    // One drawing mode at a time.
    cancelDraw(); cancelPlace()
    const map = leafletMap.current
    const L: any = (window as any).L
    if (!map || !L) return
    updateVtxSize()
    const sys = sizesForSystem.find(s => s.label === pipeSize)?.system || pipeSystem
    pipeDrawRef.current = { role: pipeRole, points: [], sizeLabel: pipeSize, sizeSystem: sys, color: pipeColor, material: pipeMaterial }
    if (!pipeLayerRef.current) pipeLayerRef.current = new L.FeatureGroup().addTo(map)
    else pipeLayerRef.current.addTo(map)
    try { map.doubleClickZoom.disable() } catch { /* ignore */ }
    const click = (e: any) => {
      const st = pipeDrawRef.current; if (!st) return
      const { pt } = snapLatLng(e.latlng, true)
      st.points.push(pt)
      setPipeDraw({ role: st.role, count: st.points.length })
      redrawPipe()
    }
    const move = (e: any) => {
      const st = pipeDrawRef.current; if (!st) return
      // Runs even with 0 points so the FIRST vertex shows its snap to a valve/manifold.
      const { pt, snapped } = snapLatLng(e.latlng, true)
      redrawPipe(pt, snapped)
    }
    const dbl = (e: any) => { try { e.originalEvent?.preventDefault?.() } catch { /* ignore */ } finishPipe() }
    // Right-click finishes the run (a common map-drawing idiom).
    const ctx = (e: any) => { try { e.originalEvent?.preventDefault?.() } catch { /* ignore */ } finishPipe() }
    // Keyboard while drawing: Enter = finish, Esc = cancel, Backspace/Delete = undo last point.
    const key = (e: KeyboardEvent) => {
      if (!pipeDrawRef.current) return
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return // don't hijack form fields
      if (e.key === 'Enter') { e.preventDefault(); finishPipe() }
      else if (e.key === 'Escape') { e.preventDefault(); cancelPipeDraw() }
      else if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); undoPipeVertex() }
    }
    map.on('click', click); map.on('mousemove', move); map.on('dblclick', dbl); map.on('contextmenu', ctx)
    window.addEventListener('keydown', key)
    pipeHandlersRef.current = { click, move, dbl, ctx, key }
    setPipeDraw({ role: pipeRole, count: 0 })
  }
  function cancelPipeDraw() {
    const map = leafletMap.current
    const h = pipeHandlersRef.current
    if (h) window.removeEventListener('keydown', h.key)
    if (map && h) { map.off('click', h.click); map.off('mousemove', h.move); map.off('dblclick', h.dbl); map.off('contextmenu', h.ctx); try { map.doubleClickZoom.enable() } catch { /* ignore */ } }
    pipeHandlersRef.current = null
    pipeDrawRef.current = null
    pipeLayerRef.current?.clearLayers()
    setPipeDraw(null)
  }
  function undoPipeVertex() {
    const st = pipeDrawRef.current
    if (!st || st.points.length === 0) return
    st.points.pop()
    setPipeDraw({ role: st.role, count: st.points.length })
    redrawPipe()
  }
  function finishPipe() {
    const st = pipeDrawRef.current
    if (!st || st.points.length < 2) { cancelPipeDraw(); return }
    const coordinates = st.points.map(p => [p[1], p[0]]) // back to GeoJSON [lng,lat]
    irrigationInfraApi.createPipe({
      pipe_role: st.role,
      size_label: st.sizeLabel || undefined,
      size_system: st.sizeSystem || undefined,
      material: st.material || undefined,
      color_hex: st.color || undefined, // omit → backend defaults by role
      geometry: { type: 'LineString', coordinates } as any,
    } as Partial<IrrigationPipe>)
      .then(() => { qc.invalidateQueries({ queryKey: ['irrigation-infra'] }); flashInfra(`${st.role === 'lateral' ? 'Lateral' : 'Mainline'} pipe added`) })
      .catch(() => flashInfra('Could not save the pipe'))
    cancelPipeDraw()
  }

  // ── Point placement: valve / manifold / water source ─────────────────────
  // Stays active for repeated drops (place a whole manifold's worth of valves) until
  // ✕. Each click snaps to the nearest pipe-end/valve so points land ON the network.
  function placeLabel(m: PlaceMode): string {
    return m === 'valve' ? 'Valve' : m === 'manifold' ? 'Manifold' : m === 'well_pump' ? 'Well pump' : 'City water'
  }
  function startPlace(mode: Exclude<PlaceMode, null>) {
    if (placeModeRef.current === mode) { cancelPlace(); return } // toggle off
    cancelDraw(); cancelPipeDraw(); cancelPlace()
    const map = leafletMap.current
    if (!map) return
    placeModeRef.current = mode
    setPlaceMode(mode)
    const click = (e: any) => {
      const m = placeModeRef.current; if (!m) return
      const { pt } = snapLatLng(e.latlng, false)
      const geometry = { type: 'Point', coordinates: [pt[1], pt[0]] } as any
      const done = () => { qc.invalidateQueries({ queryKey: ['irrigation-infra'] }); flashInfra(`${placeLabel(m)} added — click to add another, ✕ to stop`) }
      const fail = () => flashInfra(`Could not save the ${placeLabel(m).toLowerCase()}`)
      if (m === 'valve') irrigationInfraApi.createValve({ name: 'Valve', geometry, valve_type: 'zone', normally_open: false } as Partial<IrrigationValve>).then(done).catch(fail)
      else if (m === 'manifold') irrigationInfraApi.createManifold({ name: 'Manifold', geometry } as Partial<IrrigationManifold>).then(done).catch(fail)
      else irrigationInfraApi.createSource({ name: placeLabel(m), kind: m, geometry } as Partial<IrrigationSource>).then(done).catch(fail)
    }
    map.on('click', click)
    placeHandlersRef.current = { click }
  }
  function cancelPlace() {
    const map = leafletMap.current
    const h = placeHandlersRef.current
    if (map && h) map.off('click', h.click)
    placeHandlersRef.current = null
    placeModeRef.current = null
    setPlaceMode(null)
  }

  // Persist the current center+zoom as the map's default view.
  function saveCurrentView() {
    const map = leafletMap.current
    if (!map) return
    const c = map.getCenter()
    saveView(activePlotId, { lat: c.lat, lng: c.lng, zoom: map.getZoom() })
    setViewSaved(true)
    setTimeout(() => setViewSaved(false), 1800)
  }

  // The farm's address as a single geocodable string, from Settings → Farm Profile.
  function settingsAddress(): string {
    const p = tenantProfile
    if (!p) return ''
    return [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ')
  }
  // Forward-geocode free text (keyless). Tries the US Census geocoder FIRST — it
  // resolves exact/rural street addresses that Nominatim misses (e.g. Pine Island /
  // Bokeelia) — then falls back to Nominatim for non-US or place-name queries.
  async function geocodeAddress(q: string): Promise<FarmLocation | null> {
    try {
      const c = await fetch(`https://geocoding.geo.census.gov/geocoder/locations/onelineaddress?address=${encodeURIComponent(q)}&benchmark=Public_AR_Current&format=json`).then(r => r.json())
      const m = c?.result?.addressMatches?.[0]
      if (m?.coordinates?.x != null && m?.coordinates?.y != null) {
        return { lat: m.coordinates.y, lng: m.coordinates.x, label: m.matchedAddress || q }
      }
    } catch { /* best-effort — fall through to Nominatim */ }
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`).then(r => r.json())
      if (Array.isArray(res) && res[0]) return { lat: parseFloat(res[0].lat), lng: parseFloat(res[0].lon), label: res[0].display_name }
    } catch { /* best-effort */ }
    return null
  }

  // Pick the initial active plot once: the primary farm plot if any exist (plots
  // are the source of truth), otherwise fall back to the Settings address (the
  // 1:1 single-plot default). Runs once; plot switches go through selectPlot.
  useEffect(() => {
    if (plotInitRef.current) return
    if (plots.length > 0) {
      const active = plots.find(p => p.is_primary && p.lat != null && p.lon != null)
        || plots.find(p => p.lat != null && p.lon != null) || plots[0]
      plotInitRef.current = true
      setActivePlotId(active.id)
      if (active.lat != null && active.lon != null) {
        restoringPlotRef.current = true // restore this plot's saved view on first load
        setFarmLocation({ lat: active.lat, lng: active.lon, label: active.name })
      }
      return
    }
    if (tenantProfile && !farmLocation) {
      plotInitRef.current = true
      const addr = settingsAddress()
      if (addr) geocodeAddress(addr).then(loc => { if (loc) setFarmLocation(loc) })
    }
    // (effect intentionally runs with a fixed dependency set)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotsData, tenantProfile])

  // Switch the active plot: recenter the map + use it for the parcel lookup.
  function selectPlot(p: FarmPlot) {
    setActivePlotId(p.id)
    setPlotMsg('')
    if (p.lat != null && p.lon != null) { restoringPlotRef.current = true; setFarmLocation({ lat: p.lat, lng: p.lon, label: p.name }) }
    setShowLocationPanel(false)
  }
  async function deletePlot(id: string) {
    if (!confirm('Remove this plot? Its plants and zones are not deleted.')) return
    try {
      await locationsApi.remove(id)
      await qc.invalidateQueries({ queryKey: ['locations'] })
      if (activePlotId === id) { plotInitRef.current = false; setActivePlotId(null) }
    } catch { setPlotMsg('Could not remove plot.') }
  }
  // Per-plot scoping (null-safe): show an entity when there's no active plot, when
  // it's unassigned (null location_id), or when it belongs to the active plot. So
  // untagged/legacy data shows under every plot until explicitly assigned — no
  // regression. (The property boundary uses exact match instead, by design.)
  const inActivePlot = (locId?: string | null) => !activePlotId || !locId || locId === activePlotId

  // Find the farm's legal parcel boundary from free, keyless statewide GIS.
  // Uses the farm's pinned location OR the Settings address (NOT the map's current
  // pan position — that previously found a random nearby parcel). Reverse-geocodes
  // for the USPS state, then asks the backend (which proxies the state's ArcGIS
  // parcel service) for the polygon at the farm point.
  async function findMyParcel() {
    const map = leafletMap.current
    if (!map) return
    setFindingParcel(true); setParcelMsg('')
    try {
      let loc = farmLocation
      if (!loc) {
        const addr = settingsAddress()
        if (addr) loc = await geocodeAddress(addr)
      }
      if (!loc) {
        // Last resort: use the map's current center (the user clicked "Find my
        // parcel" while looking at their farm) — better than dead-ending when
        // there's no saved address/pin and geocoding missed.
        const c = map.getCenter()
        loc = { lat: c.lat, lng: c.lng, label: 'map center' }
      }
      const { lat, lng } = loc
      // Center on the address and turn on lot lines so neighbouring parcels are visible +
      // tappable. Rural county data frequently stamps an address onto an OFFSET polygon
      // (a neighbour's lot), so point/address lookup can resolve to the wrong parcel — the
      // user corrects it by tapping their actual land (lotPopup → "Use as my boundary").
      if (!showLotsRef.current) { setShowLots(true); showLotsRef.current = true }
      map.setView([lat, lng], Math.max(map.getZoom(), 18))
      void loadLotLines()
      // Prefer the Settings state if it's a 2-letter USPS code; else resolve it from the
      // point — territory bbox (PR/GU) first, then reverse-geocode the 50 states.
      let state = (tenantProfile?.state || '').trim()
      if (state.length !== 2) {
        state = await reverseState(lat, lng)
      }
      const { data } = await parcelsApi.lookup(lat, lng, state)
      if (data.found && data.geometry) {
        await saveBoundary(data.geometry)
        qc.invalidateQueries({ queryKey: ['map-features'] })
        const owner = (data.properties?.OWN_NAME as string) || (data.properties?.owner as string) || ''
        setParcelMsg(`Set to ${owner ? `${owner}’s` : 'the'} parcel at this address. Not your land? Tap the correct parcel on the map to use it instead.`)
      } else {
        setParcelMsg(data.warning || `No exact parcel here — tap your parcel on the map (lot lines shown) to set its boundary.`)
      }
    } catch {
      setParcelMsg('Parcel lookup failed')
    } finally {
      setFindingParcel(false)
    }
  }

  // Resolve the farm's point + state the same way findMyParcel does (saved plot →
  // settings address → map center) for the coverage check below.
  async function resolveFarmPoint(): Promise<{ lat: number; lng: number; state: string } | null> {
    let loc = farmLocation
    if (!loc) { const addr = settingsAddress(); if (addr) loc = await geocodeAddress(addr) }
    if (!loc && leafletMap.current) { const c = leafletMap.current.getCenter(); loc = { lat: c.lat, lng: c.lng, label: 'map center' } }
    if (!loc) return null
    let state = (tenantProfile?.state || '').trim()
    if (state.length !== 2) state = await reverseState(loc.lat, loc.lng)
    return { lat: loc.lat, lng: loc.lng, state }
  }

  // Check whether a free parcel source covers this farm. When `autoFind` (fired after
  // the user changes/saves their location), auto-run findMyParcel if covered AND no
  // boundary exists yet — so a covered farm gets its boundary set from the address
  // automatically, while never overwriting a boundary the grower already set/drew.
  async function checkCoverage(opts?: { lat?: number; lng?: number; state?: string; autoFind?: boolean }) {
    let lat = opts?.lat, lng = opts?.lng, state = opts?.state
    if (lat == null || lng == null) {
      const pt = await resolveFarmPoint()
      if (!pt) { setParcelCoverage(null); return }
      lat = pt.lat; lng = pt.lng; state = state || pt.state
    }
    if (!state || state.length !== 2) state = await reverseState(lat, lng)
    try {
      const { data } = await parcelsApi.coverage(lat, lng, state)
      setParcelCoverage(data)
      if (opts?.autoFind && data.covered) {
        const feats = featuresData?.data?.features ?? []
        const hasBoundary = feats.some(f => f.feature_type === 'boundary' && inActivePlot(f.location_id))
        if (!hasBoundary) void findMyParcel()
      }
    } catch { setParcelCoverage(null) }
  }

  // Keep the coverage flag (button label) current as the active plot / state changes.
  // Label-only here (no autoFind) — the deliberate "changed my location" auto-find is
  // triggered from pickLocation. Keyed so it runs once per plot/state.
  useEffect(() => {
    if (!mapReady) return
    const key = `${activePlotId ?? ''}|${tenantProfile?.state ?? ''}`
    if (coverageKeyRef.current === key) return
    coverageKeyRef.current = key
    void checkCoverage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, activePlotId, tenantProfile?.state])

  // ── Public reference overlays (FEMA flood tiles + NRCS soil tap-to-inspect) ───
  function soilPopupHtml(d: SoilPoint): string {
    if (!d.found) return `<div style="font-size:12px">No soil survey here.${d.warning ? ' ' + escapeHtml(d.warning) : ''}</div>`
    const prime = !!d.farmland_class && /prime/i.test(d.farmland_class)
    const row = (label: string, val?: string) => val ? `<div style="font-size:12px"><b>${label}:</b> ${escapeHtml(val)}</div>` : ''
    return `<div style="min-width:180px">
      <div style="font-weight:700;font-size:12.5px;margin-bottom:3px">${escapeHtml(d.map_unit || 'Soil')}</div>
      ${row('Drainage', d.drainage_class)}
      ${d.farmland_class ? `<div style="font-size:12px;margin-top:2px"><b>Farmland:</b> <span style="color:${prime ? '#2D6A2F' : 'inherit'};font-weight:${prime ? 700 : 400}">${escapeHtml(d.farmland_class)}</span></div>` : ''}
      ${row('Component', d.component ? `${d.component}${d.component_pct ? ` (${d.component_pct}%)` : ''}` : '')}
      <div style="font-size:10px;color:#888;margin-top:4px">USDA-NRCS SSURGO</div>
    </div>`
  }
  useEffect(() => {
    const map = leafletMap.current
    if (!map || !mapReady) return
    const L: any = (window as any).L
    // FEMA National Flood Hazard Layer (layer 28 = Flood Hazard Zones), an ArcGIS
    // dynamic MapServer rendered as keyless XYZ tiles via its `export` op (3857 bbox).
    if (overlays.flood && !floodLayerRef.current && L?.TileLayer) {
      const O = 20037508.342789244 // half the Web-Mercator world extent (m)
      const FloodTiles = L.TileLayer.extend({
        getTileUrl(c: any) {
          const s = (2 * O) / Math.pow(2, c.z)
          const minx = -O + c.x * s, maxx = -O + (c.x + 1) * s
          const maxy = O - c.y * s, miny = O - (c.y + 1) * s
          return `https://hazards.fema.gov/arcgis/rest/services/public/NFHL/MapServer/export?bbox=${minx},${miny},${maxx},${maxy}&bboxSR=3857&imageSR=3857&size=256,256&dpi=96&format=png32&transparent=true&layers=show:28&f=image`
        },
      })
      floodLayerRef.current = new FloodTiles('', { opacity: 0.45, maxZoom: 22, attribution: 'FEMA NFHL' })
      floodLayerRef.current.addTo(map)
    } else if (!overlays.flood && floodLayerRef.current) {
      try { map.removeLayer(floodLayerRef.current) } catch { /* ignore */ }
      floodLayerRef.current = null
    }
    // NRCS soil: tap the map to inspect the soil at that point (keyless SDA proxy).
    if (overlays.soil && !soilClickRef.current && L) {
      const handler = async (e: any) => {
        if (placingRef.current || drawerRef.current) return // not while placing/drawing
        const popup = L.popup({ maxWidth: 270 }).setLatLng(e.latlng).setContent('Checking soil…').openOn(map)
        try { popup.setContent(soilPopupHtml((await soilApi.point(e.latlng.lat, e.latlng.lng)).data)) }
        catch { popup.setContent('<div style="font-size:12px">Soil lookup failed.</div>') }
      }
      map.on('click', handler)
      soilClickRef.current = handler
    } else if (!overlays.soil && soilClickRef.current) {
      map.off('click', soilClickRef.current); soilClickRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlays, mapReady])

  // Drone imagery (§228): render active, placed drone orthos as imageOverlays under the farm
  // layers. Fetched via the authenticated proxy → object URL. Off → remove all + revoke URLs.
  useEffect(() => {
    const map = leafletMap.current
    const L: any = (window as any).L
    if (!map || !L || !mapReady) return
    const clearAll = () => {
      Object.keys(droneOverlaysRef.current).forEach(id => {
        try { map.removeLayer(droneOverlaysRef.current[id]) } catch { /* ignore */ }
      })
      droneOverlaysRef.current = {}
      Object.values(droneUrlRef.current).forEach(u => URL.revokeObjectURL(u))
      droneUrlRef.current = {}
    }
    if (!overlays.drone) { clearAll(); return }
    const maps = (droneMapsQuery.data ?? []).filter(m => m.is_active && m.south != null && m.north != null)
    const live = new Set(maps.map(m => m.id))
    ;(async () => {
      for (const m of maps) {
        let url = droneUrlRef.current[m.id]
        if (!url) {
          try { url = URL.createObjectURL((await droneMapsApi.file(m.id)).data); droneUrlRef.current[m.id] = url } catch { continue }
        }
        const bounds: [[number, number], [number, number]] = [[m.south!, m.west!], [m.north!, m.east!]]
        if (droneOverlaysRef.current[m.id]) droneOverlaysRef.current[m.id].setBounds(bounds).setOpacity(m.opacity)
        else droneOverlaysRef.current[m.id] = L.imageOverlay(url, bounds, { opacity: m.opacity, interactive: false }).addTo(map)
      }
      Object.keys(droneOverlaysRef.current).forEach(id => {
        if (!live.has(id)) {
          try { map.removeLayer(droneOverlaysRef.current[id]) } catch { /* ignore */ }
          delete droneOverlaysRef.current[id]
          if (droneUrlRef.current[id]) { URL.revokeObjectURL(droneUrlRef.current[id]); delete droneUrlRef.current[id] }
        }
      })
    })()
  }, [overlays.drone, droneMapsQuery.data, mapReady])

  // ── "Show lot lines": overlay neighboring parcel boundaries for the view ──────
  // US territories that have a backend parcel source (PR, GU) but that Nominatim often
  // does NOT tag with a "US-XX" ISO code — a coordinate bbox is the reliable signal.
  // Keep in sync with the backend `sources` map (internal/parcels).
  function territoryFor(lat: number, lng: number): string {
    if (lng > 144.4 && lng < 145.05 && lat > 13.2 && lat < 13.75) return 'GU' // Guam
    if (lng > -67.4 && lng < -65.1 && lat > 17.8 && lat < 18.6) return 'PR'   // Puerto Rico
    return ''
  }
  async function reverseState(lat: number, lng: number): Promise<string> {
    const terr = territoryFor(lat, lng)
    if (terr) return terr
    try {
      const rev = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`).then(r => r.json())
      return String(rev?.address?.['ISO3166-2-lvl4'] || '').split('-')[1] || ''
    } catch { return '' }
  }
  function clearLotLines() {
    const map = leafletMap.current
    if (map && lotLayerRef.current) { map.removeLayer(lotLayerRef.current); lotLayerRef.current = null }
  }
  function escapeHtml(s: string) {
    return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string))
  }
  function lotPopup(feature: any): HTMLElement {
    const p = feature.properties || {}
    const owner = String(p.OWN_NAME || p.OWNER || p.owner || p.Owner || '')
    const pid = String(p.PARCEL_ID || p.PARCELID || p.PIN || p.parcel_id || p.Parcel_ID || '')
    const acresVal = Number(p.GIS_ACRES || p.acres || p.ACRES || 0)
    const sqft = Number(p.LND_SQFOOT || 0)
    const acres = acresVal ? acresVal.toFixed(2) : (sqft ? (sqft / 43560).toFixed(2) : '')
    const d = document.createElement('div')
    d.style.cssText = 'font-size:12px;line-height:1.5;min-width:150px'
    d.innerHTML = `${owner ? `<b>${escapeHtml(owner)}</b><br/>` : ''}${pid ? `Parcel ${escapeHtml(pid)}<br/>` : ''}${acres ? `${acres} ac<br/>` : ''}`
    const btn = document.createElement('button')
    btn.textContent = 'Use as my boundary'
    btn.style.cssText = 'margin-top:6px;padding:5px 10px;border-radius:6px;border:none;background:#2D6A2F;color:#fff;font-size:12px;cursor:pointer'
    btn.onclick = async () => {
      try {
        await saveBoundary(feature.geometry)
        qc.invalidateQueries({ queryKey: ['map-features'] })
        leafletMap.current?.closePopup()
        setParcelMsg('✓ Boundary set from selected lot')
      } catch { setParcelMsg('Could not set boundary from that lot') }
    }
    d.appendChild(btn)
    // Turn this parcel into a Block (geometry zone + block), like drawing one.
    const btn2 = document.createElement('button')
    btn2.textContent = '+ Block here'
    btn2.style.cssText = 'margin-top:6px;margin-left:6px;padding:5px 10px;border-radius:6px;border:none;background:#F5A623;color:#0E1209;font-size:12px;font-weight:600;cursor:pointer'
    btn2.onclick = async () => {
      try {
        const plot = activePlotId ?? undefined
        const ac = geojsonPolygonAcres(feature.geometry)
        const zone = await plantZonesApi.create({ name: 'New block', geometry: feature.geometry, location_id: plot })
        await blocksApi.create({ name: 'New block', zone_id: zone.data.id, location_id: plot, acreage: ac ? Math.round(ac * 100) / 100 : undefined })
        qc.invalidateQueries({ queryKey: ['plant-zones'] })
        qc.invalidateQueries({ queryKey: ['blocks'] })
        leafletMap.current?.closePopup()
        setLotMsg('✓ Block created from this lot')
      } catch { setLotMsg('Could not create a block from that lot') }
    }
    d.appendChild(btn2)
    return d
  }
  function renderLotLines(fc: any) {
    const map = leafletMap.current
    const L = (window as any).L
    if (!map || !L) return
    clearLotLines()
    const layer = L.geoJSON(fc, {
      style: () => ({ color: '#38bdf8', weight: 1, fill: true, fillColor: '#38bdf8', fillOpacity: 0.04 }),
      onEachFeature: (feature: any, lyr: any) => {
        lyr.on('mouseover', () => lyr.setStyle({ weight: 2.5, fillOpacity: 0.14 }))
        lyr.on('mouseout', () => lyr.setStyle({ weight: 1, fillOpacity: 0.04 }))
        lyr.bindPopup(() => lotPopup(feature))
      },
    })
    layer.addTo(map)
    lotLayerRef.current = layer
  }
  async function loadLotLines() {
    const map = leafletMap.current
    if (!map || !showLotsRef.current) return
    if (map.getZoom() < 15) { clearLotLines(); setLotMsg('Zoom in to load lot lines.'); return }
    const b = map.getBounds()
    const c = map.getCenter()
    let state = (tenantProfile?.state || '').trim()
    if (state.length !== 2) state = await reverseState(c.lat, c.lng)
    if (!state) { setLotMsg('Set your farm state (Settings) to load lot lines.'); return }
    setLotMsg('Loading lot lines…')
    try {
      const { data } = await parcelsApi.area(
        { min_lng: b.getWest(), min_lat: b.getSouth(), max_lng: b.getEast(), max_lat: b.getNorth() },
        state,
      )
      if (!data.found || !data.geojson) { clearLotLines(); setLotMsg(data.warning || 'No lot lines for this area.'); return }
      renderLotLines(data.geojson)
      setLotMsg(`${data.count} lot${data.count === 1 ? '' : 's'} shown · click one for details`)
    } catch { setLotMsg('Could not load lot lines.') }
  }
  function toggleLotLines() {
    setShowLots(prev => {
      const next = !prev
      showLotsRef.current = next
      if (next) { void loadLotLines() } else { clearLotLines(); setLotMsg('') }
      return next
    })
  }
  // Keep the moveend handler (registered once on the map) pointed at the latest
  // loadLotLines closure (so it sees current tenantProfile/state).
  useEffect(() => { loadLotLinesRef.current = loadLotLines })

  // One boundary per PLOT. Remove only the active plot's existing boundary, then
  // store the new polygon tagged to that plot (location_id). Plots without an id
  // (single-plot / legacy) use the null-location boundary, unchanged from before.
  async function saveBoundary(geometry: any) {
    const existing = (featuresData?.data?.features ?? []).filter(
      f => f.feature_type === 'boundary' && inActivePlot(f.location_id))
    for (const b of existing) { try { await farmMapApi.deleteFeature(b.id) } catch { /* ignore */ } }
    await farmMapApi.createFeature({
      feature_type: 'boundary', name: 'Property boundary', geometry, properties: {},
      location_id: activePlotId ?? undefined,
    } as Partial<FarmFeature>)
  }

  // Bulk-assign the currently selected plant pins to a block and/or irrigation zone.
  async function assignSelected(target: { block_id?: string; irrigation_zone_id?: string }) {
    const feats = featuresData?.data?.features ?? []
    const plantIds = feats
      .filter(f => selectedIds.has(f.id))
      .map(f => (f.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined)
      .filter((x): x is string => !!x)
    if (plantIds.length === 0) return
    try {
      await plantsApi.assign(plantIds, target)
      qc.invalidateQueries({ queryKey: ['plants'] })
      qc.invalidateQueries({ queryKey: ['blocks'] })
      qc.invalidateQueries({ queryKey: ['map-features'] })
      setSelectedIds(new Set())
      setSelectMode(false)
    } catch { /* non-fatal; refetch reflects state */ }
  }

  // Bulk-delete the selected tree pins (marquee/click multi-select). Confirms first,
  // then one batch request (server soft-deletes the pins + linked plants → no 429).
  async function deleteSelected() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    const n = ids.length
    if (!window.confirm(`Delete ${n} tree${n === 1 ? '' : 's'}? This can't be undone.`)) return
    try {
      const res = await farmMapApi.deleteTreesBatch(ids)
      qc.invalidateQueries({ queryKey: ['map-features'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      qc.invalidateQueries({ queryKey: ['blocks'] })
      setSelectedIds(new Set())
      setSelectMode(false)
      const removed = res?.data?.deleted ?? n
      flashInfra(`Deleted ${removed} tree${removed === 1 ? '' : 's'}`)
    } catch {
      flashInfra('Delete failed — try again')
    }
  }

  // The variety name shared by the current selection — default/placeholder for bulk naming.
  function commonSelectedVariety(): string {
    const feats = featuresData?.data?.features ?? []
    const counts = new Map<string, number>()
    for (const id of selectedIds) {
      const nm = (feats.find(f => f.id === id)?.properties as Record<string, unknown> | undefined)?.species_name as string | undefined
      if (nm) counts.set(nm, (counts.get(nm) ?? 0) + 1)
    }
    if (counts.size === 0) return ''
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0]
  }

  // Bulk-set the variety (species) of the selected trees — recolours the pins (the
  // map keys colour off species_id/species_name, §91) and sets the displayed variety.
  async function bulkSetVariety(speciesId: string) {
    const sp = catalog.find(p => p.id === speciesId)
    if (!sp || selectedIds.size === 0) return
    try {
      await farmMapApi.bulkEditTrees({
        feature_ids: Array.from(selectedIds),
        species_id: sp.id, species_name: sp.name, scientific_name: sp.scientificName,
      })
      qc.invalidateQueries({ queryKey: ['map-features'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      flashInfra(`Set ${selectedIds.size} tree${selectedIds.size === 1 ? '' : 's'} → ${sp.name}`)
    } catch { flashInfra('Could not set variety — try again') }
  }

  // Bulk sequential naming: "<base> 1, 2, 3…" in a north→south, west→east sweep so the
  // numbers read in a sensible order. Optionally prefix the block name (numbered per
  // block then) so tree numbers are easy to locate across the farm.
  async function bulkApplyNames() {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    const feats = featuresData?.data?.features ?? []
    const plantByIdMap = new Map(plants.map(p => [p.id, p]))
    const blockByIdMap = new Map(blocks.map(b => [b.id, b]))
    type Sel = { fid: string; lat: number; lng: number; blockId: string; blockName?: string }
    const sel: Sel[] = []
    for (const fid of ids) {
      const f = feats.find(x => x.id === fid)
      if (!f || (f.geometry as any)?.type !== 'Point') continue
      const pid = (f.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined
      const plant = pid ? plantByIdMap.get(pid) : undefined
      const block = plant?.block_id ? blockByIdMap.get(plant.block_id) : undefined
      const c = (f.geometry as any).coordinates as [number, number]
      sel.push({ fid, lat: c[1], lng: c[0], blockId: plant?.block_id ?? '', blockName: block?.name })
    }
    if (sel.length === 0) return
    const base = bulkBaseName.trim() || commonSelectedVariety() || 'Tree'
    const start = Number.isFinite(bulkStartNum) ? Math.max(1, Math.floor(bulkStartNum)) : 1
    const sweep = (a: Sel[]) => a.slice().sort((x, y) => (y.lat - x.lat) || (x.lng - y.lng))
    const names: Record<string, string> = {}
    if (bulkPrependBlock) {
      const byBlock = new Map<string, Sel[]>()
      for (const t of sel) { const g = byBlock.get(t.blockId); if (g) g.push(t); else byBlock.set(t.blockId, [t]) }
      for (const group of byBlock.values()) {
        let n = start
        for (const t of sweep(group)) { names[t.fid] = t.blockName ? `${t.blockName} · ${base} ${n}` : `${base} ${n}`; n++ }
      }
    } else {
      let n = start
      for (const t of sweep(sel)) { names[t.fid] = `${base} ${n}`; n++ }
    }
    try {
      await farmMapApi.bulkEditTrees({ feature_ids: ids, names })
      qc.invalidateQueries({ queryKey: ['map-features'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      flashInfra(`Named ${Object.keys(names).length} tree${Object.keys(names).length === 1 ? '' : 's'}`)
    } catch { flashInfra('Could not name trees — try again') }
  }

  // Open the full plant editor (shared with the Plants page) for a pin's linked
  // Plant — fetched fresh so variety/block/care/dimensions are all editable here.
  async function openPlantEditor(plantId: string) {
    setLoadingPlant(true)
    try { setEditingPlant((await plantsApi.get(plantId)).data) }
    catch { /* plant may have been deleted; ignore */ }
    finally { setLoadingPlant(false) }
  }

  // ── Reshape a block/zone polygon ──────────────────────────────────────────
  // Enables leaflet-draw vertex editing on the selected polygon; Save persists the
  // new geometry to its plant_zone (a block edits its backing zone via zone_id).
  function startEditShape() {
    const zoneId = selectedBlock ? selectedBlock.zone_id : selectedZone?.id
    if (!zoneId) return
    const poly = zoneLayersRef.current[zoneId]?.getLayers?.()[0]
    if (!poly?.editing) return
    poly.editing.enable()
    const disposeDrag = enableShapeDrag(leafletMap.current, poly) // ✥ move-whole-shape handle
    editingPolyRef.current = { poly, zoneId, disposeDrag }
    setEditingShape(true)
  }
  async function saveEditShape() {
    const ctx = editingPolyRef.current
    setEditingShape(false); editingPolyRef.current = null
    if (!ctx) return
    ctx.disposeDrag?.()
    try { ctx.poly.editing.disable() } catch { /* ignore */ }
    try { await plantZonesApi.update(ctx.zoneId, { geometry: ctx.poly.toGeoJSON().geometry }) } catch { /* ignore */ }
    qc.invalidateQueries({ queryKey: ['plant-zones'] })
    qc.invalidateQueries({ queryKey: ['blocks'] })
  }
  function cancelEditShape() {
    const ctx = editingPolyRef.current
    setEditingShape(false); editingPolyRef.current = null
    if (ctx) { ctx.disposeDrag?.(); try { ctx.poly.editing.disable() } catch { /* ignore */ } }
    qc.invalidateQueries({ queryKey: ['plant-zones'] }) // re-render reverts the shape
  }

  // ── Reshape an irrigation PIPE (move/length/angle + add bends, 90° shifts) ─────
  // Enables leaflet-draw per-layer vertex editing on the selected pipe's polyline:
  // drag a vertex to move/re-angle, drag a midpoint handle to insert a bend.
  function startPipeShapeEdit() {
    if (!editInfra || editInfra.kind !== 'pipe') return
    const poly = pipeLayersRef.current[editInfra.data.id]
    if (!poly?.editing) return
    poly.editing.enable()
    const disposeDrag = enableShapeDrag(leafletMap.current, poly) // ✥ move-whole-pipe handle
    pipeEditRef.current = { poly, id: editInfra.data.id, disposeDrag }
    setEditingPipeShape(true)
  }
  async function savePipeShapeEdit() {
    const ctx = pipeEditRef.current
    setEditingPipeShape(false); pipeEditRef.current = null
    if (!ctx) return
    ctx.disposeDrag?.()
    try { ctx.poly.editing.disable() } catch { /* ignore */ }
    try {
      // Only geometry — partial update preserves role/size/material/colour.
      await irrigationInfraApi.updatePipe(ctx.id, { geometry: ctx.poly.toGeoJSON().geometry } as Partial<IrrigationPipe>)
    } catch { /* re-render reflects state */ }
    qc.invalidateQueries({ queryKey: ['irrigation-infra'] })
    setEditInfra(null)
  }
  function cancelPipeShapeEdit() {
    const ctx = pipeEditRef.current
    setEditingPipeShape(false); pipeEditRef.current = null
    if (ctx) { ctx.disposeDrag?.(); try { ctx.poly.editing.disable() } catch { /* ignore */ } }
    qc.invalidateQueries({ queryKey: ['irrigation-infra'] }) // re-render reverts the shape
  }

  // ── Resize / move a STRUCTURE (drop a preset box, then drag corners) ─────────
  // Same per-layer vertex-edit + ✥ whole-shape drag the pipe/block reshape uses.
  function startStructureShapeEdit() {
    if (!editInfra || editInfra.kind !== 'structure') return
    const poly = structureLayersRef.current[editInfra.data.id]
    if (!poly?.editing) return // point structures (no polygon) aren't resizable
    poly.editing.enable()
    const disposeDrag = enableShapeDrag(leafletMap.current, poly) // ✥ move-whole-building handle
    structureEditRef.current = { poly, id: editInfra.data.id, disposeDrag }
    setEditingStructureShape(true)
  }
  async function saveStructureShapeEdit() {
    const ctx = structureEditRef.current
    const s = editInfra?.kind === 'structure' ? editInfra.data : null
    setEditingStructureShape(false); structureEditRef.current = null
    if (!ctx) return
    ctx.disposeDrag?.()
    try { ctx.poly.editing.disable() } catch { /* ignore */ }
    try {
      // backend Update overwrites name/type/colour (only geometry is COALESCE'd), so resend them.
      await structuresApi.update(ctx.id, { name: s?.name, structure_type: s?.structure_type, color_hex: s?.color_hex, notes: s?.notes, geometry: ctx.poly.toGeoJSON().geometry } as Partial<Structure>)
    } catch { /* re-render reflects state */ }
    qc.invalidateQueries({ queryKey: ['structures'] })
    setEditInfra(null)
  }
  function cancelStructureShapeEdit() {
    const ctx = structureEditRef.current
    setEditingStructureShape(false); structureEditRef.current = null
    if (ctx) { ctx.disposeDrag?.(); try { ctx.poly.editing.disable() } catch { /* ignore */ } }
    qc.invalidateQueries({ queryKey: ['structures'] }) // re-render reverts the shape
  }

  // Drop a preset structure (pre-sized box at map centre), then auto-open it for resize.
  async function dropStructurePreset(p: StructurePreset) {
    const map = leafletMap.current
    if (!map) return
    setShowStructurePalette(false)
    const c = map.getCenter()
    try {
      const created = await structuresApi.create({ name: p.label, structure_type: p.type, color_hex: p.color, geometry: rectPolygonAround(c.lat, c.lng, p.w, p.h) } as Partial<Structure>)
      pendingStructureEditRef.current = created.data.id // render effect auto-enters resize once it's drawn
      setEditInfra({ kind: 'structure', data: created.data })
      qc.invalidateQueries({ queryKey: ['structures'] })
      flashInfra(`Placed ${p.label} — drag a corner to resize, ✥ to move`)
    } catch {
      flashInfra('Could not place structure')
    }
  }

  // ── Reshape the property boundary (farm_feature polygon) ──────────────────
  function startEditBoundary() {
    if (!selectedFeature) return
    const poly = boundaryLayerRef.current?.getLayers?.()[0]
    if (!poly?.editing) return
    poly.editing.enable()
    const disposeDrag = enableShapeDrag(leafletMap.current, poly) // ✥ move-whole-boundary handle
    boundaryEditRef.current = { poly, id: selectedFeature.id, disposeDrag }
    setEditingBoundary(true)
  }
  async function saveEditBoundary() {
    const ctx = boundaryEditRef.current
    setEditingBoundary(false); boundaryEditRef.current = null
    if (!ctx) return
    ctx.disposeDrag?.()
    try { ctx.poly.editing.disable() } catch { /* ignore */ }
    try { await farmMapApi.updateFeature(ctx.id, { geometry: ctx.poly.toGeoJSON().geometry } as Partial<FarmFeature>) } catch { /* ignore */ }
    qc.invalidateQueries({ queryKey: ['map-features'] })
  }
  function cancelEditBoundary() {
    const ctx = boundaryEditRef.current
    setEditingBoundary(false); boundaryEditRef.current = null
    if (ctx) { ctx.disposeDrag?.(); try { ctx.poly.editing.disable() } catch { /* ignore */ } }
    qc.invalidateQueries({ queryKey: ['map-features'] }) // re-render reverts the shape
  }

  // Keep refs in sync with state
  placingRef.current = placingSpecies
  drawTypeRef.current = drawingType
  selectedBlockRef.current = selectedBlock?.id ?? null
  structureTypeRef.current = structureType
  structureColorRef.current = structureColor

  const qc = useQueryClient()

  const { data: featuresData } = useQuery({
    queryKey: ['map-features'],
    queryFn: () => farmMapApi.listFeatures(),
  })

  const { data: prefsData } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => prefsApi.get().then(r => r.data),
    staleTime: 5 * 60_000,
  })
  const measurementSystem: MeasurementSystem = (prefsData?.measurement_system as MeasurementSystem) ?? 'imperial'

  // Yield/plant stats for the selected block (backend rolls up harvest_events).
  const { data: blockStats } = useQuery({
    queryKey: ['block-stats', selectedBlock?.id],
    queryFn: () => blocksApi.stats(selectedBlock!.id).then(r => r.data),
    enabled: !!selectedBlock,
    staleTime: 60_000,
  })

  // Inline rename for the selected block/zone (created as "New block"/"New zone").
  // Keyed on ID, not object identity — so a background blocks/zones refetch (e.g. the
  // acreage backfill) can't wipe what you're mid-typing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRenameVal(selectedBlock?.name ?? selectedZone?.name ?? '') }, [selectedBlock?.id, selectedZone?.id])
  async function saveName() {
    const name = renameVal.trim()
    if (!name) return
    if (selectedBlock && name !== selectedBlock.name) {
      // Optimistically update the on-map label immediately (the render effect also
      // rebinds it after the refetch).
      try { zoneLayersRef.current[selectedBlock.zone_id ?? '']?.setTooltipContent(`${name}${selectedBlock.acreage ? ` · ${selectedBlock.acreage.toFixed(2)} ac` : ''}`) } catch { /* ignore */ }
      try { await blocksApi.update(selectedBlock.id, { name }); setSelectedBlock({ ...selectedBlock, name }); qc.invalidateQueries({ queryKey: ['blocks'] }) } catch { /* ignore */ }
    } else if (selectedZone && name !== selectedZone.name) {
      try { await plantZonesApi.update(selectedZone.id, { name }); setSelectedZone({ ...selectedZone, name }); qc.invalidateQueries({ queryKey: ['plant-zones'] }) } catch { /* ignore */ }
    }
  }

  // Recolour the selected block/zone. A block's colour lives on its backing plant_zone
  // (block.zone_id); a standalone zone/sub-zone on its own id. '' = revert to the type default.
  async function saveZoneColor(color: string) {
    const zid = selectedBlock?.zone_id ?? selectedZone?.id
    if (!zid) return
    try {
      await plantZonesApi.update(zid, { color_hex: color })
      if (selectedZone) setSelectedZone({ ...selectedZone, color_hex: color })
      qc.invalidateQueries({ queryKey: ['plant-zones'] })
    } catch { /* render reflects state after refetch */ }
  }

  // Blocks + their backing/sub plant-zones (blocks-first map model).
  const { data: blocksData } = useQuery({
    queryKey: ['blocks'],
    queryFn: async (): Promise<Block[]> => {
      try { return (await blocksApi.list()).data.blocks ?? [] } catch { return [] }
    },
    retry: false,
  })
  const { data: plantZones = [] as PlantZone[] } = useQuery({
    queryKey: ['plant-zones'],
    queryFn: async (): Promise<PlantZone[]> => {
      try { return (await plantZonesApi.list()).data ?? [] } catch { return [] }
    },
    retry: false,
  })
  const blocks = blocksData ?? []
  // Refs so the once-registered map click handler sees fresh blocks/zones for
  // point-in-polygon block assignment.
  const blocksRef = useRef<Block[]>([])
  const plantZonesRef = useRef<PlantZone[]>([])
  // Leaflet layer per plant-zone id so a selected block/zone polygon can be reshaped.
  const zoneLayersRef = useRef<Record<string, any>>({})
  const pipeLayersRef = useRef<Record<string, any>>({}) // pipe id → rendered polyline (for shape editing)
  const acreageBackfilledRef = useRef<Set<string>>(new Set()) // blocks whose acreage we've auto-saved this session
  const editingPolyRef = useRef<{ poly: any; zoneId: string; disposeDrag?: () => void } | null>(null)
  const pipeEditRef = useRef<{ poly: any; id: string; disposeDrag?: () => void } | null>(null)
  const [editingPipeShape, setEditingPipeShape] = useState(false)
  const structureLayersRef = useRef<Record<string, any>>({}) // structure id → rendered polygon (for resize)
  const structureEditRef = useRef<{ poly: any; id: string; disposeDrag?: () => void } | null>(null)
  const [editingStructureShape, setEditingStructureShape] = useState(false)
  const pendingStructureEditRef = useRef<string | null>(null) // structure id to auto-open for resize after it renders
  const [showStructurePalette, setShowStructurePalette] = useState(false)
  const maskLayerRef = useRef<any>(null)
  const boundaryLayerRef = useRef<any>(null)
  const boundaryEditRef = useRef<{ poly: any; id: string; disposeDrag?: () => void } | null>(null)
  blocksRef.current = blocks
  plantZonesRef.current = plantZones

  // The selected block/zone's polygon ring (block → its backing plant_zone). Drives
  // both the snap-to-zone and the "trees in here" list so you can re-find them.
  const selectedRing = useMemo(() => {
    if (selectedBlock) return outerRing(plantZones.find(z => z.id === selectedBlock.zone_id)?.geometry)
    if (selectedZone) return outerRing(selectedZone.geometry)
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBlock?.id, selectedZone?.id, plantZones])

  // Tree/plant pins inside the selected polygon — the panel lists them so the grower
  // can re-find the trees in a zone (and jump to any one).
  const treesInSelected = useMemo(() => {
    if (!selectedRing || selectedRing.length < 3) return [] as { feature: FarmFeature; lat: number; lng: number; label: string }[]
    return (featuresData?.data?.features ?? [])
      .filter(f => (f.geometry as any)?.type === 'Point'
        && (((f.properties as Record<string, unknown> | undefined)?.plant_id) || ['tree', 'palm', 'shrub'].includes(f.feature_type))
        && inActivePlot(f.location_id))
      .map(f => {
        const c = (f.geometry as any).coordinates as [number, number] // [lng,lat]
        return { feature: f, lng: c[0], lat: c[1], label: f.name || (f.properties?.species_name as string) || 'Tree' }
      })
      .filter(t => pointInRing(t.lng, t.lat, selectedRing))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRing, featuresData, activePlotId])

  // Snap the map to the selected block/zone (once per selection id) so it's easy to
  // re-find — fitBounds with padding + a maxZoom cap so a tiny zone doesn't over-zoom.
  const fitForRef = useRef<string | null>(null)
  useEffect(() => {
    const map = leafletMap.current
    const id = selectedBlock?.id ?? selectedZone?.id ?? null
    if (!id) { fitForRef.current = null; return }
    if (!map || !mapReady || fitForRef.current === id) return
    if (!selectedRing || selectedRing.length < 3) return
    fitForRef.current = id
    try { map.fitBounds(selectedRing.map(([lng, lat]) => [lat, lng]) as [number, number][], { padding: [60, 60], maxZoom: 19 }) } catch { /* ignore */ }
  }, [selectedBlock?.id, selectedZone?.id, selectedRing, mapReady])

  // Keep the selected block/zone pointing at the latest query data so the panel
  // (name, acreage, plant count, stats) stays current and a deleted one auto-closes.
  // Compares by id; only swaps the object when the data actually changed.
  useEffect(() => {
    if (!selectedBlock) return
    const fresh = blocks.find(b => b.id === selectedBlock.id)
    if (fresh) { if (fresh !== selectedBlock) setSelectedBlock(fresh) }
    else if (blocksData) setSelectedBlock(null) // gone (only after data has loaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]) // selectedBlock handled by id compare inside
  useEffect(() => {
    if (!selectedZone) return
    const fresh = plantZones.find(z => z.id === selectedZone.id)
    if (fresh) { if (fresh !== selectedZone) setSelectedZone(fresh) }
    else setSelectedZone(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantZones])

  // Legacy tree pins that were placed before trees became real Plants — they have
  // no properties.plant_id. The backfill button below turns them into Plants.
  const legacyTrees = (featuresData?.data?.features ?? []).filter(
    f => ['tree', 'palm', 'shrub'].includes(f.feature_type) && !(f.properties as Record<string, unknown> | undefined)?.plant_id,
  )

  const { data: irrigationZones = [] as IrrigationZone[] } = useQuery({
    queryKey: ['irrigation-zones'],
    queryFn: async (): Promise<IrrigationZone[]> => {
      try {
        // Graceful degradation — irrigation zones may not be configured
        const res = await irrigationApi.listZones()
        return (res.data?.zones ?? []) as unknown as IrrigationZone[]
      } catch {
        return []
      }
    },
    retry: false,
  })

  // Irrigation infrastructure layer (§174): underground pipes (mainline always-pressurized vs
  // lateral zone pipes), valves, manifolds, and water sources (well pumps / city-water ingress).
  // All graceful — irrigation plumbing may not be mapped yet.
  const { data: irrigationInfra = { pipes: [], valves: [], manifolds: [], sources: [] } } = useQuery({
    queryKey: ['irrigation-infra'],
    queryFn: async (): Promise<{ pipes: IrrigationPipe[]; valves: IrrigationValve[]; manifolds: IrrigationManifold[]; sources: IrrigationSource[] }> => {
      const [p, v, m, s] = await Promise.all([
        irrigationInfraApi.listPipes().then(r => r.data.pipes ?? []).catch(() => [] as IrrigationPipe[]),
        irrigationInfraApi.listValves().then(r => r.data.valves ?? []).catch(() => [] as IrrigationValve[]),
        irrigationInfraApi.listManifolds().then(r => r.data.manifolds ?? []).catch(() => [] as IrrigationManifold[]),
        irrigationInfraApi.listSources().then(r => r.data.sources ?? []).catch(() => [] as IrrigationSource[]),
      ])
      return { pipes: p, valves: v, manifolds: m, sources: s }
    },
    retry: false,
  })

  // Structures (barns, well houses, packing houses, …) — color-coded resizable polygons (§174).
  const { data: structuresList = [] as Structure[] } = useQuery({
    queryKey: ['structures'],
    queryFn: async (): Promise<Structure[]> => { try { return (await structuresApi.list()).data.structures ?? [] } catch { return [] } },
    retry: false,
  })
  // Mirror infra into a ref so the (bound-once-per-draw) snap handlers read fresh targets.
  infraRef.current = irrigationInfra

  // Nominal pipe-size catalog (US + metric). The size dropdown defaults to the tenant's
  // measurement system (§174) — imperial → US sizes, metric → DN sizes.
  const { data: pipeSizes = [] } = useQuery({
    queryKey: ['pipe-sizes'],
    queryFn: async () => { try { return (await irrigationInfraApi.pipeSizes()).data.sizes ?? [] } catch { return [] } },
    staleTime: Infinity, retry: false,
  })
  const { data: userPrefs } = useQuery({ queryKey: ['user-preferences'], queryFn: () => prefsApi.get().then(r => r.data), retry: false })
  const pipeSystem = userPrefs?.measurement_system === 'metric' ? 'metric' : 'us'
  const sizesForSystem = useMemo(() => pipeSizes.filter(s => s.system === pipeSystem), [pipeSizes, pipeSystem])
  const pipeSizeTouchedRef = useRef(false)
  // Default the pipe size to a sensible nominal for the tenant's system, until the user picks one.
  useEffect(() => {
    if (pipeSizeTouchedRef.current || sizesForSystem.length === 0) return
    const preferred = sizesForSystem.find(s => s.label === '3/4"' || s.label === 'DN20') ?? sizesForSystem[0]
    setPipeSize(preferred.label)
  }, [sizesForSystem])

  // All plants (carry block_id / irrigation_zone_id) — drives spatial auto-assign:
  // when a block/zone is drawn we only absorb plants that aren't already assigned.
  const { data: plantsData } = useQuery({
    queryKey: ['plants'],
    queryFn: async (): Promise<Plant[]> => { try { return (await plantsApi.list()).data.plants ?? [] } catch { return [] } },
    retry: false,
  })
  const plants = plantsData ?? []
  const plantsRef = useRef<Plant[]>([])
  const featuresRef = useRef<FarmFeature[]>([])
  const irrigationZonesRef = useRef<IrrigationZone[]>([])
  plantsRef.current = plants
  featuresRef.current = featuresData?.data?.features ?? []
  irrigationZonesRef.current = irrigationZones

  // ── Tag filter (Layers panel) — show only entities carrying the selected tags ──
  // Inheritance mirrors the Blocks page: a tag on a plot applies to its blocks/
  // zones/plants; a tag on a block applies to its sub-zones + plants; an
  // irrigation-zone tag applies to plants assigned to it. Match-ANY across the
  // selected tags. Structures + the property boundary are context — never filtered.
  const { tags: allTags, assignments: tagAssignments } = useTags()
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set())
  const tagDirect = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const a of tagAssignments) {
      const k = `${a.entity_type}|${a.entity_id}`
      if (!m.has(k)) m.set(k, new Set())
      m.get(k)!.add(a.tag_id)
    }
    return m
  }, [tagAssignments])
  const addTagsOf = (eff: Set<string>, type: string, id?: string | null) => {
    if (!id) return
    const s = tagDirect.get(`${type}|${id}`)
    if (s) for (const t of s) eff.add(t)
  }
  const blockEffTags = (b?: Block | null): Set<string> => {
    const eff = new Set<string>()
    if (!b) return eff
    addTagsOf(eff, 'block', b.id)
    addTagsOf(eff, 'location', b.location_id)
    return eff
  }
  const passesTagFilter = (eff: Set<string>) => tagFilter.size === 0 || [...tagFilter].some(t => eff.has(t))

  // Assign a single plant pin to a block / irrigation zone (quick action on a pin).
  const quickAssign = async (plantId: string, target: { block_id?: string; irrigation_zone_id?: string; zone_id?: string }) => {
    try { await plantsApi.assign([plantId], target) } catch { return }
    qc.invalidateQueries({ queryKey: ['plants'] })
    qc.invalidateQueries({ queryKey: ['blocks'] })
    qc.invalidateQueries({ queryKey: ['plant-zones'] })
  }

  // Convert ONE tree pin (no plant_id yet) into a real Plant linked to it, auto-
  // assigning the block / plant zone / irrigation zone it sits inside. Used by the
  // feature panel's inline "Make this a Plant record" button so the action is right
  // where the user is (the bulk "Convert N map trees" button is in the list view).
  const convertFeatureToPlant = async (f: FarmFeature) => {
    const g = f.geometry as { type?: string; coordinates?: number[] } | undefined
    if (g?.type !== 'Point' || !Array.isArray(g.coordinates)) return
    const [lng, lat] = g.coordinates
    const props = (f.properties as Record<string, unknown> | undefined) ?? {}
    try {
      const p = await plantsApi.create({
        label: f.name || (props.species_name as string) || 'Tree', status: 'active',
        location_feature_id: f.id,
        block_id: blockIdAt(lat, lng), irrigation_zone_id: irrigationZoneIdAt(lat, lng), zone_id: plantZoneAt(lat, lng),
      } as Partial<Plant>)
      await farmMapApi.updateFeature(f.id, { properties: { ...props, plant_id: p.data.id } } as Partial<FarmFeature>)
      qc.invalidateQueries({ queryKey: ['map-features'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      qc.invalidateQueries({ queryKey: ['blocks'] })
      setSelectedFeature(null)
      flashAbsorb('Saved to Plants')
    } catch (e) { if (!handlePlantLimit(e)) flashAbsorb('Could not convert — try again') }
  }

  const updateFeature = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FarmFeature> }) =>
      farmMapApi.updateFeature(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['map-features'] }),
  })

  const deleteFeature = useMutation({
    mutationFn: farmMapApi.deleteFeature,
    onSuccess: () => {
      setSelectedFeature(null)
      qc.invalidateQueries({ queryKey: ['map-features'] })
    },
  })

  // ── Map initialization (runs once) ────────────────────────────────────

  useEffect(() => {
    if (!mapRef.current || leafletMap.current) return

    import('leaflet').then(async (mod) => {
      // Use the DEFAULT export (the real, mutable Leaflet object), NOT the ESM
      // namespace — the namespace is frozen, so leaflet-draw's `L.drawVersion = …`
      // throws "object is not extensible". leaflet-draw is a side-effect plugin
      // that adds L.Control.Draw / L.Draw and looks up the global L, so expose it
      // before importing. Both imports are no-ops if already loaded.
      const L: any = (mod as any).default ?? mod
      ;(window as any).L = L
      await import('leaflet-draw')

      const savedLoc = loadFarmLocation()
      const savedView = loadSavedView()
      const center: [number, number] = savedView
        ? [savedView.lat, savedView.lng]
        : savedLoc ? [savedLoc.lat, savedLoc.lng] : DEFAULT_CENTER
      // Zoom control lives bottom-right (Google-Maps-like) so it doesn't sit under
      // the top-left "Layers" panel.
      const map = L.map(mapRef.current!, { center, zoom: savedView ? savedView.zoom : 17, zoomControl: false })
      L.control.zoom({ position: 'bottomright' }).addTo(map)
      // Pane for the "dim outside the farm boundary" mask — above tiles, below the
      // overlays/markers, and non-interactive so it never intercepts clicks.
      map.createPane('maskPane')
      const mp = map.getPane('maskPane')
      if (mp) { mp.style.zIndex = '350'; mp.style.pointerEvents = 'none' }
      leafletMap.current = map
      // Keep draw-vertex dots sized to the current zoom (smaller out, bigger in).
      map.on('zoomend', () => updateVtxSize())
      // Refresh lot lines for the new view when panning/zooming (debounced),
      // only while the "Show lot lines" overlay is on.
      map.on('moveend', () => {
        if (!showLotsRef.current) return
        clearTimeout(lotDebounce.current)
        lotDebounce.current = setTimeout(() => loadLotLinesRef.current?.(), 400)
      })
      // Container may be 0-sized at first paint inside the flex layout — recompute
      // tile geometry on the next frame so the whole viewport fills (not just 2 tiles).
      setTimeout(() => map.invalidateSize(), 0)

      // Satellite base layer — Esri World Imagery (no API key required, fair use)
      // crossOrigin: '' prevents canvas taint issues in some browsers.
      // errorTileUrl: transparent 1×1 PNG replaces broken tiles instead of showing black squares.
      const TRANSPARENT_TILE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: '© Esri, Maxar, Earthstar Geographics',
          maxZoom: 22,
          // Esri imagery only has native tiles to ~z19 globally; beyond that Leaflet
          // upscales the z19 tile (slightly soft but CONTINUOUS) instead of blanking it
          // with errorTileUrl — fixes the hard "jump"/disappear at the closest zoom.
          maxNativeZoom: 19,
          crossOrigin: '',
          errorTileUrl: TRANSPARENT_TILE,
        }
      ).addTo(map)

      // Label overlay — roads, place names, boundaries
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 22, opacity: 0.6, crossOrigin: '', errorTileUrl: TRANSPARENT_TILE }
      ).addTo(map)

      // Separate layer group per overlay type
      ;(['plants', 'plantZones', 'irrigationZones', 'irrigationInfra', 'structures'] as LayerKey[]).forEach(key => {
        const lg = new (L as any).FeatureGroup()
        layerRefs.current[key] = lg
        lg.addTo(map)
      })

      // NOTE: we deliberately do NOT add the leaflet-draw toolbar (L.Control.Draw).
      // Drawing is driven by the custom "+ Block / + Sub-zone / + Irrigation" buttons
      // (startDraw → new L.Draw.Polygon().enable()), which fire the same CREATED
      // event below. The toolbar only duplicated that UI and sat under the top-left
      // Layers panel. (L.Draw.Polygon works standalone — it doesn't need the control.)
      map.on((L as any).Draw.Event.CREATED, async (e: any) => {
        const geoJson = e.layer.toGeoJSON()
        const dt = drawTypeRef.current
        try {
          const plot = activePlotId ?? undefined // tag new map entities to the active plot
          if (dt === 'boundary') {
            // Manually-drawn property boundary (when no public parcel source covers this plot).
            await saveBoundary(geoJson.geometry)
            qc.invalidateQueries({ queryKey: ['map-features'] })
            setParcelMsg('✓ Property boundary drawn.')
          } else if (dt === 'irrigation_zone') {
            // Zone-as-hub (§174): irrigation zones live in the irrigation_zones table (the named
            // "Zone N" a valve + controller bind to), NOT farm_features.
            await irrigationApi.createZone({ name: 'Zone', geometry: geoJson.geometry, color_hex: LAYER_COLORS.irrigationZones })
            qc.invalidateQueries({ queryKey: ['irrigation-zones'] })
          } else if (dt === 'sub_zone' && selectedBlockRef.current) {
            await plantZonesApi.create({ name: 'New sub-zone', geometry: geoJson.geometry, block_id: selectedBlockRef.current, location_id: plot })
            qc.invalidateQueries({ queryKey: ['plant-zones'] })
          } else if (dt === 'structure') {
            // A resizable, color-coded building/structure polygon (§174).
            const label = structureTypeRef.current.replace(/_/g, ' ')
            await structuresApi.create({ name: `New ${label}`, structure_type: structureTypeRef.current, color_hex: structureColorRef.current, geometry: geoJson.geometry } as Partial<Structure>)
            qc.invalidateQueries({ queryKey: ['structures'] })
            flashInfra(`${label.charAt(0).toUpperCase() + label.slice(1)} added`)
          } else {
            // Default: a Block. Create the backing geometry zone, then the block
            // that owns it 1:1 (blocks-first model). Acreage is auto-calculated from
            // the drawn polygon's actual coordinates (geodesic area).
            const ring = (e.layer.getLatLngs?.()?.[0] ?? []) as { lat: number; lng: number }[]
            const acres = polygonAcres(ring)
            const zone = await plantZonesApi.create({ name: 'New block', geometry: geoJson.geometry, location_id: plot })
            const created = await blocksApi.create({ name: 'New block', zone_id: zone.data.id, location_id: plot, acreage: acres ? Math.round(acres * 100) / 100 : undefined })
            // Optimistically seed the new zone into the plant-zones cache so the block
            // panel's "Detect trees in this block" button (which resolves the polygon
            // from plantZones by zone_id) works IMMEDIATELY — not only after the refetch.
            qc.setQueryData<PlantZone[]>(['plant-zones'], old => old ? [...old, zone.data] : [zone.data])
            qc.invalidateQueries({ queryKey: ['plant-zones'] })
            qc.invalidateQueries({ queryKey: ['blocks'] })
            // Select the just-drawn block so the panel shows ITS name + detect (not the
            // previously-selected block, which used to stay selected after a new draw).
            setSelectedZone(null)
            setSelectedBlock(created.data)
            // Auto-absorb any unassigned plant pins that fall inside the new block.
            const polyRing = (geoJson.geometry as { coordinates?: number[][][] } | undefined)?.coordinates?.[0]
            if (polyRing && created.data?.id) {
              const n = await absorbPlantsIntoBlock(polyRing, created.data.id)
              if (n > 0) flashAbsorb(`Added ${n} plant${n > 1 ? 's' : ''} inside to this block`)
            }
          }
        } catch { /* refetch reflects state; failure is non-fatal */ }
        drawerRef.current = null
        detachDrawHelpers()
        setDrawingType(null)
      })

      // Plant placement click handler — uses refs so it always sees fresh state.
      // Creates the pin + a real Plant linked to it (see placeTreeAt).
      map.on('click', (e: any) => {
        const plant = placingRef.current
        if (!plant) return
        const { lat, lng } = e.latlng
        placeTreeAt(plant, lat, lng)
        setPlacingSpecies(null)
      })

      // Signal that the map + layer groups exist so the render effects (which guard
      // on leafletMap.current) re-run now that they have somewhere to draw.
      setMapReady(true)
      return () => { map.remove(); leafletMap.current = null; setMapReady(false) }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Remember layer visibility across visits (default all on).
  useEffect(() => { try { localStorage.setItem(LAYERS_KEY, JSON.stringify(layers)) } catch { /* ignore */ } }, [layers])

  // ── Render features from /map/features ────────────────────────────────

  useEffect(() => {
    const features = featuresData?.data?.features
    if (!leafletMap.current || !features) return
    // Lookups for tag-filter inheritance (plant → its block / irrigation zone).
    const plantById = new Map(plants.map(p => [p.id, p]))
    const blockById = new Map(blocks.map(b => [b.id, b]))
    import('leaflet').then((L) => {
      // plantZones layer is owned by the blocks/plant-zones effect below.
      ;(['plants', 'structures'] as LayerKey[]).forEach(k =>
        layerRefs.current[k]?.clearLayers()
      )

      for (const feature of features) {
        try {
          const ft = feature.feature_type
          // Generic farm_feature "zone" rows are legacy — real zones/blocks render
          // from /plant-zones in the effect below. Skip to avoid duplicates.
          if (ft === 'zone') continue
          // Irrigation zones now live in the irrigation_zones table (zone-as-hub, §174) and
          // render from the irrigationZones query below — skip legacy farm_feature ones.
          if (ft === 'irrigation_zone') continue
          // Per-plot boundaries: only render the active plot's property outline
          // (others belong to plots you're not viewing). Matches the mask below.
          if (ft === 'boundary' && (feature.location_id ?? null) !== (activePlotId ?? null)) continue
          // Non-boundary features (plant/tree pins, structures, irrigation-zone
          // features): null-safe per-plot scoping — hide ones tagged to another plot.
          if (ft !== 'boundary' && !inActivePlot(feature.location_id)) continue
          const layerKey: LayerKey =
            ft === 'irrigation_zone' ? 'irrigationZones' :
            ft === 'boundary' || ft === 'structure' || ft === 'bed' ? 'structures' : 'plants'

          // Tag filter: a tree/plant pin must carry a selected tag — directly, via
          // its block, via its irrigation zone, or inherited from its plot.
          if (tagFilter.size > 0 && layerKey === 'plants') {
            const tfPid = (feature.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined
            const tfPlant = tfPid ? plantById.get(tfPid) : undefined
            const eff = new Set<string>()
            addTagsOf(eff, 'plant', tfPid)
            if (tfPlant?.block_id) for (const t of blockEffTags(blockById.get(tfPlant.block_id))) eff.add(t)
            addTagsOf(eff, 'irrigation_zone', tfPlant?.irrigation_zone_id)
            addTagsOf(eff, 'location', feature.location_id)
            if (!passesTagFilter(eff)) continue
          }

          // Colour each tree/plant marker by its variety: prefer the catalog
          // swatch (already fruit-aligned), else derive a fruit-aligned colour from
          // the variety/species name (so real DB varieties get a meaningful colour
          // too, with a slight per-variety shade variance), else the generic plant
          // colour. (mango → amber, citrus → orange, avocado → green, …)
          const catalogColor = PLANT_CATALOG.find(p => p.id === feature.properties?.species_id)?.color
          const namedColor = feature.properties?.species_name ? varietyColor(String(feature.properties.species_name)) : undefined
          const speciesColor = ft !== 'zone' && ft !== 'irrigation_zone'
            ? (catalogColor ?? namedColor ?? LAYER_COLORS.plants)
            : LAYER_COLORS[layerKey]

          const speciesLabel = feature.properties?.species_name
            ? ` (${feature.properties.species_name})`
            : ''
          const plantId = (feature.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined
          const isTreePin = ft === 'tree' || ft === 'palm' || ft === 'shrub' || !!plantId
          const selectFeature = (e: any) => {
            e.originalEvent?.stopPropagation()
            // Multi-select mode: clicking a tree pin toggles it (any tree/palm/shrub
            // pin, not only Plant-linked ones — so unlinked trees select too).
            if (selectMode && isTreePin) {
              setSelectedIds(prev => {
                const n = new Set(prev)
                if (n.has(feature.id)) n.delete(feature.id); else n.add(feature.id)
                return n
              })
              return
            }
            setSelectedFeature(feature)
            setActiveTab('features')
          }

          let layer: any
          if ((feature.geometry as any)?.type === 'Point') {
            // Draggable pin — nudge a tree/plant into place, or remap it when it
            // physically moves. dragend persists the new coordinates. In select mode
            // dragging is disabled so a click toggles selection cleanly.
            const coords = (feature.geometry as any).coordinates as [number, number]
            const r = ft === 'palm' ? 6 : 9
            const sz = r * 2
            const selected = selectedIds.has(feature.id)
            const ring = selected
              ? `box-shadow:0 0 0 3px #fff,0 0 0 6px ${speciesColor};`
              : `box-shadow:0 0 0 1px rgba(0,0,0,0.35);`
            const icon = L.divIcon({
              className: 'farm-pin-draggable',
              html: `<div style="width:${sz}px;height:${sz}px;border-radius:50%;background:${speciesColor};border:1.5px solid #fff;${ring}cursor:${selectMode ? 'pointer' : 'grab'}"></div>`,
              iconSize: [sz, sz],
              iconAnchor: [r, r],
            })
            const m = L.marker([coords[1], coords[0]], { icon, draggable: !selectMode })
            m.on('click', selectFeature)
            m.on('dragstart', () => m.closeTooltip())
            m.on('dragend', (e: any) => {
              const ll = e.target.getLatLng()
              updateFeature.mutate({
                id: feature.id,
                data: { geometry: { type: 'Point', coordinates: [ll.lng, ll.lat] } } as Partial<FarmFeature>,
              })
            })
            layer = m
          } else {
            const polyStyle = ft === 'boundary'
              ? { color: '#F5A623', weight: 3, dashArray: '8 5', fillColor: '#F5A623', fillOpacity: 0.05 }
              : { color: speciesColor, weight: 2, fillOpacity: 0.18 }
            layer = L.geoJSON(
              { type: 'Feature', geometry: feature.geometry, properties: {} } as any,
              { style: polyStyle }
            )
            layer.on('click', selectFeature)
            if (ft === 'boundary') boundaryLayerRef.current = layer
          }
          // No hover tooltip on the property boundary — it's a big polygon covering the
          // farm and the redundant "name" label was just noise over the map.
          if (ft !== 'boundary') layer.bindTooltip(escTip((feature.name || ft)) + escTip(speciesLabel), {
            permanent: false,
            direction: 'top',
            offset: [0, -4],
          })
          layerRefs.current[layerKey]?.addLayer(layer)
        } catch (_) {
          // unsupported geometry — skip silently
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresData, selectedIds, selectMode, activePlotId, mapReady, plants, blocks, tagFilter, tagDirect])

  // ── Marquee drag-select (select mode): drag a box over the map to select every ──
  // tree/plant pin inside it. Map panning is disabled while selecting so a drag draws
  // a rectangle instead of panning; a plain click (no drag) still toggles a single pin
  // via the marker handler. Big AI-detection results → rubber-band a region, then delete.
  useEffect(() => {
    if (!mapReady || !selectMode) return
    const map = leafletMap.current
    if (!map) return
    let cancelled = false
    let cleanup = () => {}
    import('leaflet').then((Lmod) => {
      if (cancelled) return
      const L = (Lmod as any).default ?? Lmod
      map.dragging.disable()
      if (map.boxZoom) map.boxZoom.disable()
      const container = map.getContainer()
      container.classList.add('grafty-selecting') // CSS forces crosshair over panes/markers
      container.style.cursor = 'crosshair'

      let startLL: any = null
      let moved = false
      let rect: any = null

      const down = (e: any) => { startLL = e.latlng; moved = false; rect = null }
      const move = (e: any) => {
        if (!startLL) return
        if (!moved) {
          // ignore sub-4px jitter so a plain click still toggles a pin (no marquee)
          const a = map.latLngToContainerPoint(startLL)
          const b = map.latLngToContainerPoint(e.latlng)
          if (Math.abs(a.x - b.x) < 4 && Math.abs(a.y - b.y) < 4) return
          moved = true
        }
        const bounds = L.latLngBounds(startLL, e.latlng)
        if (rect) rect.setBounds(bounds)
        else rect = L.rectangle(bounds, { color: '#2D6A2F', weight: 2, dashArray: '5 5', fillColor: '#2D6A2F', fillOpacity: 0.08, interactive: false }).addTo(map)
      }
      const up = () => {
        if (startLL && moved && rect) {
          const bounds = rect.getBounds()
          const feats = featuresData?.data?.features ?? []
          const hit: string[] = []
          for (const f of feats) {
            if ((f.geometry as any)?.type !== 'Point') continue
            // Any tree pin (tree/palm/shrub) OR a plant-linked pin — NOT only
            // Plant-linked ones, so trees that aren't a Plant yet still select.
            const ft = f.feature_type
            if (!(ft === 'tree' || ft === 'palm' || ft === 'shrub' || (f.properties as Record<string, unknown> | undefined)?.plant_id)) continue
            if (!inActivePlot(f.location_id)) continue
            const c = (f.geometry as any).coordinates as [number, number]
            if (bounds.contains([c[1], c[0]])) hit.push(f.id)
          }
          if (hit.length) setSelectedIds(prev => new Set([...prev, ...hit]))
        }
        if (rect) { try { map.removeLayer(rect) } catch { /* */ } rect = null }
        startLL = null; moved = false
      }
      map.on('mousedown', down); map.on('mousemove', move); map.on('mouseup', up)
      cleanup = () => {
        map.off('mousedown', down); map.off('mousemove', move); map.off('mouseup', up)
        if (rect) { try { map.removeLayer(rect) } catch { /* */ } }
        map.dragging.enable()
        if (map.boxZoom) map.boxZoom.enable()
        container.classList.remove('grafty-selecting')
        container.style.cursor = ''
      }
    })
    return () => { cancelled = true; cleanup() }
    // inActivePlot closes over activePlotId (already a dep) — no need to re-bind on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, selectMode, featuresData, activePlotId])

  // Keyboard Delete/Backspace bulk-deletes the marquee selection (with confirm).
  // Ignored while typing in a field; only active in select mode.
  useEffect(() => {
    if (!selectMode) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return
      if (selectedIds.size === 0) return
      e.preventDefault()
      deleteSelected()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectMode, selectedIds])

  // ── Dim everything outside the property boundary (inverted-polygon mask) ───
  useEffect(() => {
    const map = leafletMap.current
    if (!map) return
    import('leaflet').then((L) => {
      if (maskLayerRef.current) { map.removeLayer(maskLayerRef.current); maskLayerRef.current = null }
      if (!maskOn) return
      // Mask to the ACTIVE plot's boundary (per-plot), matching the render filter.
      const boundary = (featuresData?.data?.features ?? []).find(
        f => f.feature_type === 'boundary' && inActivePlot(f.location_id))
      const g = boundary?.geometry as any
      if (!g) return
      const toLatLng = (ring: number[][]) => ring.map(([lng, lat]) => [lat, lng] as [number, number])
      let holes: [number, number][][] = []
      if (g.type === 'Polygon' && g.coordinates?.[0]) holes = [toLatLng(g.coordinates[0])]
      else if (g.type === 'MultiPolygon') holes = (g.coordinates ?? []).map((poly: number[][][]) => toLatLng(poly[0])).filter((h: any[]) => h?.length)
      if (holes.length === 0) return
      // Big outer ring (whole world) + the boundary ring(s) as holes = a mask with
      // a window at the farm. Lives in the non-interactive low-z maskPane.
      const world: [number, number][] = [[-89.9, -179.9], [89.9, -179.9], [89.9, 179.9], [-89.9, 179.9]]
      const mask = (L as any).polygon([world, ...holes], {
        pane: 'maskPane', interactive: false, stroke: false, fillColor: '#000', fillOpacity: 0.45,
      })
      mask.addTo(map)
      maskLayerRef.current = mask
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresData, maskOn, activePlotId, mapReady])

  // ── Render blocks + plant-zones from /plant-zones ─────────────────────
  // Amber = a block's own polygon (its zone backs a block 1:1); purple dashed =
  // a species sub-zone inside a block; green = a standalone zone.

  useEffect(() => {
    if (!leafletMap.current) return
    const blockByZone = new Map<string, Block>()
    const blockById = new Map<string, Block>()
    for (const b of blocks) { if (b.zone_id) blockByZone.set(b.zone_id, b); blockById.set(b.id, b) }
    import('leaflet').then((L) => {
      layerRefs.current.plantZones?.clearLayers()
      zoneLayersRef.current = {}
      for (const z of plantZones) {
        if (!z.geometry) continue
        if (!inActivePlot(z.location_id)) continue // per-plot scoping (covers blocks via their backing zone)
        const block = blockByZone.get(z.id)
        const isSub = !!z.block_id
        // Tag filter: a block/zone polygon must carry a selected tag — its own, its
        // (parent) block's, or one inherited from its plot.
        if (tagFilter.size > 0) {
          const eff = new Set<string>()
          addTagsOf(eff, 'plant_zone', z.id)
          addTagsOf(eff, 'location', z.location_id)
          if (block) for (const t of blockEffTags(block)) eff.add(t)
          if (z.block_id) for (const t of blockEffTags(blockById.get(z.block_id))) eff.add(t)
          if (!passesTagFilter(eff)) continue
        }
        // Saved colour wins (editable in the block/zone panel); else the type default.
        const roleColor = block ? '#F5A623' : isSub ? '#A855F7' : LAYER_COLORS.plantZones
        const color = z.color_hex || roleColor
        // The currently-selected block/zone gets a bright, thick, filled outline so
        // it's OBVIOUS which one is active (fixes "selecting between blocks isn't clear").
        const selected = (block && selectedBlock?.id === block.id) || (!block && selectedZone?.id === z.id)
        // While the irrigation tools are open, blocks/zones go NON-interactive so clicks
        // fall through to pipes/valves/structures underneath (the grower's "if I'm in
        // irrigation edit mode I want it to select the pipe, not the block").
        try {
          const layer = L.geoJSON(
            { type: 'Feature', geometry: z.geometry as any, properties: {} } as any,
            { interactive: !showInfraTools, style: { color: selected ? '#FFFFFF' : color, weight: selected ? 4 : 2, fillColor: color, fillOpacity: selected ? 0.35 : (isSub ? 0.10 : 0.18), dashArray: isSub && !selected ? '5 4' : undefined } }
          )
          // Acreage: use the saved value, else compute from the polygon coordinates.
          // Backfill (save) once per session for blocks drawn before auto-acreage.
          const computedAc = geojsonPolygonAcres(z.geometry as any)
          const acres = block?.acreage ?? (computedAc || undefined)
          if (block && (block.acreage == null || block.acreage === 0) && computedAc > 0 && !acreageBackfilledRef.current.has(block.id)) {
            acreageBackfilledRef.current.add(block.id)
            blocksApi.update(block.id, { acreage: Math.round(computedAc * 100) / 100 })
              .then(() => qc.invalidateQueries({ queryKey: ['blocks'] }))
              .catch(() => acreageBackfilledRef.current.delete(block.id))
          }
          const acLabel = acres ? ` · ${acres.toFixed(2)} ac` : ''
          const selectThis = (ev?: any) => {
            ev?.originalEvent?.stopPropagation?.()
            ev?.stopPropagation?.()
            setSelectedFeature(null)
            if (block) { setSelectedBlock(block); setSelectedZone(null) }
            else { setSelectedZone(z); setSelectedBlock(null) }
          }
          // Blocks get a persistent label (name + acreage); zones/sub-zones a hover tip.
          // In irrigation mode the always-on label is suppressed (it renders above the
          // map in the tooltipPane and would steal clicks meant for pipes/valves).
          if (block && showBlockLabels && !showInfraTools) {
            // interactive:true makes the always-on label clickable. It renders in
            // the tooltipPane (above tree pins / sub-zones in the markerPane), so it
            // is a RELIABLE way to select a block whose polygon body is covered by
            // pins — fixes "sometimes I can't select the block".
            layer.bindTooltip(`${escTip(block.name)}${acLabel}`, { permanent: true, direction: 'center', className: 'block-map-label', interactive: true })
            layer.on('tooltipopen', (ev: any) => {
              const el = ev.tooltip?.getElement?.()
              if (el) { el.style.cursor = 'pointer'; el.onclick = (dom: MouseEvent) => { dom.stopPropagation(); selectThis() } }
            })
          } else if (block) {
            // Labels hidden: name on hover, polygon click still selects.
            layer.bindTooltip(`${escTip(block.name)}${acLabel}`, { permanent: false, direction: 'center' })
          } else {
            layer.bindTooltip(isSub ? `Sub-zone: ${escTip(z.name)}` : `Zone: ${escTip(z.name)}`, { permanent: false, direction: 'top' })
          }
          layer.on('click', selectThis)
          zoneLayersRef.current[z.id] = layer
          layerRefs.current.plantZones?.addLayer(layer)
        } catch { /* skip bad geometry */ }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantZones, blocks, activePlotId, mapReady, tagFilter, tagDirect, selectedBlock?.id, selectedZone?.id, showBlockLabels, showInfraTools])

  // ── Render irrigation zones from /irrigation/zones ────────────────────

  useEffect(() => {
    if (!leafletMap.current) return
    import('leaflet').then((L) => {
      layerRefs.current.irrigationZones?.clearLayers()
      for (const zone of irrigationZones) {
        if (!zone.geometry) continue
        // Tag filter: an irrigation zone must carry a selected tag directly.
        if (tagFilter.size > 0) {
          const eff = new Set<string>()
          addTagsOf(eff, 'irrigation_zone', zone.id)
          if (!passesTagFilter(eff)) continue
        }
        try {
          const layer = L.geoJSON(
            { type: 'Feature', geometry: zone.geometry, properties: {} } as any,
            {
              style: {
                color: zone.color_hex || LAYER_COLORS.irrigationZones, // saved colour wins
                weight: 2,
                fillOpacity: 0.12,
                dashArray: '6 4',
              },
            }
          )
          const label = zone.name || zone.controller_zone_name || 'Zone'
          layer.bindTooltip(`Irrigation: ${escTip(label)}`, { permanent: false })
          layer.on('click', (ev: any) => { ev?.originalEvent?.stopPropagation?.(); setEditInfra({ kind: 'irrigation_zone', data: zone }) })
          layerRefs.current.irrigationZones?.addLayer(layer)
        } catch (_) {
          // invalid zone geometry — skip silently
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [irrigationZones, mapReady, tagFilter, tagDirect])

  // ── Irrigation infrastructure layer (§174): pipes + valves/manifolds + sources ──
  // Mainline pipes (always pressurized, before the valve) render solid; lateral pipes
  // (the zone circuit, on only when the valve opens) render dashed + teal so the two
  // are visually distinct. Valves/manifolds/sources are point markers.
  useEffect(() => {
    if (!leafletMap.current || !mapReady) return
    import('leaflet').then((L) => {
      const lg = layerRefs.current.irrigationInfra
      if (!lg) return
      lg.clearLayers()
      pipeLayersRef.current = {} // rebuilt below; stale edit handles are gone with clearLayers
      // Pipes — LineString. Lateral = dashed teal; mainline = solid cyan.
      for (const pipe of irrigationInfra.pipes) {
        const coords = (pipe.geometry as any)?.coordinates
        if (!Array.isArray(coords) || coords.length < 2) continue
        const lateral = pipe.pipe_role === 'lateral'
        try {
          const line = L.polyline(coords.map((c: number[]) => [c[1], c[0]] as [number, number]), {
            color: pipe.color_hex || (lateral ? '#2DD4BF' : '#38BDF8'),
            weight: lateral ? 3 : 4,
            opacity: 0.9,
            dashArray: lateral ? '8 6' : undefined, // dashed = only live when the valve is open
          })
          const sz = pipe.size_label ? ` · ${pipe.size_label}` : ''
          const openPipe = (ev: any) => { ev?.originalEvent?.stopPropagation?.(); setEditInfra({ kind: 'pipe', data: pipe }) }
          line.bindTooltip(`${lateral ? 'Lateral' : 'Mainline'} pipe${escTip(pipe.name ? ': ' + pipe.name : '')}${escTip(sz)} — click to edit`, { permanent: false })
          line.on('click', openPipe)
          // Transparent fat hit-line UNDER the visible one so a thin pipe (3–4px,
          // dashed laterals especially) is easy to click/tap — Leaflet polylines have
          // no click tolerance. Carries the same open handler + tooltip.
          const hit = L.polyline(coords.map((c: number[]) => [c[1], c[0]] as [number, number]), { color: '#000', weight: 16, opacity: 0, interactive: true })
          hit.on('click', openPipe)
          hit.bindTooltip(`${lateral ? 'Lateral' : 'Mainline'} pipe${escTip(pipe.name ? ': ' + pipe.name : '')}${escTip(sz)} — click to edit`, { permanent: false })
          pipeLayersRef.current[pipe.id] = line // for shape editing
          lg.addLayer(hit)  // hit area first (under)
          lg.addLayer(line) // visible pipe on top
        } catch (_) { /* bad geometry — skip */ }
      }
      // Point markers: manifolds (square-ish), valves (diamond via circle), sources (well/city).
      const point = (geom: any): [number, number] | null => {
        const c = geom?.coordinates
        return Array.isArray(c) && c.length >= 2 ? [c[1], c[0]] : null
      }
      for (const m of irrigationInfra.manifolds) {
        const p = point(m.geometry); if (!p) continue
        const mk = L.circleMarker(p, { radius: 6, color: '#0EA5E9', fillColor: '#0EA5E9', fillOpacity: 0.9, weight: 2 })
        mk.bindTooltip(`Manifold${escTip(m.name ? ': ' + m.name : '')}`, { permanent: false })
        mk.on('click', (ev: any) => { ev?.originalEvent?.stopPropagation?.(); setEditInfra({ kind: 'manifold', data: m }) })
        lg.addLayer(mk)
      }
      // One name everywhere (§174): a valve shows its linked zone's name (the "Zone N" hub).
      const zoneName = (zid?: string | null): string => {
        if (!zid) return ''
        const z = irrigationZones.find(z => z.id === zid)
        return z ? (z.name || z.controller_zone_name || '') : ''
      }
      for (const v of irrigationInfra.valves) {
        if (!v.geometry) continue // null geometry ⇒ located at its manifold
        const p = point(v.geometry); if (!p) continue
        const psi = v.output_psi != null ? ` · ${v.output_psi} PSI` : ''
        const label = zoneName(v.zone_id) || v.name
        const mk = L.circleMarker(p, { radius: 5, color: '#22D3EE', fillColor: '#0E1209', fillOpacity: 1, weight: 2 })
        mk.bindTooltip(`Valve${escTip(label ? ': ' + label : '')}${escTip(psi)}`, { permanent: false })
        mk.on('click', (ev: any) => { ev?.originalEvent?.stopPropagation?.(); setEditInfra({ kind: 'valve', data: v }) })
        lg.addLayer(mk)
      }
      for (const s of irrigationInfra.sources) {
        const p = point(s.geometry); if (!p) continue
        const isWell = s.kind === 'well_pump' || s.kind === 'booster_pump'
        const mk = L.circleMarker(p, { radius: 7, color: '#2563EB', fillColor: isWell ? '#2563EB' : '#60A5FA', fillOpacity: 0.95, weight: 2 })
        const kindLabel = s.kind.replace(/_/g, ' ')
        mk.bindTooltip(`${escTip(kindLabel)}${escTip(s.name ? ': ' + s.name : '')}`, { permanent: false })
        mk.on('click', (ev: any) => { ev?.originalEvent?.stopPropagation?.(); setEditInfra({ kind: 'source', data: s }) })
        lg.addLayer(mk)
      }
    })
  }, [irrigationInfra, irrigationZones, mapReady])

  // ── Structures layer (§174): barns/well-houses/packing-houses — color-coded polygons ──
  useEffect(() => {
    if (!leafletMap.current || !mapReady) return
    import('leaflet').then((L) => {
      const map = leafletMap.current
      if (!map) return
      if (!structuresExtraRef.current) {
        structuresExtraRef.current = new (L as any).FeatureGroup()
        if (layers.structures) structuresExtraRef.current.addTo(map)
      }
      const lg = structuresExtraRef.current
      lg.clearLayers()
      structureLayersRef.current = {}
      for (const st of structuresList) {
        if (!st.geometry) continue
        try {
          const layer = L.geoJSON({ type: 'Feature', geometry: st.geometry, properties: {} } as any, {
            style: { color: st.color_hex || LAYER_COLORS.structures, weight: 2, fillOpacity: 0.25 },
            pointToLayer: (_f: any, latlng: any) => L.circleMarker(latlng, { radius: 7, color: st.color_hex || LAYER_COLORS.structures, fillOpacity: 0.7, weight: 2 }),
          })
          const typeLabel = (st.structure_type || 'structure').replace(/_/g, ' ')
          layer.bindTooltip(`${escTip(st.name || typeLabel)}`, { permanent: false })
          layer.on('click', (ev: any) => { ev?.originalEvent?.stopPropagation?.(); setEditInfra({ kind: 'structure', data: st }) })
          // Keep the inner polygon (has leaflet-draw `.editing`) so it can be resized/moved.
          const inner = (layer.getLayers && layer.getLayers()[0]) || layer
          structureLayersRef.current[st.id] = inner
          lg.addLayer(layer)
        } catch (_) { /* bad geometry — skip */ }
      }
      // A just-dropped preset auto-enters resize mode once its polygon is on the map.
      const pid = pendingStructureEditRef.current
      if (pid && structureLayersRef.current[pid]?.editing) {
        pendingStructureEditRef.current = null
        startStructureShapeEdit()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuresList, mapReady, layers.structures])

  // ── Apply layer visibility toggles ────────────────────────────────────

  useEffect(() => {
    const map = leafletMap.current
    if (!map) return
    ;(Object.keys(layers) as LayerKey[]).forEach(key => {
      const lg = layerRefs.current[key]
      if (!lg) return
      if (layers[key]) { if (!map.hasLayer(lg)) map.addLayer(lg) }
      else { if (map.hasLayer(lg)) map.removeLayer(lg) }
    })
    // The structures-table group rides with the "Structures & Beds" key.
    const se = structuresExtraRef.current
    if (se) {
      if (layers.structures) { if (!map.hasLayer(se)) map.addLayer(se) }
      else { if (map.hasLayer(se)) map.removeLayer(se) }
    }
  }, [layers, mapReady])

  // ── Cursor feedback when placing ──────────────────────────────────────

  useEffect(() => {
    if (!mapRef.current) return
    mapRef.current.style.cursor = placingSpecies ? 'crosshair' : ''
  }, [placingSpecies])

  // ── Fly to farm location when it changes ─────────────────────────────
  // On a plot switch (restoringPlotRef set), restore that plot's saved view (center +
  // zoom) if it has one — so flipping between properties keeps each one's saved view
  // instead of resetting to the plot centre @ 17. An address search (flag not set)
  // always flies to the searched point.
  useEffect(() => {
    if (!farmLocation || !leafletMap.current) return
    if (restoringPlotRef.current) {
      restoringPlotRef.current = false
      const sv = loadSavedView(activePlotId)
      if (sv) { leafletMap.current.flyTo([sv.lat, sv.lng], sv.zoom, { animate: true, duration: 1.0 }); return }
    }
    leafletMap.current.flyTo([farmLocation.lat, farmLocation.lng], 17, { animate: true, duration: 1.2 })
    // activePlotId read only inside the plot-restore branch (set together with farmLocation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmLocation])

  // ── Geocode address via Nominatim (debounced autocomplete) ───────────────

  const nominatimTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const searchLocation = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 3) { setLocationResults([]); return }
    setLocationSearching(true)
    try {
      const results: any[] = []
      // 1) US Census exact match first — it nails precise/rural street addresses
      //    that Nominatim mangles (only when the query looks like a street address).
      if (/\d/.test(q)) {
        try {
          const c = await fetch(`https://geocoding.geo.census.gov/geocoder/locations/onelineaddress?address=${encodeURIComponent(q)}&benchmark=Public_AR_Current&format=json`).then(r => r.json())
          const m = c?.result?.addressMatches?.[0]
          if (m?.coordinates?.x != null) {
            results.push({ place_id: 'census', display_name: m.matchedAddress, lat: String(m.coordinates.y), lon: String(m.coordinates.x) })
          }
        } catch { /* ignore — Nominatim still runs */ }
      }
      // 2) Nominatim suggestions — US-biased + ranked toward the current map view
      //    (a "viewbox" bias, not a hard filter), which kills the far-away weirdness.
      const map = leafletMap.current
      let vb = ''
      if (map) {
        const b = map.getBounds()
        vb = `&viewbox=${b.getWest()},${b.getNorth()},${b.getEast()},${b.getSouth()}&bounded=0`
      }
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=6&addressdetails=1&countrycodes=us${vb}&q=${encodeURIComponent(q)}`
      const data = await fetch(url, { headers: { 'Accept-Language': 'en' } }).then(r => r.json())
      for (const d of (Array.isArray(data) ? data : [])) {
        if (!results.some(r => r.display_name === d.display_name)) results.push(d)
      }
      setLocationResults(results.slice(0, 7))
    } catch { /* ignore */ } finally {
      setLocationSearching(false)
    }
  }, [])

  function handleLocationInput(value: string) {
    setLocationSearch(value)
    if (nominatimTimer.current) clearTimeout(nominatimTimer.current)
    if (value.trim().length < 3) { setLocationResults([]); return }
    nominatimTimer.current = setTimeout(() => searchLocation(value), 400)
  }

  // Picking a search result saves it as a farm PLOT (server-persisted, multi-device).
  // The first plot is the primary; additional plots require a paid plan (the search
  // is hidden for free plans that already have a plot, and the server also enforces it).
  async function pickLocation(result: { display_name: string; lat: string; lon: string }) {
    const loc: FarmLocation = { lat: parseFloat(result.lat), lng: parseFloat(result.lon), label: result.display_name }
    setPlotMsg('')
    const isFirst = plots.length === 0
    try {
      const { data: created } = await locationsApi.create({
        name: result.display_name.split(',')[0] || 'Farm',
        address: result.display_name,
        lat: loc.lat, lon: loc.lng,
        is_primary: isFirst,
      })
      await qc.invalidateQueries({ queryKey: ['locations'] })
      setActivePlotId(created.id)
      setFarmLocation(loc)
      saveFarmLocation(loc) // per-device cache for instant centring next load
      setShowLocationPanel(false); setLocationSearch(''); setLocationResults([])
      prefsApi.completeStep('address').then(() => qc.invalidateQueries({ queryKey: ['onboarding'] })).catch(() => {})
      // They just set their location — check coverage and, if covered with no boundary
      // yet, auto-find the parcel from this address. (Pre-set the key so the label
      // effect doesn't also fire a duplicate coverage call for the new plot.)
      coverageKeyRef.current = `${created.id}|${tenantProfile?.state ?? ''}`
      void checkCoverage({ lat: loc.lat, lng: loc.lng, autoFind: true })
    } catch (e: any) {
      if (e?.response?.status === 402) {
        setPlotMsg('Additional plots are available on the Grower & Estate plans.')
      } else {
        // Fall back to a local-only pin so the map still works if the save failed.
        saveFarmLocation(loc); setFarmLocation(loc)
        setShowLocationPanel(false); setLocationSearch(''); setLocationResults([])
      }
    }
  }

  // ── Derived values ────────────────────────────────────────────────────

  const features = featuresData?.data?.features ?? []
  const undesignatedZones = irrigationZones.filter(z => z.designation_status === 'undesignated')

  // Variety → marker-colour legend for the trees/plants currently shown (matches the
  // marker colour resolution above: catalog swatch by species_id, else varietyColor).
  const varietyLegend = useMemo(() => {
    const m = new Map<string, string>()
    for (const f of features) {
      const ft = f.feature_type
      if (ft === 'zone' || ft === 'irrigation_zone' || ft === 'boundary' || ft === 'structure' || ft === 'bed') continue
      if (!inActivePlot(f.location_id)) continue
      const name = (f.properties as Record<string, unknown> | undefined)?.species_name as string | undefined
      if (!name || m.has(name)) continue
      const sid = (f.properties as Record<string, unknown> | undefined)?.species_id
      const catalog = PLANT_CATALOG.find(p => p.id === sid)?.color
      m.set(name, catalog ?? varietyColor(name))
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    // (effect intentionally runs with a fixed dependency set)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresData, activePlotId])

  // Global growable catalog (so seeded types — bananas, strawberries, orchids,
  // Garcinia/achacha — appear in the map's species picker, not just the hardcoded list).
  const { data: refData } = useQuery({
    queryKey: ['growable-ref'],
    queryFn: () => growableRefApi.list().then(r => r.data.reference),
    staleTime: 60 * 60 * 1000, // global catalog rarely changes
  })
  const catalog = useMemo(() => mergeCatalog(refData), [refData])
  const catalogCategories = useMemo(
    () => ['All', ...Array.from(new Set(catalog.map(p => p.category)))],
    [catalog],
  )

  const filteredCatalog = useMemo(() =>
    catalog.filter(p => {
      const q = catalogSearch.toLowerCase()
      const matchText = p.name.toLowerCase().includes(q) || p.scientificName.toLowerCase().includes(q)
      const matchCat = catalogCategory === 'All' || p.category === catalogCategory
      return matchText && matchCat
    }),
    [catalog, catalogSearch, catalogCategory]
  )

  const featureLayerKey = (ft: string): LayerKey =>
    ft === 'irrigation_zone' ? 'irrigationZones' :
    ft === 'zone' ? 'plantZones' :
    ft === 'structure' || ft === 'bed' ? 'structures' : 'plants'

  // ── AI tree detection ──────────────────────────────────────────────────
  // Outer ring [lng,lat][] of the saved boundary for the active plot, if any — so the
  // top "Detect" button scopes to the user's OWN land (detection is clipped to the
  // polygon backend-side), instead of whatever happens to be in the viewport.
  function activeBoundaryRing(): number[][] | undefined {
    const b = (featuresData?.data?.features ?? []).find(
      f => f.feature_type === 'boundary' && inActivePlot(f.location_id))
    const g = b?.geometry as any
    if (g?.type === 'Polygon') return g.coordinates?.[0]
    if (g?.type === 'MultiPolygon') return g.coordinates?.[0]?.[0]
    return undefined
  }
  async function runDetect(polygon?: number[][]) {
    const map = leafletMap.current
    if (!map || detecting) return
    setDetecting(true); setDetectWarning(null); setDetectEngine(null); setDetectModelUsed(null)
    try {
      const b = map.getBounds()
      // engine/model are honored server-side only when the matching superadmin picker
      // flag is on; sending them otherwise is harmlessly ignored.
      const pick = { engine: detectEngineChoice || undefined, model: detectModelChoice || undefined }
      const { data } = await treeDetectApi.detect(
        polygon && polygon.length >= 3
          ? { polygon, ...pick }
          : { min_lng: b.getWest(), min_lat: b.getSouth(), max_lng: b.getEast(), max_lat: b.getNorth(), ...pick },
      )
      processDetections(data, map)
    } catch (e: any) {
      setDetectWarning(e?.response?.data?.error ?? e?.message ?? 'Detection failed')
    } finally { setDetecting(false) }
  }
  // Shared post-processing for satellite + drone-ortho detection: drop detections that land on a
  // tree ALREADY on the map (the model is deterministic on the same imagery, so re-running would
  // otherwise stack a second pin — the within-run dedupe §157 doesn't see placed pins; ~2.5 m is
  // below typical tree spacing) and on a mapped building (the VLM sometimes paints "trees" across
  // roofs — mark barns/houses as Structures and they're excluded), then show the preview.
  function processDetections(data: { trees?: DetectedTree[]; engine?: string; model?: string; warning?: string }, map: any) {
    const existing = (featuresData?.data?.features ?? [])
      .filter(f => (f.geometry as any)?.type === 'Point'
        && (((f.properties as Record<string, unknown> | undefined)?.plant_id) || ['tree', 'palm', 'shrub'].includes(f.feature_type))
        && inActivePlot(f.location_id))
      .map(f => (f.geometry as any).coordinates as [number, number]) // [lng,lat]
    const notExisting = (data.trees ?? []).filter(t =>
      !existing.some(c => map.distance([t.lat, t.lng], [c[1], c[0]]) < 2.5))
    const dupSkipped = (data.trees?.length ?? 0) - notExisting.length
    const structRings = (structuresList ?? [])
      .map(s => outerRing(s.geometry))
      .filter((r): r is number[][] => !!r && r.length >= 3)
    const fresh = structRings.length
      ? notExisting.filter(t => !structRings.some(r => pointInRing(t.lng, t.lat, r)))
      : notExisting
    const structSkipped = notExisting.length - fresh.length
    setDetected(fresh) // the effect below renders dots filtered by pickiness
    setDetectEngine(data.engine ?? null)
    setDetectModelUsed(data.model ?? null)
    const notes = [
      dupSkipped > 0 ? `${dupSkipped} already on the map` : '',
      structSkipped > 0 ? `${structSkipped} over a building` : '',
    ].filter(Boolean).join(', ')
    if (data.warning) setDetectWarning(data.warning)
    else if (fresh.length === 0 && notes) setDetectWarning(`No new trees — skipped ${notes}.`)
    else if (notes) flashInfra(`Skipped ${notes}`)
  }
  // Detect trees on a placed drone ortho (§229). Prefers the ortho whose footprint contains the
  // map centre (else the first active placed one), fits to it, and reuses the same preview +
  // pickiness + Place-N flow as satellite detection.
  async function detectOnDrone() {
    const map = leafletMap.current
    if (!map || detecting) return
    const maps = (droneMapsQuery.data ?? []).filter(m => m.is_active && m.south != null && m.north != null && m.west != null && m.east != null)
    if (!maps.length) { setDetectWarning('No placed drone imagery yet — upload + place a drone map first.'); return }
    const c = map.getCenter()
    const dm = maps.find(m => c.lat >= (m.south as number) && c.lat <= (m.north as number) && c.lng >= (m.west as number) && c.lng <= (m.east as number)) ?? maps[0]
    setDetecting(true); setDetectWarning(null); setDetectEngine(null); setDetectModelUsed(null)
    try {
      const pick = { engine: detectEngineChoice || undefined, model: detectModelChoice || undefined }
      const { data } = await treeDetectApi.detectDrone({ drone_map_id: dm.id, ...pick })
      processDetections(data, map)
      map.fitBounds([[dm.south as number, dm.west as number], [dm.north as number, dm.east as number]], { maxZoom: 20 })
    } catch (e: any) {
      setDetectWarning(e?.response?.data?.error ?? e?.message ?? 'Detection failed')
    } finally { setDetecting(false) }
  }
  function clearDetected() {
    const map = leafletMap.current
    if (map && detectLayerRef.current) { map.removeLayer(detectLayerRef.current); detectLayerRef.current = null }
    setDetected(null); setDetectWarning(null); setDetectEngine(null); setDetectModelUsed(null)
  }
  async function placeDetected() {
    const trees = (detected ?? []).filter(t => (t.confidence ?? 1) >= pickiness) // only the visible (pickier) set
    if (trees.length === 0) return
    setPlacing(true)
    const sp = catalog.find(p => p.id === detectSpecies)
    // Detected trees become real Plants too (same as manual placement), with
    // block_id assigned by point-in-polygon. Sent as ONE batch request — placing
    // ~200 trees as individual calls tripped the per-IP rate limiter (429s).
    const entry: PlantEntry = sp ?? { id: 'detected', name: 'Detected tree', scientificName: '', category: 'Tree', color: '#F5A623', featureType: 'tree' }
    try {
      await farmMapApi.placeTreesBatch({
        location_id: activePlotId ?? undefined,
        trees: trees.map(t => ({
          lat: t.lat, lng: t.lng, label: entry.name,
          species_id: entry.id, species_name: entry.name, scientific_name: entry.scientificName,
          block_id: blockIdAt(t.lat, t.lng),
          irrigation_zone_id: irrigationZoneIdAt(t.lat, t.lng), // auto-snap to the overlapping irrigation zone
          zone_id: plantZoneAt(t.lat, t.lng), // auto-snap to the overlapping plant zone / sub-zone
        })),
      })
      qc.invalidateQueries({ queryKey: ['map-features'] })
      qc.invalidateQueries({ queryKey: ['plants'] })
      qc.invalidateQueries({ queryKey: ['blocks'] })
      clearDetected()
    } catch (e) {
      // Plant-limit 402 → upsell modal (§242); otherwise the generic retry alert.
      if (!handlePlantLimit(e)) alert('Could not place the detected trees — please try again.')
    } finally { setPlacing(false) }
  }

  // Render the detection preview dots, filtered live by the pickiness slider.
  // Higher confidence → more opaque dot. The dots are DRAGGABLE markers so you can
  // nudge a detection onto the real canopy before saving (placement); a tooltip says
  // so. Re-runs on detect or slider change (and after a drag, which rebuilds at the
  // new spot). Until "Place" is clicked these are previews, not real pins.
  useEffect(() => {
    const map = leafletMap.current
    if (!map) return
    if (detectLayerRef.current) { map.removeLayer(detectLayerRef.current); detectLayerRef.current = null }
    const all = detected ?? []
    if (all.length === 0) return
    let cancelled = false
    import('leaflet').then(m => {
      if (cancelled || !leafletMap.current) return
      const L = m.default as any
      const grp = L.layerGroup()
      all.forEach((t, idx) => {
        if ((t.confidence ?? 1) < pickiness) return // hidden by the pickiness slider
        const c = Math.min(1, Math.max(0, t.confidence ?? 1))
        const icon = L.divIcon({
          className: '',
          html: `<div style="width:13px;height:13px;border-radius:50%;background:#F5A623;border:2px solid #fff;opacity:${(0.4 + 0.55 * c).toFixed(2)};box-shadow:0 0 0 1px rgba(0,0,0,0.35);cursor:move"></div>`,
          iconSize: [13, 13], iconAnchor: [6.5, 6.5],
        })
        const mk = L.marker([t.lat, t.lng], { icon, draggable: true, keyboard: false })
        mk.bindTooltip('Drag to nudge · click “Place” to save', { direction: 'top' })
        mk.on('dragend', () => {
          const ll = mk.getLatLng()
          setDetected(prev => prev ? prev.map((d, j) => (j === idx ? { ...d, lat: ll.lat, lng: ll.lng } : d)) : prev)
        })
        grp.addLayer(mk)
      })
      grp.addTo(map); detectLayerRef.current = grp
    })
    return () => { cancelled = true }
  }, [detected, pickiness, mapReady])

  // Track narrow screens (phone) so the tool panel overlays instead of squishing the map.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-width: 820px)')
    const on = () => setIsNarrow(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  // When the panel hides/shows (or overlays), Leaflet must recompute its size so the
  // map fills the freed space and tiles aren't grey.
  useEffect(() => {
    if (!mapReady) return
    const t = setTimeout(() => { try { leafletMap.current?.invalidateSize() } catch { /* ignore */ } }, 60)
    return () => clearTimeout(t)
  }, [panelOpen, mapReady])

  // ── Render ────────────────────────────────────────────────────────────

  const visibleDetected = (detected ?? []).filter(t => (t.confidence ?? 1) >= pickiness)

  return (
    <main id="main-content" className="flex h-full relative" style={{ background: 'var(--color-bg)' }}>
      {/* Map area */}
      <div className="flex-1 relative min-w-0">
        <div ref={mapRef} className="w-full h-full" />

        {/* Show the tool panel when it's hidden (full-map mode / mobile) — a drawer
            pull on the right edge, clear of the top-right Layers panel + bottom panels. */}
        {!panelOpen && (
          <button
            onClick={togglePanel}
            className="absolute z-[1400] text-xs font-semibold"
            style={{ top: '50%', right: 0, transform: 'translateY(-50%)', padding: '14px 6px', borderRadius: '8px 0 0 8px', writingMode: 'vertical-rl', background: 'var(--color-primary)', color: '#04220e', border: 'none', boxShadow: '-3px 0 12px rgba(0,0,0,0.3)', cursor: 'pointer' }}
            aria-label="Show tools panel"
          >
            ☰ Tools
          </button>
        )}

        {/* Transient confirmation toast (plant auto-assignment, etc.) */}
        {absorbMsg && (
          <div className="absolute z-[1200]" style={{ top: 14, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-bg-card)', border: '1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)', color: 'var(--color-text)', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
            ✓ {absorbMsg}
          </div>
        )}
        {/* Irrigation/structure draw confirmation toast */}
        {infraMsg && (
          <div className="absolute z-[1200]" style={{ bottom: 14, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-bg-card)', border: '1px solid rgba(14,165,233,0.5)', color: 'var(--color-text)', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
            ✓ {infraMsg}
          </div>
        )}

        {/* AI tree detection (top-right) */}
        <div className="absolute top-4 right-4 z-[1000] rounded-xl p-3" style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', width: 230 }}>
          <button onClick={() => runDetect(activeBoundaryRing())} disabled={detecting}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: detecting ? 'wait' : 'pointer', background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)', color: 'var(--color-primary)' }}>
            <span aria-hidden>✨</span> {detecting ? 'Scanning…' : (activeBoundaryRing() ? 'Detect trees in my boundary' : 'Detect trees in view')}
          </button>
          <p style={{ margin: '6px 0 0', fontSize: 10, color: 'var(--color-text-faint)', lineHeight: 1.4 }}>
            {activeBoundaryRing()
              ? 'Automatically scans inside your saved property boundary and marks tree positions.'
              : 'Automatically scans the satellite view and marks tree positions. Pan/zoom to your block first.'}
          </p>
          {/* Detect on a placed drone ortho — higher-res than satellite (§229). Shown when the
              Drone imagery overlay is on and there's an active placed ortho. */}
          {overlays.drone && (droneMapsQuery.data ?? []).some(m => m.is_active && m.south != null) && (
            <>
              <button onClick={detectOnDrone} disabled={detecting}
                style={{ width: '100%', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 0', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: detecting ? 'wait' : 'pointer', background: 'color-mix(in srgb, var(--color-primary) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 35%, transparent)', color: 'var(--color-primary)' }}>
                <span aria-hidden>✈️</span> {detecting ? 'Scanning…' : 'Detect on drone imagery'}
              </button>
              <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--color-text-faint)', lineHeight: 1.4 }}>
                Scans your uploaded drone orthomosaic — sharper than satellite for a block-scale flight.
              </p>
            </>
          )}
          {/* In-UX testing pickers (superadmin-gated, §179) */}
          {detectOptions?.engine_picker && detectOptions.engines.length > 1 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 9.5, color: 'var(--color-text-faint)', marginBottom: 2 }}>Detector</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {detectOptions.engines.map(eng => {
                  const active = (detectEngineChoice || detectOptions.default_engine) === eng
                  return (
                    <button key={eng} onClick={() => setDetectEngineChoice(eng)}
                      style={{ flex: 1, padding: '5px 0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        textTransform: 'capitalize',
                        background: active ? 'color-mix(in srgb, var(--color-primary) 20%, transparent)' : 'var(--color-bg-card-2)',
                        border: active ? '1px solid color-mix(in srgb, var(--color-primary) 55%, transparent)' : '1px solid var(--color-border)',
                        color: active ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                      {eng === 'deepforest' ? 'DeepForest' : 'Gemini'}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {detectOptions?.model_picker && (detectEngineChoice || detectOptions.default_engine) === 'gemini' && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 9.5, color: 'var(--color-text-faint)', marginBottom: 2 }}>Gemini model</div>
              <select value={detectModelChoice} onChange={e => setDetectModelChoice(e.target.value)} className="input text-xs" style={{ padding: '5px 8px', width: '100%' }}>
                <option value="">Default ({detectOptions.default_model})</option>
                {detectOptions.models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
          {detectWarning && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-primary)' }}>{detectWarning}</div>}
          {detected && detected.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12, color: 'var(--color-text)', fontWeight: 600 }}>
                {visibleDetected.length} of {detected.length} tree{detected.length > 1 ? 's' : ''} shown
                {detectEngine === 'deepforest' && (
                  <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 600, color: 'var(--color-accent)' }}>· DeepForest</span>
                )}
                {detectEngine === 'gemini' && detectModelUsed && (
                  <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 600, color: 'var(--color-accent)' }}>· {detectModelUsed}</span>
                )}
              </div>
              {/* Pickiness — filter the detections live by AI confidence. Drag right to
                  show fewer, more-certain trees (cuts false positives); left for more. */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: 'var(--color-text-faint)', marginBottom: 1 }}>
                  <span>more</span><span style={{ fontWeight: 600 }}>pickiness</span><span>pickier</span>
                </div>
                <input type="range" min={0} max={0.9} step={0.05} value={pickiness}
                  onChange={e => setPickiness(parseFloat(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--color-primary)' }} />
                <div style={{ fontSize: 9.5, color: 'var(--color-text-faint)', textAlign: 'center' }}>
                  showing trees ≥ {Math.round(pickiness * 100)}% confidence
                </div>
              </div>
              <select value={detectSpecies} onChange={e => setDetectSpecies(e.target.value)} className="input text-xs" style={{ padding: '5px 8px' }}>
                <option value="">Place as “Tree” (set species later)</option>
                {catalog.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={placeDetected} disabled={placing || visibleDetected.length === 0}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: placing ? 'wait' : 'pointer', background: '#2D6A2F', border: 'none', color: '#fff', opacity: visibleDetected.length === 0 ? 0.5 : 1 }}>
                  {placing ? 'Placing…' : `Place ${visibleDetected.length}`}
                </button>
                <button onClick={clearDetected} style={{ padding: '7px 12px', borderRadius: 7, fontSize: 12, background: 'var(--color-bg-card-2)', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>Clear</button>
              </div>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--color-text-faint)' }}>Drag any amber dot to nudge it onto the tree before placing. “Place” saves them as plants (auto-added to the block / zones they sit in; movable &amp; editable after).</p>
            </div>
          )}
        </div>

        {/* Variety colour legend (bottom-left) — what the tree marker colours mean.
            Hidden during select mode so the bulk-action panel can use the corner. */}
        {layers.plants && varietyLegend.length > 0 && !selectMode && (
          <div
            className="absolute bottom-4 left-4 z-[1000] rounded-xl p-2.5"
            style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', maxWidth: 200, maxHeight: 240, overflowY: 'auto' }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Varieties</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {varietyLegend.map(([name, color]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Layer toggles */}
        <div
          className="absolute top-4 left-4 z-[1000] rounded-xl p-3 space-y-1.5"
          style={{
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            minWidth: 170,
          }}
          aria-label="Map layer controls"
        >
          <p className="text-xs font-semibold uppercase tracking-wider pb-1"
            style={{ color: 'var(--color-text-muted)' }}>
            Layers
          </p>
          {(Object.keys(LAYER_LABELS) as LayerKey[]).map(key => (
            <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={layers[key]}
                onChange={() => setLayers(prev => ({ ...prev, [key]: !prev[key] }))}
                aria-label={`Toggle ${LAYER_LABELS[key]}`}
                className="rounded"
                style={{ accentColor: LAYER_COLORS[key] }}
              />
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: LAYER_COLORS[key] }} />
              <span className="text-xs" style={{ color: 'var(--color-text)' }}>{LAYER_LABELS[key]}</span>
            </label>
          ))}
          {/* Hide the always-on block name·acreage labels (they can overlay tree
              pins) without hiding the block polygons. Persisted; names still show
              on hover and the polygon stays selectable. */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showBlockLabels}
              onChange={() => setShowBlockLabels(v => { const n = !v; try { localStorage.setItem(BLOCK_LABELS_KEY, n ? '1' : '0') } catch { /* ignore */ } return n })}
              aria-label="Toggle block labels"
              className="rounded"
              style={{ accentColor: LAYER_COLORS.plantZones }}
            />
            <span className="w-2.5 h-2.5 shrink-0" style={{ border: `1.5px solid ${LAYER_COLORS.plantZones}`, fontSize: 0 }} />
            <span className="text-xs" style={{ color: 'var(--color-text)' }}>Block labels</span>
          </label>
          {/* Public reference overlays — free gov data: FEMA flood + NRCS soil. */}
          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 8, marginTop: 6 }}>
            <p className="text-xs font-semibold uppercase tracking-wider pb-1" style={{ color: 'var(--color-text-muted)' }}>
              Data overlays
            </p>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={overlays.flood} onChange={() => setOverlays(o => ({ ...o, flood: !o.flood }))} className="rounded" style={{ accentColor: '#38bdf8' }} aria-label="Toggle FEMA flood zones" />
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: '#38bdf8' }} />
              <span className="text-xs" style={{ color: 'var(--color-text)' }}>🌊 Flood zones (FEMA)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none mt-1">
              <input type="checkbox" checked={overlays.soil} onChange={() => setOverlays(o => ({ ...o, soil: !o.soil }))} className="rounded" style={{ accentColor: '#a16207' }} aria-label="Toggle NRCS soil inspect" />
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: '#a16207' }} />
              <span className="text-xs" style={{ color: 'var(--color-text)' }}>🧪 Soil survey (NRCS)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none mt-1">
              <input type="checkbox" checked={overlays.drone} onChange={() => setOverlays(o => ({ ...o, drone: !o.drone }))} className="rounded" style={{ accentColor: '#F5A623' }} aria-label="Toggle drone imagery" />
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: '#F5A623' }} />
              <span className="text-xs" style={{ color: 'var(--color-text)' }}>✈️ Drone imagery</span>
            </label>
            {overlays.soil && (
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-faint)', lineHeight: 1.4 }}>
                Tap anywhere on the map to see the soil type &amp; prime-farmland rating.
              </p>
            )}
          </div>
          {/* Tag filter — click tags to show only matching blocks/zones/plants
              (with plot→block→plant inheritance). Match-ANY across selected tags. */}
          {allTags.length > 0 && (
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 8, marginTop: 4 }}>
              <p className="text-xs font-semibold uppercase tracking-wider pb-1"
                style={{ color: 'var(--color-text-muted)' }}>
                Filter by tag
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 190 }}>
                {allTags.map(t => {
                  const active = tagFilter.has(t.id)
                  return (
                    <button key={t.id}
                      onClick={() => setTagFilter(prev => { const n = new Set(prev); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n })}
                      title={active ? 'Click to remove from filter' : `Show only “${t.name}” items`}
                      style={{
                        fontSize: 10.5, fontWeight: 600, cursor: 'pointer', borderRadius: 100, padding: '2px 8px',
                        color: active ? '#fff' : t.color,
                        background: active ? t.color : `color-mix(in srgb, ${t.color} 14%, transparent)`,
                        border: `1px solid ${active ? t.color : `color-mix(in srgb, ${t.color} 45%, transparent)`}`,
                      }}>
                      {t.name}
                    </button>
                  )
                })}
              </div>
              {tagFilter.size > 0 && (
                <p className="text-xs mt-1.5" style={{ color: 'var(--color-text-faint)' }}>
                  Showing tagged blocks, zones & plants. Structures stay visible.
                </p>
              )}
            </div>
          )}
          {/* Reset = show all layers + block labels + clear the tag filter. */}
          {(Object.values(layers).some(v => !v) || tagFilter.size > 0 || !showBlockLabels) && (
            <button
              onClick={() => { setLayers({ ...ALL_LAYERS_ON }); setTagFilter(new Set()); setShowBlockLabels(true); try { localStorage.setItem(BLOCK_LABELS_KEY, '1') } catch { /* ignore */ } }}
              className="text-xs w-full text-left"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', paddingTop: 2 }}
            >
              ↺ Reset (show all)
            </button>
          )}
          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 8, marginTop: 4 }}>
            <button
              onClick={() => setShowLocationPanel(true)}
              className="text-xs w-full text-left flex items-center gap-1.5"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}
              title={farmLocation ? farmLocation.label : 'Set farm location'}
            >
              📍 {farmLocation ? 'Change location' : 'Set farm location'}
            </button>
            {farmLocation && (
              <p className="text-xs mt-1 truncate" style={{ color: 'var(--color-text-faint)', maxWidth: 150 }}>
                {farmLocation.label.split(',').slice(0, 2).join(',')}
              </p>
            )}
            <button
              onClick={saveCurrentView}
              className="text-xs w-full text-left flex items-center gap-1.5 mt-1.5"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: viewSaved ? '#2D6A2F' : 'var(--color-text-muted)' }}
              title="Open the map at this exact zoom + position next time"
            >
              {viewSaved ? '✓ View saved' : '🎯 Save this view'}
            </button>
            {parcelCoverage && !parcelCoverage.covered ? (
              <button
                onClick={() => startDraw('boundary')}
                className="text-xs w-full text-left flex items-center gap-1.5 mt-1.5"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: drawingType === 'boundary' ? '#38bdf8' : 'var(--color-text-muted)' }}
                title="No public parcel records cover this location — trace your property boundary on the map"
              >
                ✏️ {drawingType === 'boundary' ? 'Drawing boundary — tap the map…' : 'Draw my parcel boundary'}
              </button>
            ) : (
              <button
                onClick={findMyParcel}
                disabled={findingParcel}
                className="text-xs w-full text-left flex items-center gap-1.5 mt-1.5"
                style={{ background: 'none', border: 'none', cursor: findingParcel ? 'wait' : 'pointer', color: 'var(--color-text-muted)' }}
                title="Auto-detect your legal property boundary from free public parcel records (where available)"
              >
                🏡 {findingParcel ? 'Finding parcel…' : 'Find my parcel boundary'}
              </button>
            )}
            {parcelCoverage && !parcelCoverage.covered && !parcelMsg && (
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-faint)', lineHeight: 1.4 }}>
                No public parcel records for {parcelCoverage.county ? `${parcelCoverage.county} County` : 'this area'} yet — draw your boundary instead.
              </p>
            )}
            {parcelMsg && (
              <p className="text-xs mt-1" style={{ color: parcelMsg.startsWith('✓') ? '#2D6A2F' : 'var(--color-text-faint)', lineHeight: 1.4 }}>
                {parcelMsg}
              </p>
            )}
            <button
              onClick={toggleLotLines}
              className="text-xs w-full text-left flex items-center gap-1.5 mt-1.5"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: showLots ? '#38bdf8' : 'var(--color-text-muted)' }}
              title="Overlay neighboring property (parcel) boundaries from public records. Click a lot for owner/ID, or set it as your boundary."
            >
              🗺️ {showLots ? 'Hide lot lines' : 'Show lot lines (neighbors)'}
            </button>
            {lotMsg && (
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-faint)', lineHeight: 1.4 }}>{lotMsg}</p>
            )}
          </div>
        </div>

        {/* Farm location search panel */}
        {showLocationPanel && (
          <div
            className="absolute top-4 left-48 z-[1001] rounded-xl p-4"
            style={{
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              width: 320,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
                {plots.length > 0 ? 'Farm Plots' : 'Set Farm Location'}
              </p>
              <button
                onClick={() => { setShowLocationPanel(false); setLocationResults([]); setLocationSearch('') }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 2 }}
              >✕</button>
            </div>

            {/* Existing plots — click to switch the active plot (recenters the map). */}
            {plots.length > 0 && (
              <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {plots.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                      onClick={() => selectPlot(p)}
                      className="flex-1 text-left text-xs rounded-lg"
                      style={{
                        padding: '6px 8px',
                        background: p.id === activePlotId ? 'color-mix(in srgb, var(--color-primary) 14%, transparent)' : 'var(--color-bg)',
                        border: `1px solid ${p.id === activePlotId ? '#F5A623' : 'var(--color-border)'}`,
                        color: 'var(--color-text)', cursor: 'pointer',
                      }}
                    >
                      {p.id === activePlotId ? '📍 ' : '○ '}{p.name}{p.is_primary ? ' · primary' : ''}
                    </button>
                    {!p.is_primary && (
                      <button onClick={() => deletePlot(p.id)} title="Remove plot"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '2px 5px' }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {plotMsg && <p className="text-xs mb-2" style={{ color: '#F5A623', lineHeight: 1.4 }}>{plotMsg}</p>}

            {/* Add-a-plot search is gated: free plans set their single plot; more need a paid plan. */}
            {!canAddPlot && (
              <p className="text-xs mb-2" style={{ color: 'var(--color-text-faint)', lineHeight: 1.4 }}>
                Additional plots are available on the <strong>Grower</strong> &amp; <strong>Estate</strong> plans.
              </p>
            )}
            {plots.length > 0 && canAddPlot && (
              <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>Add another plot:</p>
            )}

            {/* Autocomplete input — results appear inline as you type. Disabled when
                the plan can't add another plot (free plan that already has one). */}
            <div className="relative mb-1" style={{ display: canAddPlot ? undefined : 'none' }}>
              <input
                className="w-full text-xs px-3 py-2 rounded-lg"
                style={{
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-text)',
                  outline: 'none',
                  paddingRight: locationSearching ? 32 : undefined,
                }}
                placeholder="Start typing your address or farm name…"
                value={locationSearch}
                onChange={e => handleLocationInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') { setLocationResults([]); setLocationSearch('') } }}
                disabled={!canAddPlot}
                autoFocus
                autoComplete="off"
              />
              {locationSearching && (
                <span style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 11, color: 'var(--color-text-muted)',
                }}>…</span>
              )}
            </div>
            {/* Dropdown results */}
            {locationResults.length > 0 && (
              <div style={{
                border: '1px solid var(--color-border)',
                borderRadius: 8, overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                marginBottom: 8,
              }}>
                {locationResults.map((r, i) => {
                  const parts = r.display_name.split(', ')
                  const primary = parts[0]
                  const secondary = parts.slice(1).join(', ')
                  return (
                    <button
                      key={i}
                      onClick={() => pickLocation(r)}
                      className="w-full text-left"
                      style={{
                        display: 'block', width: '100%', padding: '8px 12px',
                        background: i % 2 === 0 ? 'var(--color-bg)' : 'var(--color-bg-card)',
                        borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
                        color: 'var(--color-text)',
                        cursor: 'pointer', border: 'none', textAlign: 'left',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'color-mix(in srgb, var(--color-primary) 12%, transparent)')}
                      onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? 'var(--color-bg)' : 'var(--color-bg-card)')}
                    >
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{primary}</div>
                      {secondary && <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 1 }}>{secondary}</div>}
                    </button>
                  )
                })}
              </div>
            )}
            {locationResults.length === 0 && !locationSearching && locationSearch.length >= 3 && (
              <p className="text-xs mb-2" style={{ color: 'var(--color-text-faint)' }}>No results. Try a more specific address.</p>
            )}
            <p className="text-xs" style={{ color: 'var(--color-text-faint)' }}>
              Type at least 3 characters — results appear automatically.
            </p>
          </div>
        )}

        {/* Draw mode controls */}
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] flex flex-wrap justify-center gap-2 max-w-[94vw]"
        >
          {(['block', 'sub_zone', 'structure'] as DrawType[]).map(type => {
            const labelMap: Record<DrawType, string> = { block: '+ Block', sub_zone: '+ Sub-zone', irrigation_zone: '+ Irrigation', boundary: 'Boundary', structure: '+ Structure' }
            const colorMap: Record<DrawType, string> = { block: '#F5A623', sub_zone: '#A855F7', irrigation_zone: LAYER_COLORS.irrigationZones, boundary: '#38bdf8', structure: '#B45309' }
            const disabled = type === 'sub_zone' && !selectedBlock
            // "+ Structure" opens the preset palette (drop-a-box → resize); the rest start a polygon draw.
            const isStructure = type === 'structure'
            const active = isStructure ? (showStructurePalette || drawingType === 'structure') : drawingType === type
            return (
              <button
                key={type}
                disabled={disabled}
                onClick={() => { if (isStructure) { setShowStructurePalette(v => !v); setActiveTab('irrigation'); setPanelOpen(true) } else { startDraw(type) } }}
                title={disabled ? 'Select a block first, then draw a sub-zone inside it' : isStructure ? 'Add a barn, stall, well house, trough… (pick one, then resize)' : `Draw a ${labelMap[type].replace('+ ', '').toLowerCase()} polygon`}
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
                style={{
                  background: active ? colorMap[type] : 'var(--color-bg-card)',
                  color: active ? '#fff' : 'var(--color-text)',
                  border: `1px solid ${colorMap[type]}60`,
                  opacity: disabled ? 0.45 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                {labelMap[type]}
              </button>
            )
          })}
          {drawingType && (
            <>
              <button
                onClick={finishDraw}
                title="Close the shape from the points you've placed (no need to tap the first dot)"
                className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-all"
                style={{ background: '#2D6A2F', color: '#fff', border: '1px solid #2D6A2F', cursor: 'pointer' }}
              >
                ✓ Finish
              </button>
              <button
                onClick={undoLastVertex}
                title="Remove the last point placed"
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
                style={{ background: 'var(--color-bg-card)', color: 'var(--color-text)', border: '1px solid var(--color-border)', cursor: 'pointer' }}
              >
                ↶ Undo
              </button>
              <button
                onClick={cancelDraw}
                title="Cancel drawing"
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
                style={{ background: 'var(--color-bg-card)', color: 'var(--color-text)', border: '1px solid var(--color-border)', cursor: 'pointer' }}
              >
                ✕ Cancel
              </button>
            </>
          )}
          <button
            onClick={() => { setSelectMode(v => !v); setSelectedIds(new Set()) }}
            title="Drag a box to select trees (or click pins), then bulk-assign or delete them"
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
            style={{
              background: selectMode ? '#2D6A2F' : 'var(--color-bg-card)',
              color: selectMode ? '#fff' : 'var(--color-text)',
              border: '1px solid rgba(45,106,47,0.5)', cursor: 'pointer',
            }}
          >
            {selectMode ? '✓ Selecting' : '☑ Select'}
          </button>
          <button
            onClick={() => { const willOpen = !showInfraTools; setShowStructurePalette(false); setShowInfraTools(willOpen); if (willOpen) { setActiveTab('irrigation'); setPanelOpen(true) } else { cancelPipeDraw(); cancelPlace() } }}
            title="Draw irrigation zones, pipes, valves & water sources (opens the Irrigation tab)"
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
            style={{
              background: showInfraTools ? '#0EA5E9' : 'var(--color-bg-card)',
              color: showInfraTools ? '#fff' : 'var(--color-text)',
              border: '1px solid rgba(14,165,233,0.5)', cursor: 'pointer',
            }}
          >
            🚿 Irrigation system
          </button>
        </div>

        {/* The structure palette + irrigation-tools panels were relocated OFF the map
            into the right sidebar's Irrigation tab (no longer floating overlays) — see
            the `activeTab === 'irrigation'` block. The toolbar buttons just open that tab. */}

        {/* On-map cue while a pipe is being drawn — makes it obvious how to END the run. */}
        {pipeDraw && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-[1002] rounded-full px-4 py-2"
            style={{ background: 'rgba(14,23,9,0.9)', border: '1px solid #2DD4BF', color: '#fff', fontSize: 12, boxShadow: '0 6px 20px rgba(0,0,0,0.45)', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            <b>{pipeDraw.count}</b> point{pipeDraw.count === 1 ? '' : 's'} · finish: <b>Enter</b> / right-click / double-click · <b>Esc</b> cancel · <b>⌫</b> undo
          </div>
        )}

        {/* Multi-select bulk-assign panel */}
        {/* Select mode — a slim banner (always, so the mode is obvious) at top-center, and
            the bulk-action panel docked bottom-left, shown ONLY once trees are selected so it
            never blocks the map while you're drawing the box. */}
        {selectMode && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1001] rounded-full"
            style={{ background: '#2D6A2F', color: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', maxWidth: '92vw' }}>
            <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>◉ Select mode</span>
            <span style={{ fontSize: 11, opacity: 0.9 }}>
              {selectedIds.size > 0 ? `${selectedIds.size} selected — ` : ''}drag a box over your trees (or tap pins)
            </span>
            <button onClick={() => { setSelectMode(false); setSelectedIds(new Set()) }}
              style={{ fontSize: 11, fontWeight: 600, background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', borderRadius: 999, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Done
            </button>
          </div>
        )}
        {selectMode && selectedIds.size > 0 && (
          <div className="absolute bottom-4 left-4 z-[1001] rounded-xl p-3"
            style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', width: 250, maxHeight: '72vh', overflowY: 'auto', boxShadow: '0 8px 28px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text)' }}>
                {selectedIds.size} tree{selectedIds.size === 1 ? '' : 's'} selected
              </span>
              <button onClick={() => setSelectedIds(new Set())}
                style={{ fontSize: 10, background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', textDecoration: 'underline' }}>
                Clear
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <select className="input text-xs" value="" style={{ padding: '6px 8px' }}
                onChange={e => { if (e.target.value) assignSelected({ block_id: e.target.value }) }}>
                <option value="">→ Add to block…</option>
                {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <select className="input text-xs" value="" style={{ padding: '6px 8px' }}
                onChange={e => { if (e.target.value) assignSelected({ irrigation_zone_id: e.target.value }) }}>
                <option value="">→ Add to irrigation zone…</option>
                {irrigationZones.map(z => <option key={z.id} value={z.id}>{z.name || z.controller_zone_name || 'Zone'}</option>)}
              </select>

              {/* Bulk set variety (recolours the pins) */}
              <select className="input text-xs" value="" style={{ padding: '6px 8px' }}
                onChange={e => { if (e.target.value) { bulkSetVariety(e.target.value); e.currentTarget.value = '' } }}>
                <option value="">→ Set variety…</option>
                {catalogCategories.filter(c => c !== 'All').map(cat => (
                  <optgroup key={cat} label={cat}>
                    {catalog.filter(p => p.category === cat).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                ))}
              </select>

              {/* Bulk sequential naming */}
              <div style={{ borderTop: '1px solid var(--color-border)', margin: '2px 0 0', paddingTop: 8 }}>
                <div style={{ fontSize: 10, color: 'var(--color-text-faint)', marginBottom: 4 }}>Name them numerically</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input className="input text-xs" style={{ flex: 2, padding: '6px 8px', minWidth: 0 }}
                    value={bulkBaseName} onChange={e => setBulkBaseName(e.target.value)}
                    placeholder={commonSelectedVariety() || 'e.g. Glenn Mango'} />
                  <input className="input text-xs" type="number" min={1} style={{ width: 52, padding: '6px 8px' }}
                    value={bulkStartNum} onChange={e => setBulkStartNum(parseInt(e.target.value || '1', 10))}
                    title="Starting number" />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={bulkPrependBlock} onChange={e => setBulkPrependBlock(e.target.checked)} />
                  Prefix block name
                </label>
                <button onClick={bulkApplyNames}
                  style={{ width: '100%', padding: '6px 0', borderRadius: 7, fontSize: 11, fontWeight: 600,
                    background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)',
                    border: '1px solid var(--color-primary)', color: 'var(--color-primary)', cursor: 'pointer' }}>
                  🔢 Name {selectedIds.size} tree{selectedIds.size === 1 ? '' : 's'}
                </button>
              </div>

              <button onClick={deleteSelected}
                style={{ padding: '6px 0', borderRadius: 7, fontSize: 11, fontWeight: 600,
                  background: 'color-mix(in srgb, var(--color-danger) 16%, transparent)',
                  border: '1px solid var(--color-danger)', color: 'var(--color-danger)', cursor: 'pointer' }}>
                🗑 Delete {selectedIds.size} tree{selectedIds.size === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        )}

        {/* Click-to-edit a placed map element (recolour/rename/retype/delete). Keyed so it re-seeds. */}
        {editInfra && (
          <InfraEditor
            key={`${editInfra.kind}:${(editInfra.data as { id: string }).id}`}
            target={editInfra} qc={qc} zones={irrigationZones} sizes={pipeSizes}
            manifolds={irrigationInfra.manifolds} valves={irrigationInfra.valves}
            shapeEditing={editInfra.kind === 'structure' ? editingStructureShape : editingPipeShape}
            onEditShape={editInfra.kind === 'structure' ? startStructureShapeEdit : startPipeShapeEdit}
            onSaveShape={editInfra.kind === 'structure' ? saveStructureShapeEdit : savePipeShapeEdit}
            onCancelShape={editInfra.kind === 'structure' ? cancelStructureShapeEdit : cancelPipeShapeEdit}
            onClose={() => { if (editingPipeShape) cancelPipeShapeEdit(); if (editingStructureShape) cancelStructureShapeEdit(); setEditInfra(null) }}
          />
        )}

        {/* Selected block / zone panel */}
        {(selectedBlock || selectedZone) && (
          <div className="absolute bottom-6 left-6 z-[1000] rounded-xl p-3" style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', width: 248 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: selectedBlock ? '#F5A623' : '#A855F7' }}>
                {selectedBlock ? '◆ Block' : selectedZone?.block_id ? '◇ Sub-zone' : '◇ Zone'}
              </span>
              <button onClick={() => { if (editingShape) cancelEditShape(); setSelectedBlock(null); setSelectedZone(null) }} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', fontSize: 14 }}>✕</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ color: 'var(--color-text-faint)', flexShrink: 0, fontSize: 11 }} aria-hidden>✎</span>
              <input
                value={renameVal}
                onChange={e => setRenameVal(e.target.value)}
                onBlur={saveName}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setRenameVal(selectedBlock?.name ?? selectedZone?.name ?? ''); (e.target as HTMLInputElement).blur() } }}
                placeholder="Name this block…"
                aria-label="Rename"
                title="Type a name, then Enter"
                style={{ flex: 1, fontSize: 14, fontWeight: 600, color: 'var(--color-text)', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, width: '100%', padding: '4px 7px', outline: 'none' }}
              />
            </div>
            {/* Recolour the block/zone on the map (saved to its backing plant_zone). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', flexShrink: 0 }}>Colour</span>
              <ColorSwatches
                value={plantZones.find(z => z.id === (selectedBlock?.zone_id ?? selectedZone?.id))?.color_hex ?? ''}
                presets={ZONE_COLOR_PRESETS}
                wheelDefault={selectedBlock ? '#F5A623' : selectedZone?.block_id ? '#A855F7' : LAYER_COLORS.plantZones}
                onChange={saveZoneColor}
              />
            </div>
            {selectedBlock && (
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                {selectedBlock.tracking_mode} · {(selectedBlock.effective_plant_count ?? selectedBlock.plant_count ?? 0)} plants
                {selectedBlock.acreage ? ` · ${selectedBlock.acreage} ac` : ''}
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <TagEditor entityType={selectedBlock ? 'block' : 'plant_zone'} entityId={(selectedBlock ?? selectedZone)!.id} />
            </div>
            {/* Trees in this zone — re-find them + jump to any one. */}
            {treesInSelected.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text)' }}>🌳 {treesInSelected.length} tree{treesInSelected.length === 1 ? '' : 's'} in here</span>
                  <button
                    onClick={() => { const map = leafletMap.current; if (map) { try { map.fitBounds(treesInSelected.map(t => [t.lat, t.lng]) as [number, number][], { padding: [50, 50], maxZoom: 20 }) } catch { /* ignore */ } } }}
                    style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Zoom to
                  </button>
                </div>
                <div style={{ maxHeight: 124, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {treesInSelected.map(t => (
                    <button key={t.feature.id}
                      onClick={() => { const map = leafletMap.current; if (map) map.setView([t.lat, t.lng], Math.max(map.getZoom(), 20)); setSelectedFeature(t.feature); setActiveTab('features'); setPanelOpen(true) }}
                      title="Jump to this tree"
                      style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--color-text)', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {selectedBlock && blockStats && (blockStats.harvest_kg_all_time > 0 || blockStats.last_harvest_date != null) && (
              <div style={{ marginTop: 8, padding: '7px 9px', borderRadius: 8, background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)', border: '1px solid var(--color-border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-muted)' }}>
                  <span>Harvest · all-time</span><strong style={{ color: 'var(--color-text)' }}>{formatWeight(blockStats.harvest_kg_all_time, measurementSystem)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                  <span>This year</span><strong style={{ color: 'var(--color-text)' }}>{formatWeight(blockStats.harvest_kg_ytd, measurementSystem)}</strong>
                </div>
                {blockStats.harvest_kg_machine > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-faint)', marginTop: 2 }}>
                    <span>hand / machine</span><span>{formatWeight(blockStats.harvest_kg_hand, measurementSystem)} / {formatWeight(blockStats.harvest_kg_machine, measurementSystem)}</span>
                  </div>
                )}
                {blockStats.last_harvest_date && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-faint)', marginTop: 2 }}>
                    <span>Last harvest</span><span>{new Date(blockStats.last_harvest_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
              </div>
            )}
            {selectedBlock && (
              <p style={{ fontSize: 10, color: 'var(--color-text-faint)', marginTop: 6, lineHeight: 1.4 }}>
                Use <strong>+ Sub-zone</strong> above to group species inside this block, or place trees inside it.
              </p>
            )}
            {selectedBlock && (() => {
              const z = plantZones.find(pz => pz.id === selectedBlock.zone_id)
              const ring = outerRing(z?.geometry)
              return ring && ring.length >= 3 ? (
                <button
                  onClick={() => runDetect(ring)}
                  disabled={detecting}
                  style={{ width: '100%', marginTop: 8, padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: detecting ? 'wait' : 'pointer', background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)', color: 'var(--color-primary)' }}
                  title="Automatically scans only inside this block's boundary"
                >
                  {detecting ? 'Scanning…' : '✨ Detect trees in this block'}
                </button>
              ) : null
            })()}
            {editingShape && (
              <p style={{ fontSize: 10, color: 'var(--color-text-faint)', margin: '8px 0 0', lineHeight: 1.4 }}>
                Drag a corner to reshape; drag a midpoint to add one; tap a corner to delete it; drag the green ✥ to move the whole shape.
              </p>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {editingShape ? (
                <>
                  <button onClick={saveEditShape}
                    style={{ flex: 1, padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: '#2D6A2F', border: 'none', color: '#fff', cursor: 'pointer' }}>
                    Save shape
                  </button>
                  <button onClick={cancelEditShape}
                    style={{ padding: '6px 12px', borderRadius: 7, fontSize: 12, background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {((selectedBlock && selectedBlock.zone_id) || selectedZone) && (
                    <button onClick={startEditShape}
                      title="Drag corners to reshape, midpoints to add, tap a corner to delete, ✥ to move the whole shape — then Save"
                      style={{ flex: 1, padding: '6px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: 'color-mix(in srgb, var(--color-primary) 14%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)', color: 'var(--color-primary)', cursor: 'pointer' }}>
                      ✏️ Edit shape
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      if (selectedBlock) {
                        if (!confirm(`Delete block “${selectedBlock.name}”? Its trees and plants are kept; the outline is removed from the map.`)) return
                        try {
                          await blocksApi.remove(selectedBlock.id)
                          // The visible polygon is the block's backing plant_zone —
                          // remove it too, or the shape lingers as an orphaned zone.
                          if (selectedBlock.zone_id) await plantZonesApi.remove(selectedBlock.zone_id)
                        } catch { alert('Could not delete the block — please try again.'); return }
                        qc.invalidateQueries({ queryKey: ['blocks'] }); qc.invalidateQueries({ queryKey: ['plant-zones'] })
                      } else if (selectedZone) {
                        if (!confirm(`Delete zone “${selectedZone.name}”? Its plants are kept.`)) return
                        try { await plantZonesApi.remove(selectedZone.id) } catch { alert('Could not delete the zone — please try again.'); return }
                        qc.invalidateQueries({ queryKey: ['plant-zones'] })
                      }
                      setSelectedBlock(null); setSelectedZone(null)
                    }}
                    style={{ padding: '6px 12px', borderRadius: 7, fontSize: 12, background: 'transparent', border: '1px solid #F8717160', color: '#F87171', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Placement indicator */}
        {placingSpecies && (
          <div
            role="status"
            aria-live="polite"
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[1000] rounded-xl px-4 py-2.5 flex items-center gap-3 shadow-lg"
            style={{ background: 'var(--color-primary)', color: 'var(--color-bg)' }}
          >
            <span className="text-sm font-medium">
              Click map to place: {placingSpecies.name}
            </span>
            <button
              onClick={() => setPlacingSpecies(null)}
              aria-label="Cancel placement"
              className="text-xs opacity-70 hover:opacity-100"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Dim backdrop behind the panel when it overlays the map on a phone. */}
      {panelOpen && isNarrow && (
        <div onClick={togglePanel} className="absolute inset-0 z-[1490]" style={{ background: 'rgba(0,0,0,0.4)' }} aria-hidden />
      )}

      {/* Right sidebar — hideable (full-map mode / mobile); overlays the map on a phone. */}
      {panelOpen && (
      <aside
        className={`flex flex-col overflow-hidden shrink-0 ${isNarrow ? '' : 'w-80'}`}
        style={{
          borderLeft: '1px solid var(--color-border)', background: 'var(--color-bg-card)',
          ...(isNarrow ? { position: 'absolute', top: 0, right: 0, bottom: 0, zIndex: 1500, width: 'min(360px, 92vw)', boxShadow: '-8px 0 24px rgba(0,0,0,0.45)' } : {}),
        }}
        aria-label="Map sidebar"
      >
        {/* Tabs */}
        <div className="flex shrink-0 items-stretch" style={{ borderBottom: '1px solid var(--color-border)' }}>
          {(
            [
              { id: 'features' as const,   label: 'Features' },
              { id: 'catalog' as const,    label: 'Catalog' },
              { id: 'irrigation' as const, label: 'Irrigation' },
            ]
          ).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex-1 py-2.5 text-xs font-semibold transition-colors"
              aria-selected={activeTab === tab.id}
              style={{
                color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-muted)',
                borderBottom: activeTab === tab.id ? '2px solid var(--color-primary)' : '2px solid transparent',
              }}
            >
              {tab.label}
              {tab.id === 'irrigation' && undesignatedZones.length > 0 && (
                <span
                  className="ml-1.5 inline-block text-xs px-1.5 rounded-full"
                  style={{ background: 'var(--color-primary)', color: 'var(--color-bg)', fontSize: '0.6rem' }}
                >
                  {undesignatedZones.length}
                </span>
              )}
            </button>
          ))}
          {/* Collapse the panel → full-map mode. */}
          <button
            onClick={togglePanel}
            title="Hide panel"
            aria-label="Hide tools panel"
            className="px-3 text-sm font-bold shrink-0"
            style={{ color: 'var(--color-text-muted)', background: 'transparent', border: 'none', borderLeft: '1px solid var(--color-border)', cursor: 'pointer' }}
          >
            ›
          </button>
        </div>

        {/* Tab panels */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Features tab ── */}
          {activeTab === 'features' && !selectedFeature && (
            <div>
              <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--color-text)' }}>
                  {features.length} feature{features.length !== 1 ? 's' : ''} on map
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                  {placingSpecies
                    ? `Click the map to place ${placingSpecies.name}`
                    : 'Click a feature to select it · drag a pin to move it · Catalog tab to add plants'}
                </p>
                {legacyTrees.length > 0 && (
                  <button
                    onClick={backfillTreesToPlants}
                    disabled={backfilling}
                    className="text-xs mt-2 w-full"
                    style={{ padding: '6px 0', borderRadius: 7, fontWeight: 600, cursor: backfilling ? 'wait' : 'pointer', background: 'rgba(45,106,47,0.14)', border: '1px solid rgba(45,106,47,0.45)', color: '#2D6A2F' }}
                    title="These trees were placed before they became Plant records"
                  >
                    {backfilling ? 'Converting…' : `Convert ${legacyTrees.length} map tree${legacyTrees.length > 1 ? 's' : ''} to Plants`}
                  </button>
                )}
              </div>
              <div>
                {features.map(f => {
                  const lk = featureLayerKey(f.feature_type)
                  return (
                    <button
                      key={f.id}
                      onClick={() => setSelectedFeature(f)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                      style={{ borderBottom: '1px solid var(--color-border)' }}
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: LAYER_COLORS[lk] }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--color-text)' }}>
                          {f.name || '(unnamed)'}
                        </p>
                        <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                          {f.feature_type.replace(/_/g, ' ')}
                          {f.properties?.species_name ? ` · ${f.properties.species_name}` : ''}
                        </p>
                      </div>
                    </button>
                  )
                })}
                {features.length === 0 && (
                  <div className="px-4 py-10 text-center empty-state mx-3 my-4">
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      No features yet. Browse the Plant Catalog to add your first tree, or draw a zone using the buttons above the map.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Feature detail panel ── */}
          {activeTab === 'features' && selectedFeature && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span
                  className="badge capitalize"
                  style={{
                    background: LAYER_COLORS[featureLayerKey(selectedFeature.feature_type)] + '28',
                    color: LAYER_COLORS[featureLayerKey(selectedFeature.feature_type)],
                  }}
                >
                  {selectedFeature.feature_type.replace(/_/g, ' ')}
                </span>
                <button
                  onClick={() => setSelectedFeature(null)}
                  aria-label="Back to feature list"
                  className="text-xs"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  Back
                </button>
              </div>

              {selectedFeature.feature_type === 'boundary' && (() => {
                const g = selectedFeature.geometry as any
                const ring: number[][] | undefined = g?.type === 'MultiPolygon' ? g?.coordinates?.[0]?.[0] : g?.coordinates?.[0]
                return ring && ring.length >= 3 ? (
                  <button
                    onClick={() => runDetect(ring)}
                    disabled={detecting}
                    style={{ width: '100%', marginBottom: 12, padding: '8px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: detecting ? 'wait' : 'pointer', background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)', color: 'var(--color-primary)' }}
                    title="Automatically scans only within your property boundary"
                  >
                    {detecting ? 'Scanning…' : '✨ Detect trees in property boundary'}
                  </button>
                ) : null
              })()}

              {selectedFeature.feature_type === 'boundary' && (
                <>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    {editingBoundary ? (
                      <>
                        <button onClick={saveEditBoundary} style={{ flex: 1, padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, background: '#2D6A2F', border: 'none', color: '#fff', cursor: 'pointer' }}>Save shape</button>
                        <button onClick={cancelEditBoundary} style={{ padding: '7px 12px', borderRadius: 7, fontSize: 12, background: 'transparent', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>Cancel</button>
                      </>
                    ) : (
                      <button onClick={startEditBoundary} title="Drag the corners to reshape the property boundary, then Save" style={{ flex: 1, padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, background: 'color-mix(in srgb, var(--color-primary) 14%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)', color: 'var(--color-primary)', cursor: 'pointer' }}>✏️ Edit boundary shape</button>
                    )}
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none" style={{ marginBottom: 12 }}>
                    <input type="checkbox" checked={maskOn} onChange={() => setMaskOn(v => !v)} className="rounded" style={{ accentColor: '#F5A623' }} />
                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Dim everything outside the farm</span>
                  </label>
                </>
              )}

              {(() => {
                const props = selectedFeature.properties as Record<string, string> | undefined
                return props?.species_name ? (
                  <SpeciesCard speciesId={props.species_id} speciesName={props.species_name} catalog={catalog} />
                ) : null
              })()}

              {(() => {
                const pid = (selectedFeature.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined
                return pid ? (
                  <button
                    onClick={() => openPlantEditor(pid)}
                    disabled={loadingPlant}
                    style={{ width: '100%', marginBottom: 10, padding: '8px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: loadingPlant ? 'wait' : 'pointer', background: 'rgba(45,106,47,0.16)', border: '1px solid rgba(45,106,47,0.45)', color: '#2D6A2F' }}
                    title="Open the full plant record — variety, block, status, care, dimensions, photos"
                  >
                    {loadingPlant ? 'Loading…' : '✎ Edit plant details (variety, block, care…)'}
                  </button>
                ) : ['tree', 'palm', 'shrub'].includes(selectedFeature.feature_type) ? (
                  <button
                    onClick={() => convertFeatureToPlant(selectedFeature)}
                    style={{ width: '100%', marginBottom: 10, padding: '8px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(45,106,47,0.16)', border: '1px solid rgba(45,106,47,0.45)', color: '#2D6A2F' }}
                    title="Create a Plant record for this tree (auto-added to the block / zones it sits in) so it shows up in Plants and can be tagged, given care, and harvested"
                  >
                    ➕ Make this a Plant record
                  </button>
                ) : null
              })()}

              {/* Quick spatial assignment — add this pin to the block / irrigation zone
                  it sits inside (one tap, no full editor). Only shown when it's inside
                  one it isn't already part of. */}
              {(() => {
                const pid = (selectedFeature.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined
                const g = selectedFeature.geometry as { type?: string; coordinates?: number[] } | undefined
                if (!pid || g?.type !== 'Point' || !Array.isArray(g.coordinates)) return null
                const [lng, lat] = g.coordinates
                const plant = plants.find(p => p.id === pid)
                const bId = blockIdAt(lat, lng)
                const block = bId ? blocks.find(b => b.id === bId) : undefined
                const iId = irrigationZoneIdAt(lat, lng)
                const iz = iId ? irrigationZones.find(z => z.id === iId) : undefined
                const zId = plantZoneAt(lat, lng)
                const pz = zId ? plantZones.find(z => z.id === zId) : undefined
                const showBlock = block && plant?.block_id !== block.id
                const showIrr = iz && plant?.irrigation_zone_id !== iz.id
                const showZone = pz && plant?.zone_id !== pz.id
                if (!showBlock && !showIrr && !showZone) return null
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                    {showBlock && (
                      <button
                        onClick={async () => { await quickAssign(pid, { block_id: block!.id }); flashAbsorb(`Added to block “${block!.name}”`) }}
                        style={{ width: '100%', padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)', border: '1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)', color: 'var(--color-primary)' }}
                        title="Assign this tree to the block it sits inside"
                      >
                        ➕ Add to block “{block!.name}”
                      </button>
                    )}
                    {showZone && (
                      <button
                        onClick={async () => { await quickAssign(pid, { zone_id: pz!.id }); flashAbsorb(`Added to zone “${pz!.name}”`) }}
                        style={{ width: '100%', padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(168,85,247,0.16)', border: '1px solid rgba(168,85,247,0.45)', color: '#A855F7' }}
                        title="Assign this tree to the plant zone it sits inside"
                      >
                        ➕ Add to zone “{pz!.name}”
                      </button>
                    )}
                    {showIrr && (
                      <button
                        onClick={async () => { await quickAssign(pid, { irrigation_zone_id: iz!.id }); flashAbsorb(`Added to irrigation “${iz!.name || iz!.controller_zone_name || 'zone'}”`) }}
                        style={{ width: '100%', padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(20,184,166,0.16)', border: '1px solid rgba(20,184,166,0.45)', color: '#14b8a6' }}
                        title="Assign this tree to the irrigation zone it sits inside"
                      >
                        ➕ Add to irrigation “{iz!.name || iz!.controller_zone_name || 'zone'}”
                      </button>
                    )}
                  </div>
                )
              })()}

              {/* Tags — on the plant record if this pin is a real Plant, else on the
                  map feature itself. Inherited downward by anything inside (zones/blocks). */}
              {(() => {
                const pid = (selectedFeature.properties as Record<string, unknown> | undefined)?.plant_id as string | undefined
                return (
                  <div style={{ marginBottom: 12 }}>
                    <div className="label" style={{ marginBottom: 4 }}>Tags</div>
                    {pid
                      ? <TagEditor entityType="plant" entityId={pid} />
                      : <span style={{ fontSize: 11, color: 'var(--color-text-faint)' }}>Convert to a Plant to tag it.</span>}
                  </div>
                )
              })()}

              <label className="label" htmlFor="feature-name-input">Name</label>
              <input
                id="feature-name-input"
                defaultValue={selectedFeature.name}
                className="input mb-4 text-sm"
                placeholder="Feature name"
                onBlur={e => updateFeature.mutate({ id: selectedFeature.id, data: { name: e.target.value } })}
              />

              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginBottom: 12 }}>
                <PhotoManager resourceType="feature" resourceId={selectedFeature.id} label="Photos" />
              </div>

              <button
                onClick={() => deleteFeature.mutate(selectedFeature.id)}
                className="btn-danger text-xs w-full"
                aria-label={`Delete ${selectedFeature.name || 'feature'}`}
              >
                Delete feature
              </button>
            </div>
          )}

          {/* ── Catalog tab ── */}
          {activeTab === 'catalog' && (
            <div className="flex flex-col h-full">
              <div className="p-3 space-y-2 shrink-0" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <input
                  className="input text-xs"
                  placeholder="Search species or latin name..."
                  value={catalogSearch}
                  onChange={e => setCatalogSearch(e.target.value)}
                  aria-label="Search plant catalog"
                />
                <div className="flex gap-1 flex-wrap">
                  {catalogCategories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setCatalogCategory(cat)}
                      className="text-xs px-2.5 py-0.5 rounded-full transition-colors"
                      style={{
                        background: catalogCategory === cat ? 'var(--color-primary)' : 'var(--color-bg-card-2)',
                        color: catalogCategory === cat ? 'var(--color-bg)' : 'var(--color-text-muted)',
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Click a plant, then click the map to place it.
                </p>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredCatalog.map(plant => (
                  <button
                    key={plant.id}
                    onClick={() => {
                      setPlacingSpecies(plant)
                      setActiveTab('features')
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      background: placingSpecies?.id === plant.id ? 'var(--color-bg-card-2)' : 'transparent',
                    }}
                  >
                    <span
                      className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center text-xs font-bold"
                      style={{ background: plant.color + '22', color: plant.color }}
                      aria-hidden="true"
                    >
                      {plant.name[0]}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--color-text)' }}>
                        {plant.name}
                      </p>
                      <p className="text-xs italic truncate" style={{ color: 'var(--color-text-muted)' }}>
                        {plant.scientificName}
                      </p>
                    </div>
                    <span
                      className="shrink-0 text-xs px-2 py-0.5 rounded-lg"
                      style={{
                        background: 'var(--color-bg-card-2)',
                        color: 'var(--color-text-muted)',
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      Place
                    </span>
                  </button>
                ))}
                {filteredCatalog.length === 0 && (
                  <div className="px-4 py-8 text-center">
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      No plants match your search.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Irrigation tab ── */}
          {activeTab === 'irrigation' && (
            <div>
              {/* Irrigation drawing tools (relocated off the map). Toggle with the
                  "🚿 Irrigation system" map button; draw acts on the map. */}
              {showInfraTools && (
                <div className="p-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  {/* ── Zones (watering areas — the named "Zone N" hub) ── */}
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: LAYER_COLORS.irrigationZones, marginBottom: 6 }}>Zones</div>
                  <button onClick={() => startDraw('irrigation_zone')} className="text-xs w-full px-3 py-1.5 rounded-lg font-semibold" style={{ marginBottom: 4, background: drawingType === 'irrigation_zone' ? LAYER_COLORS.irrigationZones : 'var(--color-bg)', color: drawingType === 'irrigation_zone' ? '#04222e' : 'var(--color-text)', border: `1px solid ${LAYER_COLORS.irrigationZones}80`, cursor: 'pointer' }}>
                    {drawingType === 'irrigation_zone' ? '✓ Drawing — click corners' : '＋ Draw irrigation zone'}
                  </button>
                  <p style={{ fontSize: 10, color: 'var(--color-text-faint)', margin: '0 0 10px', lineHeight: 1.4 }}>
                    A watering area; name it (e.g. “Zone 1”) and link a valve + controller after drawing.
                  </p>
                  {/* ── Pipes ── */}
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#38BDF8', marginBottom: 6 }}>Pipes</div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <select className="input text-xs" value={pipeRole} onChange={e => { const r = e.target.value as 'mainline' | 'lateral'; setPipeRole(r); if (pipeDrawRef.current) { pipeDrawRef.current.role = r; redrawPipe(); setPipeDraw({ role: r, count: pipeDrawRef.current.points.length }) } }} style={{ flex: 1, padding: '6px 8px' }}>
                      <option value="mainline">Mainline (always on)</option>
                      <option value="lateral">Lateral (after valve)</option>
                    </select>
                    <select className="input text-xs" value={pipeSize} onChange={e => { pipeSizeTouchedRef.current = true; setPipeSize(e.target.value); if (pipeDrawRef.current) pipeDrawRef.current.sizeLabel = e.target.value }} style={{ flex: 1, padding: '6px 8px' }} title="Nominal pipe size (defaults to your measurement system)">
                      {sizesForSystem.length === 0 && <option value="">size…</option>}
                      {sizesForSystem.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                    <select className="input text-xs" value={pipeMaterial} onChange={e => { setPipeMaterial(e.target.value); if (pipeDrawRef.current) pipeDrawRef.current.material = e.target.value }} style={{ flex: 1, padding: '6px 8px' }} title="Pipe material">
                      <option value="pvc">PVC</option>
                      <option value="poly">Poly</option>
                      <option value="drip">Drip tubing</option>
                      <option value="other">Other</option>
                    </select>
                    <div title="Pipe colour — white PVC, purple = reclaimed/non-potable, or pick any">
                      <ColorSwatches value={pipeColor} presets={PIPE_COLOR_PRESETS} wheelDefault="#A06CD5"
                        onChange={c => { setPipeColor(c); if (pipeDrawRef.current) { pipeDrawRef.current.color = c; redrawPipe() } }} />
                    </div>
                  </div>
                  {!pipeDraw ? (
                    <button onClick={startPipeDraw} className="text-xs w-full px-3 py-1.5 rounded-lg font-semibold" style={{ background: '#38BDF8', color: '#04222e', border: 'none', cursor: 'pointer' }}>
                      ✏️ Draw {pipeRole === 'lateral' ? 'lateral' : 'mainline'} pipe
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={finishPipe} disabled={pipeDraw.count < 2} className="text-xs flex-1 px-2 py-1.5 rounded-lg font-semibold" style={{ background: pipeDraw.count < 2 ? 'var(--color-bg)' : '#2D6A2F', color: '#fff', border: '1px solid #2D6A2F', opacity: pipeDraw.count < 2 ? 0.5 : 1, cursor: pipeDraw.count < 2 ? 'not-allowed' : 'pointer' }}>
                        ✓ Finish ({pipeDraw.count})
                      </button>
                      <button onClick={undoPipeVertex} className="text-xs px-2 py-1.5 rounded-lg" style={{ background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', cursor: 'pointer' }}>↶</button>
                      <button onClick={cancelPipeDraw} className="text-xs px-2 py-1.5 rounded-lg" style={{ background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', cursor: 'pointer' }}>✕</button>
                    </div>
                  )}
                  <p style={{ fontSize: 10, color: 'var(--color-text-faint)', margin: '6px 0 10px', lineHeight: 1.4 }}>
                    Click on the map to drop bends — they snap to valves, sources & pipe ends (start ON a valve to connect). Finish: <b>Enter</b>, right-click, or double-click · <b>Esc</b> cancel · <b>⌫</b> undo.
                  </p>
                  {/* ── Valves / manifolds / sources ── */}
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#22D3EE', marginBottom: 6 }}>Valves & water sources</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {([['valve', 'Valve'], ['manifold', 'Manifold'], ['well_pump', 'Well pump'], ['city_water', 'City water']] as [Exclude<PlaceMode, null>, string][]).map(([mode, label]) => (
                      <button key={mode} onClick={() => startPlace(mode)} className="text-xs px-2 py-1.5 rounded-lg font-medium" style={{ background: placeMode === mode ? '#0EA5E9' : 'var(--color-bg)', color: placeMode === mode ? '#fff' : 'var(--color-text)', border: '1px solid rgba(14,165,233,0.4)', cursor: 'pointer' }}>
                        {placeMode === mode ? '✓ ' : '+ '}{label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Structure palette (relocated off the map). Toggle with the map's "+ Structure" button. */}
              {showStructurePalette && (
                <div className="p-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#B45309', marginBottom: 8 }}>Add a structure</div>
                  <p style={{ fontSize: 10, color: 'var(--color-text-faint)', margin: '0 0 10px', lineHeight: 1.4 }}>
                    Pick one — a sized box drops at the map centre. Drag its corners to resize, ✥ to move.
                  </p>
                  {STRUCTURE_CATEGORIES.map(cat => (
                    <div key={cat} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-muted)', marginBottom: 4 }}>{cat}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {STRUCTURE_PRESETS.filter(p => p.category === cat).map(p => (
                          <button key={p.key} onClick={() => dropStructurePreset(p)} title={`${p.label} · ${p.w}×${p.h} m`} className="text-xs px-2 py-1 rounded-lg" style={{ background: 'var(--color-bg)', color: 'var(--color-text)', border: `1px solid ${p.color}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 9, height: 9, borderRadius: 2, background: p.color, display: 'inline-block', flexShrink: 0 }} />
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button onClick={() => { setShowStructurePalette(false); startDraw('structure') }} className="text-xs w-full px-3 py-1.5 rounded-lg font-medium" style={{ marginTop: 2, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', cursor: 'pointer' }}>
                    ✏️ Custom shape (draw it yourself)
                  </button>
                </div>
              )}

              <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--color-text)' }}>
                  Irrigation Zones
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                  {undesignatedZones.length > 0
                    ? `${undesignatedZones.length} zone${undesignatedZones.length !== 1 ? 's' : ''} need map placement`
                    : 'All zones designated on map'}
                </p>
              </div>

              {irrigationZones.map(zone => (
                <div
                  key={zone.id}
                  className="flex items-center gap-3 px-4 py-3"
                  style={{ borderBottom: '1px solid var(--color-border)' }}
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{
                      background: zone.designation_status === 'designated'
                        ? LAYER_COLORS.irrigationZones
                        : 'var(--color-text-faint)',
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: 'var(--color-text)' }}>
                      {zone.name || zone.controller_zone_name || `Zone ${zone.id.slice(0, 6)}`}
                    </p>
                    <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                      {zone.bound
                        ? <span style={{ color: LAYER_COLORS.irrigationZones }}>Linked to controller{zone.controller_zone_name ? ` · ${zone.controller_zone_name}` : ''}</span>
                        : zone.station_number != null
                          ? `Zone ${zone.station_number} · not yet linked`
                          : <span style={{ color: 'var(--color-text-faint)' }}>Not linked to a controller</span>}
                    </p>
                  </div>
                  {zone.designation_status === 'undesignated' && (
                    <button
                      onClick={() => setDrawingType('irrigation_zone')}
                      className="text-xs px-2 py-1 rounded-lg shrink-0"
                      style={{
                        background: 'var(--color-bg-card-2)',
                        color: LAYER_COLORS.irrigationZones,
                        border: `1px solid ${LAYER_COLORS.irrigationZones}40`,
                      }}
                    >
                      Draw
                    </button>
                  )}
                </div>
              ))}

              {irrigationZones.length === 0 && (
                <div className="px-4 py-10 text-center empty-state mx-3 my-4">
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    No irrigation zones yet. Draw one with “🚿 Irrigation system → Draw irrigation zone”, name it (e.g. “Zone 1”), then link a valve + controller. Or import a controller in Settings → Integrations.
                  </p>
                </div>
              )}
            </div>
          )}

        </div>
      </aside>
      )}

      {/* Full plant editor opened from a map pin (shared with the Plants page). */}
      {editingPlant && (
        <PlantModal
          plant={editingPlant}
          onClose={() => setEditingPlant(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['map-features'] }); qc.invalidateQueries({ queryKey: ['plants'] }) }}
        />
      )}
    </main>
  )
}
